// src/App.jsx
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from "framer-motion";
import { pageTransition, staggerContainer, fadeUpItem } from "./motion/variants";
import IgnitionCore from "./three/IgnitionCore";
import { AuthProvider, useAuth } from "./context/AuthContext";   // ✅ NEW
import AuthPage from "./pages/AuthPage";                          // ✅ NEW
import ResetPasswordPage from "./pages/ResetPasswordPage";         // ✅ NEW
import OnboardingPage from "./pages/OnboardingPage";             // ✅ NEW
import AdminDashboard from "./pages/AdminDashboard";             // ✅ Super-admin
import TermsPage from "./pages/TermsPage";                        // ✅ Legal
import PrivacyPage from "./pages/PrivacyPage";                    // ✅ Legal
import RefundPage from "./pages/RefundPage";                      // ✅ Legal
import { useTasks } from "./hooks/useTasks";
import KanbanColumn from "./components/KanbanColumn";
import TiltPCard from "./components/TiltPCard";
import AddTaskModal from "./components/AddTaskModal";
import AddToSlackButton from "./components/AddToSlackButton";
import ConnectGmailButton from "./components/ConnectGmailButton";
import ConnectWhatsAppButton from "./components/ConnectWhatsAppButton";
import SourceBadge from "./components/SourceBadge";
import EmailTaskInbox from "./components/EmailTaskInbox";

// -- API base URL — env var with hardcoded fallback ---------------------------
const BASE_URL = process.env.REACT_APP_API_URL || "";

// -- Responsive window width hook ---------------------------------------------
function useWindowWidth() {
  const [w, setW] = React.useState(window.innerWidth);
  React.useEffect(() => {
    const handler = () => setW(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return w;
}



// -- Date formatting utility ---------------------------------------------------
// Normalises "2026-04-30" and "2026-04-30T00:00:00[Z]" → local Date object.
// When a timezone string (IANA, e.g. "Asia/Karachi") is supplied the date is
// displayed in that zone; otherwise it falls back to the browser's local time.
function formatDeadline(raw, timezone, opts = {}) {
  if (!raw) return null;
  // Force local-time parsing for bare date strings (avoids UTC-midnight shift)
  const normalised = /T/.test(raw) ? raw : raw + "T00:00:00";
  const date = new Date(normalised);
  if (isNaN(date.getTime())) return raw; // unparseable — return raw as fallback
  const baseOpts = { day: "numeric", month: "short", year: "numeric", ...opts };
  try {
    return date.toLocaleDateString(undefined, timezone ? { ...baseOpts, timeZone: timezone } : baseOpts);
  } catch {
    return date.toLocaleDateString(undefined, baseOpts);
  }
}

// Same normalisation used for overdue comparisons (returns a Date)
function parseDeadline(raw) {
  if (!raw) return null;
  const normalised = /T/.test(raw) ? raw : raw + "T00:00:00";
  const d = new Date(normalised);
  return isNaN(d.getTime()) ? null : d;
}

// -- CSS variables + animations ------------------------------------------------
const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    /* Core palette -- "Ignition": obsidian black igniting into lacquer red */
    --bg-base:            #060504;
    --bg-page:            #0a0706;
    --bg-sidebar:         #0c0908;
    --bg-card:            rgba(255,255,255,0.032);
    --bg-card-hover:      rgba(255,255,255,0.058);
    --bg-elevated:        rgba(255,255,255,0.06);

    /* Borders */
    --border-glass:       rgba(255,255,255,0.072);
    --border-strong:      rgba(255,255,255,0.14);

    /* Accent colors */
    --accent-ember:       #ff6a52;
    --accent-crimson:     #c81f30;
    --accent-blood:       #4a0e13;
    --accent-amber:       #d99a3f;
    --accent-emerald:     #3fae7d;
    --accent-rose:        #ff6a52;
    --accent-1:           #ff6a52;
    /* Back-compat aliases -- old var names still referenced in a few components;
       these get retired once every component is repainted in step 2 */
    --accent-blue:        #ff6a52;
    --accent-violet:      #c81f30;
    --accent-cyan:        #3fae7d;

    /* Gradients */
    --grad-primary:       linear-gradient(135deg, #ff6a52 0%, #c81f30 55%, #4a0e13 100%);
    --grad-success:       linear-gradient(135deg, #3fae7d 0%, #1d9e75 100%);
    --grad-danger:        linear-gradient(135deg, #ff6a52 0%, #c81f30 100%);
    --grad-amber:         linear-gradient(135deg, #d99a3f 0%, #b3711f 100%);
    --grad-sidebar:       linear-gradient(180deg, #0c0908 0%, #060403 100%);

    /* Text */
    --color-text-primary:   #f5f0eb;
    --color-text-secondary: #9a908a;
    --color-text-tertiary:  #847a73;
    --color-text-muted:     #655c56;

    /* Typography */
    --font-display: 'Space Grotesk', sans-serif;
    --font-sans:    'Inter', sans-serif;
    --font-mono:    'JetBrains Mono', monospace;

    /* Shadows */
    --shadow-sm:   0 1px 3px rgba(0,0,0,0.5);
    --shadow-md:   0 4px 16px rgba(0,0,0,0.55);
    --shadow-lg:   0 12px 40px rgba(0,0,0,0.65);
    --shadow-glow: 0 0 24px rgba(255,90,54,0.32);

    /* Radius */
    --radius-sm:  6px;
    --radius-md:  10px;
    --radius-lg:  16px;
    --radius-xl:  22px;
  }

  html { scroll-behavior: smooth; }

  body {
    background: var(--bg-page);
    color: var(--color-text-primary);
    font-family: var(--font-sans);
    font-size: 14px;
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overflow-x: auto;
  }

  /* Scrollbar */
  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 999px; }
  ::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.2); }

  /* Animations */
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes pulse   { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
  @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
  @keyframes fadeUp  { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
  @keyframes slideIn { from { opacity:0; transform:translateX(-8px); } to { opacity:1; transform:translateX(0); } }
  @keyframes scaleIn { from { opacity:0; transform:scale(0.96); } to { opacity:1; transform:scale(1); } }
  @keyframes glow-pulse { 0%,100% { box-shadow: 0 0 20px rgba(255,106,82,0.3); } 50% { box-shadow: 0 0 40px rgba(255,106,82,0.6); } }

  .fade-up         { animation: fadeUp 0.45s cubic-bezier(0.16,1,0.3,1) both; }
  .fade-up.delay-1 { animation-delay: 0.08s; }
  .fade-up.delay-2 { animation-delay: 0.16s; }
  .fade-up.delay-3 { animation-delay: 0.24s; }
  .scale-in        { animation: scaleIn 0.3s cubic-bezier(0.16,1,0.3,1) both; }
  .slide-in        { animation: slideIn 0.35s cubic-bezier(0.16,1,0.3,1) both; }

  /* Noise texture overlay on sidebar */
  .noise-bg::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E");
    pointer-events: none;
    border-radius: inherit;
    z-index: 0;
  }

  /* Focus ring */
  :focus-visible { outline: 2px solid rgba(255,90,54,0.6); outline-offset: 2px; border-radius: 4px; }

  /* Selection */
  ::selection { background: rgba(255,90,54,0.25); color: #f5f0eb; }

  /* -- Premium card hover --------------- */
  .pcard {
    background: rgba(255,255,255,0.032);
    border: 1px solid var(--border-glass);
    border-radius: var(--radius-lg);
    transition: transform 0.2s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.2s, border-color 0.2s;
  }
  .pcard:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.07);
    border-color: rgba(255,255,255,0.1);
  }

  /* -- Table rows ----------------------- */
  .trow {
    transition: background 0.12s;
    border-radius: 8px;
  }
  .trow:hover { background: rgba(255,255,255,0.04) !important; }

  /* -- Btn primary ---------------------- */
  .btn-primary {
    height: 38px; padding: 0 20px; border-radius: 10px; border: none;
    background: var(--grad-primary); background-size: 220% 220%; color: #f5f0eb;
    text-shadow: 0 1px 2px rgba(0,0,0,0.45);
    font-family: var(--font-sans); font-size: 13px; font-weight: 600;
    cursor: pointer; display: inline-flex; align-items: center; gap: 7px;
    box-shadow: 0 0 24px rgba(255,90,54,0.35), inset 0 1px 0 rgba(255,255,255,0.18);
    transition: transform 0.12s, box-shadow 0.12s, opacity 0.12s, background-position 0.6s;
    white-space: nowrap;
  }
  .btn-primary:hover { transform: translateY(-1px); background-position: 100% 100%; box-shadow: 0 0 36px rgba(255,90,54,0.5), inset 0 1px 0 rgba(255,255,255,0.22); }
  .btn-primary:active { transform: translateY(0); }
  .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

  /* -- Btn ghost ------------------------ */
  .btn-ghost {
    height: 38px; padding: 0 16px; border-radius: 10px;
    border: 1px solid var(--border-glass);
    background: rgba(255,255,255,0.03); color: var(--color-text-secondary);
    font-family: var(--font-sans); font-size: 13px; font-weight: 500;
    cursor: pointer; display: inline-flex; align-items: center; gap: 7px;
    transition: all 0.12s;
  }
  .btn-ghost:hover { background: rgba(255,255,255,0.07); color: var(--color-text-primary); border-color: rgba(255,255,255,0.12); }

  /* -- Input field ---------------------- */
  .field-input {
    width: 100%; height: 40px; padding: 0 13px;
    background: rgba(255,255,255,0.04);
    border: 1px solid var(--border-glass);
    border-radius: 9px; font-family: var(--font-sans); font-size: 13px;
    color: var(--color-text-primary); outline: none;
    transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
    box-sizing: border-box;
  }
  .field-input:focus {
    border-color: rgba(255,90,54,0.55);
    background: rgba(255,90,54,0.06);
    box-shadow: 0 0 0 3px rgba(255,90,54,0.14);
  }
  .field-input::placeholder { color: var(--color-text-muted); }

  /* -- Page header ---------------------- */
  .page-header {
    position: sticky; top: 0; z-index: 40; min-height: 56px;
    background: rgba(10,7,6,0.88);
    backdrop-filter: blur(28px); -webkit-backdrop-filter: blur(28px);
    border-bottom: 1px solid rgba(255,255,255,0.055);
    box-shadow: 0 1px 0 rgba(255,90,54,0.07), 0 4px 20px rgba(0,0,0,0.25);
    padding: 10px clamp(12px, 2.5vw, 28px); display: flex; align-items: center; gap: 10; flex-wrap: wrap;
  }
  /* Reserve room for the fixed hamburger button on phone widths, so page
     titles/content never sit underneath it. */
  @media (max-width: 640px) {
    .page-header { padding-left: 56px; }
    .mobile-header-pad { padding-left: 56px !important; }
  }

  /* -- Ownership page — detail panel drops below the list instead of
     squeezing into a fixed 360px column on narrow screens ---- */
  @media (max-width: 860px) {
    .ownership-split-grid { grid-template-columns: 1fr !important; }
  }

  /* -- Dashboard Kanban board — responsive ---- */
  /* Desktop/tablet: horizontal scroll if columns don't fit (unchanged).
     Phones: stack columns vertically instead, so nothing needs a
     sideways scroll + pinch-zoom just to read a card. */
  @media (max-width: 700px) {
    .dash-kanban-scroll { overflow-x: visible !important; }
    .dash-kanban-grid {
      grid-template-columns: 1fr !important;
      min-width: 0 !important;
    }
  }

  /* -- Section card --------------------- */
  .section-card {
    background: rgba(255,255,255,0.03);
    border: 1px solid var(--border-glass);
    border-radius: var(--radius-lg);
    padding: 22px 24px;
  }

  /* -- Badge ---------------------------- */
  .badge {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 3px 9px; border-radius: 999px;
    font-size: 11px; font-weight: 600; letter-spacing: 0.01em;
    border: 1px solid transparent;
  }

  /* -- Animated counter ----------------- */
  @keyframes countUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
  .count-up { animation: countUp 0.4s cubic-bezier(0.16,1,0.3,1) both; }

  /* -- Skeleton shimmer ----------------- */
  .skeleton {
    background: linear-gradient(90deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.09) 50%, rgba(255,255,255,0.04) 100%);
    background-size: 200% 100%;
    animation: shimmer 1.4s ease-in-out infinite;
    border-radius: 6px;
  }

  /* -- Modal backdrop ------------------- */
  .modal-backdrop {
    position: fixed; inset: 0; z-index: 200;
    background: rgba(0,0,0,0.65);
    backdrop-filter: blur(8px);
    display: flex; align-items: center; justify-content: center;
    animation: scaleIn 0.18s ease both;
  }
  .modal-box {
    background: linear-gradient(160deg, #17110f 0%, #0d0908 100%);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: var(--radius-xl);
    box-shadow: 0 32px 100px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,90,54,0.1);
    animation: scaleIn 0.22s cubic-bezier(0.34,1.56,0.64,1) both;
    max-width: calc(100vw - 32px);
    width: 100%;
  }

  /* -- Status pill ---------------------- */
  .status-pill {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 3px 10px; border-radius: 999px; font-size: 11px; font-weight: 600;
    border: 1px solid transparent;
  }
  .status-pill::before {
    content: ''; width: 5px; height: 5px; border-radius: 50%;
    background: currentColor; opacity: 0.8; flex-shrink: 0;
  }

  /* -- Progress bar --------------------- */
  .progress-track {
    height: 5px; border-radius: 999px;
    background: rgba(255,255,255,0.07); overflow: hidden;
  }
  .progress-fill {
    height: 100%; border-radius: 999px;
    transition: width 0.7s cubic-bezier(0.16,1,0.3,1);
  }

  /* -- Glow orb ------------------------- */
  .glow-orb {
    position: absolute; border-radius: 50%;
    pointer-events: none; filter: blur(32px);
  }

  /* -- Icon bubble ---------------------- */
  .icon-bubble {
    display: flex; align-items: center; justify-content: center;
    border-radius: var(--radius-md); flex-shrink: 0;
  }

  /* -- Page transition ------------------ */
  @keyframes pageIn {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .page-enter { animation: pageIn 0.32s cubic-bezier(0.16,1,0.3,1) both; }

  /* -- Ripple on click ------------------ */
  @keyframes ripple {
    from { transform: scale(0); opacity: 0.4; }
    to   { transform: scale(2.5); opacity: 0; }
  }

  /* -- Task row ------------------------- */
  .task-row {
    display: grid; align-items: center;
    border-radius: 10px; padding: 10px 14px;
    transition: background 0.12s, box-shadow 0.12s;
    position: relative; overflow: hidden;
  }
  .task-row:hover {
    background: rgba(255,255,255,0.045) !important;
    box-shadow: inset 0 0 0 1px rgba(255,255,255,0.06);
  }

  /* -- Ownership node card -------------- */
  .owner-card {
    background: rgba(255,255,255,0.04);
    border: 1px solid var(--border-glass);
    border-radius: var(--radius-lg);
    padding: 18px 20px; cursor: pointer;
    transition: transform 0.2s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.2s, border-color 0.15s;
    position: relative; overflow: hidden;
  }
  .owner-card:hover {
    transform: translateY(-3px) scale(1.01);
    box-shadow: 0 12px 36px rgba(0,0,0,0.45);
    border-color: rgba(255,255,255,0.12);
  }
  .owner-card.selected {
    background: linear-gradient(135deg, rgba(255,90,54,0.14) 0%, rgba(200,31,48,0.14) 100%);
    border-color: rgba(255,90,54,0.45);
  }

  /* -- Integration card ----------------- */
  .integ-card {
    background: rgba(255,255,255,0.035);
    border: 1px solid var(--border-glass);
    border-radius: var(--radius-lg);
    padding: 24px 26px; overflow: hidden; position: relative;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .integ-card:hover {
    border-color: rgba(255,255,255,0.1);
    box-shadow: 0 8px 32px rgba(0,0,0,0.35);
  }

  /* -- Pulse dot for live status -------- */
  @keyframes pulseDot {
    0%, 100% { box-shadow: 0 0 0 0 rgba(63,174,125,0.4); }
    50%       { box-shadow: 0 0 0 5px rgba(63,174,125,0); }
  }
  .pulse-dot {
    width: 7px; height: 7px; border-radius: 50%;
    background: #3fae7d; animation: pulseDot 2s ease-in-out infinite;
  }

  /* -- Tooltip -------------------------- */
  .has-tooltip { position: relative; }
  .has-tooltip:hover::after {
    content: attr(data-tip);
    position: absolute; bottom: 110%; left: 50%; transform: translateX(-50%);
    background: #1c1210; border: 1px solid var(--border-glass);
    color: var(--color-text-secondary); font-size: 11px; font-weight: 500;
    padding: 4px 9px; border-radius: 6px; white-space: nowrap;
    pointer-events: none; animation: fadeUp 0.15s ease both;
    font-family: var(--font-sans); z-index: 100;
  }

  /* -- Stagger fade for list items ------ */
  .stagger > * { animation: fadeUp 0.4s cubic-bezier(0.16,1,0.3,1) both; }
  .stagger > *:nth-child(1) { animation-delay: 0.04s; }
  .stagger > *:nth-child(2) { animation-delay: 0.08s; }
  .stagger > *:nth-child(3) { animation-delay: 0.12s; }
  .stagger > *:nth-child(4) { animation-delay: 0.16s; }
  .stagger > *:nth-child(5) { animation-delay: 0.20s; }
  .stagger > *:nth-child(6) { animation-delay: 0.24s; }

  /* -- Tour overlay --------------------- */
  @keyframes tourIn {
    from { opacity: 0; transform: scale(0.92) translateY(16px); }
    to   { opacity: 1; transform: scale(1) translateY(0); }
  }
  @keyframes tourSlide {
    from { opacity: 0; transform: translateX(24px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes spotlightPulse {
    0%,100% { box-shadow: 0 0 0 4px rgba(255,90,54,0.4), 0 0 0 8px rgba(255,90,54,0.15); }
    50%      { box-shadow: 0 0 0 6px rgba(255,90,54,0.55), 0 0 0 14px rgba(255,90,54,0.08); }
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }

  @keyframes slideUpIn {
    from { opacity: 0; transform: translateY(24px) scale(0.96); }
    to   { opacity: 1; transform: translateY(0)    scale(1); }
  }

  @keyframes confettiFall {
    0%   { opacity: 1; transform: translateY(0) rotate(0deg) scale(1); }
    100% { opacity: 0; transform: translateY(320px) rotate(var(--rot,360deg)) scale(0.4); }
  }

  @keyframes particleFloat {
    0%   { transform: translateY(0px) rotate(0deg); opacity: 0.8; }
    50%  { transform: translateY(-18px) rotate(180deg); opacity: 0.4; }
    100% { transform: translateY(-36px) rotate(360deg); opacity: 0; }
  }
  .tour-card { animation: tourIn 0.38s cubic-bezier(0.34,1.56,0.64,1) both; }
  .tour-step-slide { animation: tourSlide 0.3s cubic-bezier(0.16,1,0.3,1) both; }

`;

// -- Tour step definitions -----------------------------------------------------
const TOUR_STEPS = [
  {
    id: "welcome", emoji: "👋", title: "Welcome to AI Workflow Coordinator",
    subtitle: "Your intelligent command center",
    description: "You're about to master the most powerful workflow tool your team has ever used. This quick tour will show you everything — it only takes 2 minutes.",
    color: "#ff6a52", gradient: "linear-gradient(135deg,#ff6a52 0%,#c81f30 100%)", tip: null, xp: 0,
  },
  {
    id: "dashboard", emoji: "⬡", title: "Dashboard — Your Mission Control",
    subtitle: "Everything at a glance",
    description: "The Dashboard shows your live task counts, Kanban board, team status, and a real-time system health indicator. It's the first page you'll see every morning.",
    color: "#ff6a52", gradient: "linear-gradient(135deg,#ff6a52 0%,#d99a3f 100%)",
    tip: "💡 Pro tip: The Kanban board lets you drag tasks between columns — or click a card to see full details.", xp: 10,
  },
  {
    id: "nav_badges", emoji: "🔴", title: "Live Sidebar Badges — Always in the Know",
    subtitle: "Real-time counts on every nav item",
    description: "Every section of the sidebar shows a live number badge that updates automatically as your data changes. No need to click into a page to know what's waiting for you.",
    color: "#d99a3f", gradient: "linear-gradient(135deg,#d99a3f 0%,#c9702e 100%)",
    tip: "💡 What each badge means:\n🟠 Dashboard = active (in-progress) tasks\n🔵 Tasks = total task count\n🔴 Compliance = issues needing attention\n🟢 Reports = completed tasks\n🟣 Ownership = number of owners",
    xp: 18,
    isBadgeStep: true,
  },
  {
    id: "tasks", emoji: "✦", title: "Tasks — Full Task Management",
    subtitle: "Create, assign, track, complete",
    description: "The Tasks page gives you a filterable table of every task. Create new tasks, assign them to teammates, set priorities (Critical → Low), and add deadlines.",
    color: "#c81f30", gradient: "linear-gradient(135deg,#c81f30 0%,#ff4d6a 100%)",
    tip: "💡 Pro tip: Use the search bar + status/priority filters to instantly zero in on what matters.", xp: 27,
  },
  {
    id: "compliance", emoji: "◈", title: "Compliance — Task Health Monitor",
    subtitle: "Catch problems before they explode",
    description: "Compliance automatically flags overdue tasks, unassigned work, stale tasks (7+ days inactive), and high-priority items that haven't started yet. Your score shows overall team health.",
    color: "#ff4d5e", gradient: "linear-gradient(135deg,#ff4d5e 0%,#c9702e 100%)",
    tip: "💡 Pro tip: Keep your Compliance Score above 80% — that means your team is running clean.", xp: 36,
  },
  {
    id: "knowledge", emoji: "◉", title: "Knowledge — Team Brain",
    subtitle: "Document everything, forget nothing",
    description: "Store SOPs, runbooks, decision logs, and team notes in the Knowledge base. Pin critical docs so they're always visible, and search across everything instantly.",
    color: "#3fae7d", gradient: "linear-gradient(135deg,#3fae7d 0%,#d99a3f 100%)",
    tip: "💡 Pro tip: Tag your notes with categories to keep things organised as your library grows.", xp: 45,
  },
  {
    id: "ownership", emoji: "⊕", title: "Ownership — Who Owns What",
    subtitle: "Visualise workload distribution",
    description: "The Ownership Graph shows every team member's task load — how many tasks they own, their completion rate, and priority breakdown. Spot overloaded teammates instantly.",
    color: "#d99a3f", gradient: "linear-gradient(135deg,#d99a3f 0%,#c9702e 100%)",
    tip: "💡 Pro tip: Click any person card to expand their full task list with status and deadlines.", xp: 54,
  },
  {
    id: "reports", emoji: "▲", title: "Reports — Performance Analytics",
    subtitle: "Data-driven decisions",
    description: "Reports gives you completion rates, velocity trends, priority distribution, and KPIs over time. Share these with stakeholders to show team progress.",
    color: "#d99a3f", gradient: "linear-gradient(135deg,#d99a3f 0%,#ff6a52 100%)",
    tip: "💡 Pro tip: Check Reports weekly to spot trends before they become problems.", xp: 63,
  },
  {
    id: "integrations", emoji: "⛓", title: "Integrations — Connect Your Stack",
    subtitle: "Slack, GitHub, Zapier and more",
    description: "Connect AI Workflow to your existing tools. Get Slack notifications when tasks are assigned or overdue, trigger automations via Zapier, or post updates to GitHub.",
    color: "#c81f30", gradient: "linear-gradient(135deg,#c81f30 0%,#ff6a52 100%)",
    tip: "💡 Pro tip: Enable the Slack integration first — your team will get nudged about deadlines automatically.", xp: 72,
  },
  {
    id: "api", emoji: "🔑", title: "API — Build On Top",
    subtitle: "Full REST API access",
    description: "Every feature in this app is available via API. Generate API keys, browse the endpoint reference, and build your own automations or integrations on top of your workflow data.",
    color: "#3fae7d", gradient: "linear-gradient(135deg,#3fae7d 0%,#c81f30 100%)",
    tip: "💡 Pro tip: The API docs are available at /docs — fully interactive with Swagger UI.", xp: 81,
  },
  {
    id: "finish", emoji: "🏆", title: "You're Ready to Command!",
    subtitle: "Tour complete — 100 XP earned",
    description: "You now know everything about AI Workflow Coordinator. Your dashboard is live, your team is waiting, and your first task is to create something worth doing.",
    color: "#d99a3f", gradient: "linear-gradient(135deg,#d99a3f 0%,#ff4d5e 100%)",
    tip: null, xp: 100,
  },
];

// -- Confetti burst on tour finish ---------------------------------------------
function ConfettiBurst() {
  const pieces = [...Array(60)].map((_, i) => ({
    id: i,
    x: 40 + Math.random() * 20,
    color: ["#ff6a52","#c81f30","#d99a3f","#3fae7d","#ff4d5e","#d99a3f","#ff4d6a"][i % 7],
    size: 6 + Math.random() * 8,
    delay: Math.random() * 0.6,
    dur: 1.2 + Math.random() * 1,
    rot: Math.random() * 720 - 360,
    shape: i % 3,
  }));
  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 10 }}>
      {pieces.map(p => (
        <div key={p.id} style={{
          position: "absolute",
          left: `${p.x}%`, top: "30%",
          width: p.size, height: p.shape === 2 ? p.size * 0.4 : p.size,
          background: p.color,
          borderRadius: p.shape === 0 ? "50%" : p.shape === 1 ? "2px" : "1px",
          opacity: 0,
          animation: `confettiFall ${p.dur}s ${p.delay}s ease-out forwards`,
          transform: `rotate(${p.rot}deg)`,
        }} />
      ))}
    </div>
  );
}

// -- Floating particles --------------------------------------------------------
function TourParticles({ color }) {
  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", borderRadius: "inherit" }}>
      {[...Array(10)].map((_, i) => (
        <div key={i} style={{
          position: "absolute", borderRadius: i % 2 === 0 ? "50%" : "2px",
          background: color, opacity: 0.5,
          width: 3 + (i % 4) * 2, height: 3 + (i % 4) * 2,
          left: `${5 + i * 10}%`, bottom: "8%",
          animation: `particleFloat ${1.6 + i * 0.25}s ease-in-out ${i * 0.15}s infinite`,
        }} />
      ))}
    </div>
  );
}

// -- Feature preview illustrations per step ---------------------------------
function StepVisual({ stepId, color }) {
  const s = { borderRadius: 8, overflow: "hidden", position: "relative" };
  if (stepId === "welcome") return (
    <div style={{ ...s, background: "rgba(255,106,82,0.07)", border: "1px solid rgba(255,106,82,0.18)", padding: "18px 20px", display: "flex", gap: 14, alignItems: "center" }}>
      {[["⬡","Dashboard","#ff6a52"],["✦","Tasks","#c81f30"],["◈","Compliance","#ff4d5e"],["⊕","Ownership","#d99a3f"],["▲","Reports","#d99a3f"],["🔑","API","#3fae7d"]].map(([icon, label, c]) => (
        <div key={label} style={{ display:"flex",flexDirection:"column",alignItems:"center",gap:5,flex:1 }}>
          <div style={{ width:36,height:36,borderRadius:10,background:`${c}20`,border:`1px solid ${c}35`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16 }}>{icon}</div>
          <div style={{ fontSize:9,color:"var(--color-text-tertiary)",fontWeight:600,letterSpacing:"0.04em",textTransform:"uppercase" }}>{label}</div>
        </div>
      ))}
    </div>
  );
  if (stepId === "dashboard") return (
    <div style={{ ...s, background:"rgba(255,255,255,0.03)",border:"1px solid var(--border-glass)",padding:"14px 16px" }}>
      <div style={{ display:"flex",gap:8,marginBottom:10 }}>
        {[["24","Tasks","#ff6a52"],["8","In Progress","#d99a3f"],["3","Overdue","#ff4d5e"]].map(([n,l,c])=>(
          <div key={l} style={{ flex:1,background:`${c}12`,border:`1px solid ${c}28`,borderRadius:8,padding:"8px 10px" }}>
            <div style={{ fontSize:18,fontWeight:800,color:c,fontFamily:"var(--font-display)" }}>{n}</div>
            <div style={{ fontSize:10,color:"var(--color-text-tertiary)",marginTop:1 }}>{l}</div>
          </div>
        ))}
      </div>
      <div style={{ display:"flex",gap:6 }}>
        {[["To Do","#c81f30",3],["In Progress","#d99a3f",2],["Done","#3fae7d",4]].map(([l,c,cnt])=>(
          <div key={l} style={{ flex:1,background:"rgba(255,255,255,0.02)",border:`1px solid ${c}22`,borderRadius:6,padding:"8px 8px" }}>
            <div style={{ fontSize:9,fontWeight:700,color:c,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.05em" }}>{l}</div>
            {[...Array(cnt)].map((_,i)=><div key={i} style={{ height:5,borderRadius:3,background:c,opacity:0.15+i*0.12,marginBottom:3 }}/>)}
          </div>
        ))}
      </div>
    </div>
  );
  if (stepId === "nav_badges") return (
    <div style={{ ...s, background:"rgba(255,255,255,0.03)",border:"1px solid var(--border-glass)",padding:"12px 14px",display:"flex",flexDirection:"column",gap:7 }}>
      {[["⬡","Dashboard","#d99a3f","8"],["✦","Tasks","#ff6a52","24"],["◈","Compliance","#ff4d5e","3"],["▲","Reports","#3fae7d","12"],["⊕","Ownership","#c81f30","5"]].map(([icon,label,c,badge])=>(
        <div key={label} style={{ display:"flex",alignItems:"center",gap:9,padding:"5px 8px",borderRadius:7,background:"rgba(255,255,255,0.025)" }}>
          <span style={{ fontSize:13 }}>{icon}</span>
          <span style={{ flex:1,fontSize:12,color:"var(--color-text-secondary)",fontWeight:500 }}>{label}</span>
          <div style={{ minWidth:20,height:18,borderRadius:999,background:`${c}25`,border:`1px solid ${c}45`,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 6px" }}>
            <span style={{ fontSize:10,fontWeight:700,color:c }}>{badge}</span>
          </div>
        </div>
      ))}
    </div>
  );
  if (stepId === "tasks") return (
    <div style={{ ...s, background:"rgba(255,255,255,0.03)",border:"1px solid var(--border-glass)",padding:"12px 14px" }}>
      <div style={{ display:"flex",gap:6,marginBottom:10 }}>
        {["All","To Do","In Progress","Done"].map((t,i)=>(
          <div key={t} style={{ padding:"3px 10px",borderRadius:999,fontSize:10,fontWeight:600,background:i===0?"rgba(200,31,48,0.2)":"rgba(255,255,255,0.04)",border:i===0?"1px solid rgba(200,31,48,0.4)":"1px solid var(--border-glass)",color:i===0?"#c81f30":"var(--color-text-tertiary)" }}>{t}</div>
        ))}
      </div>
      {[["Fix auth bug","critical","#ff4d5e","Alex"],["Write docs","high","#d99a3f","Sam"],["Deploy v2","medium","#c81f30","Kim"],["Add tests","low","#3fae7d","—"]].map(([task,pri,c,ass])=>(
        <div key={task} style={{ display:"flex",alignItems:"center",gap:8,padding:"6px 0",borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
          <div style={{ width:6,height:6,borderRadius:"50%",background:c,flexShrink:0 }}/>
          <span style={{ flex:1,fontSize:11,color:"var(--color-text-secondary)" }}>{task}</span>
          <span style={{ fontSize:9,padding:"2px 7px",borderRadius:999,background:`${c}18`,color:c,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.04em" }}>{pri}</span>
          <span style={{ fontSize:10,color:"var(--color-text-tertiary)",minWidth:28,textAlign:"right" }}>{ass}</span>
        </div>
      ))}
    </div>
  );
  if (stepId === "compliance") return (
    <div style={{ ...s, background:"rgba(255,255,255,0.03)",border:"1px solid var(--border-glass)",padding:"14px 16px" }}>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12 }}>
        <span style={{ fontSize:11,color:"var(--color-text-secondary)",fontWeight:600 }}>Compliance Score</span>
        <span style={{ fontSize:22,fontWeight:800,color:"#3fae7d",fontFamily:"var(--font-display)" }}>84%</span>
      </div>
      <div style={{ height:5,borderRadius:999,background:"rgba(255,255,255,0.06)",marginBottom:12,overflow:"hidden" }}>
        <div style={{ height:"100%",width:"84%",borderRadius:999,background:"linear-gradient(90deg,#3fae7d,#d99a3f)",transition:"width 0.6s" }}/>
      </div>
      {[["Overdue tasks","2","#ff4d5e"],["Unassigned","1","#d99a3f"],["Stale (7d+)","0","#3fae7d"],["High priority idle","1","#c81f30"]].map(([l,n,c])=>(
        <div key={l} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"4px 0",borderBottom:"1px solid rgba(255,255,255,0.03)" }}>
          <span style={{ fontSize:11,color:"var(--color-text-tertiary)" }}>{l}</span>
          <span style={{ fontSize:12,fontWeight:700,color:c }}>{n}</span>
        </div>
      ))}
    </div>
  );
  if (stepId === "knowledge") return (
    <div style={{ ...s, background:"rgba(255,255,255,0.03)",border:"1px solid var(--border-glass)",padding:"12px 14px" }}>
      <div style={{ display:"flex",gap:6,marginBottom:9 }}>
        {[["📌","Pinned","#d99a3f"],["📄","SOP","#ff6a52"],["📋","Runbook","#c81f30"]].map(([e,l,c])=>(
          <div key={l} style={{ flex:1,padding:"6px 8px",borderRadius:8,background:`${c}12`,border:`1px solid ${c}28` }}>
            <div style={{ fontSize:14,marginBottom:4 }}>{e}</div>
            <div style={{ fontSize:9,color:c,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.04em" }}>{l}</div>
          </div>
        ))}
      </div>
      {[["Deploy Runbook","📋","#c81f30"],["Auth SOP","📄","#ff6a52"],["Q3 Decisions","📝","#3fae7d"],["On-call Guide","⚡","#ff4d5e"]].map(([title,e,c])=>(
        <div key={title} style={{ display:"flex",alignItems:"center",gap:8,padding:"5px 0",borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
          <span style={{ fontSize:12 }}>{e}</span>
          <span style={{ flex:1,fontSize:11,color:"var(--color-text-secondary)" }}>{title}</span>
          <div style={{ width:6,height:6,borderRadius:"50%",background:c }}/>
        </div>
      ))}
    </div>
  );
  if (stepId === "ownership") return (
    <div style={{ ...s, background:"rgba(255,255,255,0.03)",border:"1px solid var(--border-glass)",padding:"12px 14px",display:"flex",flexDirection:"column",gap:7 }}>
      {[["Alex Chen","#ff6a52",8,75],["Sam Park","#c81f30",5,60],["Kim Lee","#3fae7d",3,90],["Jordan","#d99a3f",11,45]].map(([name,c,tasks,pct])=>(
        <div key={name} style={{ display:"flex",alignItems:"center",gap:9 }}>
          <div style={{ width:28,height:28,borderRadius:"50%",background:`${c}25`,border:`1.5px solid ${c}50`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:c,flexShrink:0 }}>{name[0]}</div>
          <div style={{ flex:1 }}>
            <div style={{ display:"flex",justifyContent:"space-between",marginBottom:3 }}>
              <span style={{ fontSize:11,color:"var(--color-text-secondary)",fontWeight:500 }}>{name}</span>
              <span style={{ fontSize:10,color:"var(--color-text-tertiary)" }}>{tasks} tasks · {pct}%</span>
            </div>
            <div style={{ height:3,borderRadius:999,background:"rgba(255,255,255,0.06)",overflow:"hidden" }}>
              <div style={{ height:"100%",width:`${pct}%`,borderRadius:999,background:c,opacity:0.7 }}/>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
  if (stepId === "reports") return (
    <div style={{ ...s, background:"rgba(255,255,255,0.03)",border:"1px solid var(--border-glass)",padding:"14px 16px" }}>
      <div style={{ display:"flex",gap:8,marginBottom:12 }}>
        {[["Velocity","↑12%","#3fae7d"],["Done Rate","78%","#ff6a52"],["Avg Time","2.4d","#c81f30"]].map(([l,v,c])=>(
          <div key={l} style={{ flex:1,padding:"8px",borderRadius:8,background:`${c}10`,border:`1px solid ${c}25`,textAlign:"center" }}>
            <div style={{ fontSize:14,fontWeight:800,color:c,fontFamily:"var(--font-display)" }}>{v}</div>
            <div style={{ fontSize:9,color:"var(--color-text-tertiary)",marginTop:2,fontWeight:600 }}>{l}</div>
          </div>
        ))}
      </div>
      <div style={{ display:"flex",alignItems:"flex-end",gap:4,height:52 }}>
        {[35,55,42,68,50,75,90,62,80,95].map((h,i)=>(
          <div key={i} style={{ flex:1,height:`${h}%`,borderRadius:"3px 3px 0 0",background:i===9?"#ff6a52":i===8?"rgba(255,106,82,0.6)":"rgba(255,106,82,0.25)",transition:"height 0.3s" }}/>
        ))}
      </div>
      <div style={{ height:1,background:"rgba(255,255,255,0.05)",marginTop:2 }}/>
    </div>
  );
  if (stepId === "integrations") return (
    <div style={{ ...s, background:"rgba(255,255,255,0.03)",border:"1px solid var(--border-glass)",padding:"12px 14px",display:"grid",gridTemplateColumns:"1fr 1fr",gap:7 }}>
      {[["Slack","#4A154B","💬","Connected"],["GitHub","#24292e","⚡","Connect"],["Zapier","#FF4A00","⚙","Connect"],["Notion","#f5f0eb","📝","Connect"]].map(([name,c,e,status])=>(
        <div key={name} style={{ padding:"10px",borderRadius:8,background:"rgba(255,255,255,0.025)",border:`1px solid ${status==="Connected"?"rgba(63,174,125,0.35)":"var(--border-glass)"}` }}>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:5 }}>
            <span style={{ fontSize:16 }}>{e}</span>
            <div style={{ fontSize:9,padding:"2px 6px",borderRadius:999,background:status==="Connected"?"rgba(63,174,125,0.2)":"rgba(255,255,255,0.07)",color:status==="Connected"?"#3fae7d":"var(--color-text-tertiary)",fontWeight:600 }}>{status}</div>
          </div>
          <div style={{ fontSize:11,color:"var(--color-text-secondary)",fontWeight:600 }}>{name}</div>
        </div>
      ))}
    </div>
  );
  if (stepId === "api") return (
    <div style={{ ...s, background:"#0c0503",border:"1px solid rgba(63,174,125,0.2)",padding:"12px 16px",fontFamily:"var(--font-mono)" }}>
      <div style={{ display:"flex",gap:6,marginBottom:8 }}>
        {["GET","POST","PUT","DELETE"].map((m,i)=>(<div key={m} style={{ padding:"2px 8px",borderRadius:4,fontSize:9,fontWeight:700,background:["rgba(63,174,125,0.2)","rgba(255,106,82,0.2)","rgba(217,154,63,0.2)","rgba(255,77,94,0.2)"][i],color:["#3fae7d","#ff6a52","#d99a3f","#ff4d5e"][i] }}>{m}</div>))}
      </div>
      {["/tasks","  → list + filter","/tasks/:id","  → task details","/tasks (POST)","  → create task"].map((line,i)=>(
        <div key={i} style={{ fontSize:10,color:i%2===0?"#d99a3f":"var(--color-text-tertiary)",marginBottom:1,lineHeight:1.7 }}>{line}</div>
      ))}
      <div style={{ marginTop:8,padding:"6px 10px",borderRadius:6,background:"rgba(63,174,125,0.08)",border:"1px solid rgba(63,174,125,0.15)",fontSize:10,color:"#3fae7d" }}>✓ Authorization: Bearer ••••••••</div>
    </div>
  );
  if (stepId === "finish") return (
    <div style={{ ...s, background:`${color}0d`,border:`1px solid ${color}30`,padding:"16px 18px",textAlign:"center" }}>
      <div style={{ fontSize:42,marginBottom:8,animation:"pulse 1.5s ease-in-out infinite" }}>🏆</div>
      <div style={{ fontSize:13,fontWeight:700,color:"var(--color-text-primary)",marginBottom:6 }}>You earned 100 XP!</div>
      <div style={{ display:"flex",gap:6,flexWrap:"wrap",justifyContent:"center" }}>
        {["Dashboard","Tasks","Compliance","Knowledge","Ownership","Reports","API"].map((l,i)=>(
          <div key={l} style={{ padding:"3px 10px",borderRadius:999,fontSize:10,fontWeight:600,background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.12)",color:"var(--color-text-secondary)",animation:`fadeUp 0.3s ${i*0.06}s ease both` }}>✓ {l}</div>
        ))}
      </div>
    </div>
  );
  return null;
}

// -- Main Tour Overlay — with optional skip -----------------------------------
function TourOverlay({ onComplete, onSkip }) {
  const [step, setStep]             = useState(0);
  const [xp, setXp]                 = useState(0);
  const [exiting, setExiting]       = useState(false);
  const [typing, setTyping]         = useState(true);
  const [shownChars, setShownChars] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [btnHover, setBtnHover]     = useState(false);
  const [showSkipNotice, setShowSkipNotice] = useState(false);

  const skipIntro = () => {
    localStorage.setItem("aw_tour_done", "1");
    setShowSkipNotice(true);
  };

  const dismissSkipNotice = () => {
    setExiting(true);
    setTimeout(() => onSkip(), 450);
  };

  const current = TOUR_STEPS[step];
  const isFirst = step === 0;
  const isLast  = step === TOUR_STEPS.length - 1;
  const progress = ((step) / (TOUR_STEPS.length - 1)) * 100;

  // Typewriter effect — faster on later steps so users don't get impatient
  useEffect(() => {
    setTyping(true);
    setShownChars(0);
    const text = current.description;
    let i = 0;
    const speed = isFirst ? 22 : 14;
    const interval = setInterval(() => {
      i++;
      setShownChars(i);
      if (i >= text.length) { clearInterval(interval); setTyping(false); }
    }, speed);
    return () => clearInterval(interval);
  }, [step]);

  const advance = () => {
    if (typing) { setShownChars(current.description.length); setTyping(false); return; }
    if (isLast) { finish(); return; }
    setXp(current.xp);
    setStep(s => s + 1);
  };

  const finish = () => {
    setShowConfetti(true);
    setTimeout(() => {
      setExiting(true);
      localStorage.setItem("aw_tour_done", "1");
      setTimeout(() => onComplete(), 500);
    }, 900);
  };

  // Keyboard shortcut: Space / Enter to advance
  useEffect(() => {
    const handler = (e) => {
      if (e.code === "Space" || e.code === "Enter") { e.preventDefault(); advance(); }
      if (e.code === "ArrowLeft" && !isFirst && !isLast && step > 1) setStep(s => s - 1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [step, typing, isFirst, isLast]);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(4,5,12,0.96)", backdropFilter: "blur(16px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: "20px",
      opacity: exiting ? 0 : 1, transition: "opacity 0.45s ease",
    }}>
      {showConfetti && <ConfettiBurst />}

      {/* ── Skip Intro Notice Modal ── */}
      {showSkipNotice && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 10001,
          background: "rgba(4,5,12,0.92)", backdropFilter: "blur(12px)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: "20px",
        }}>
          <div style={{
            width: "100%", maxWidth: 500,
            background: "linear-gradient(160deg,#140806 0%,#120705 60%,#0a0403 100%)",
            border: "1px solid rgba(255,106,82,0.35)", borderRadius: 24,
            padding: "36px 32px", boxShadow: "0 40px 100px rgba(0,0,0,0.8), 0 0 60px rgba(255,106,82,0.08)",
            textAlign: "center", animation: "fadeUp 0.4s cubic-bezier(0.16,1,0.3,1) both",
          }}>
            {/* Icon */}
            <div style={{
              width: 72, height: 72, borderRadius: 20, margin: "0 auto 20px",
              background: "rgba(255,106,82,0.12)", border: "1.5px solid rgba(255,106,82,0.35)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 32, boxShadow: "0 0 32px rgba(255,106,82,0.2)",
            }}>📚</div>

            {/* Heading */}
            <div style={{
              fontSize: 22, fontWeight: 800, letterSpacing: "-0.03em", marginBottom: 10,
              fontFamily: "var(--font-display)",
              background: "linear-gradient(135deg, #efe7df 0%, #ffb199 100%)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
            }}>Intro skipped — you're all set!</div>

            {/* Body */}
            <div style={{
              fontSize: 14, color: "var(--color-text-secondary)", lineHeight: 1.75, marginBottom: 24,
            }}>
              No worries — everything you need is in the{" "}
              <span style={{ color: "#ffb199", fontWeight: 700 }}>Knowledge Panel</span>.{" "}
              You can find it in the sidebar anytime. It contains all the info about how the software works, and you can also{" "}
              <span style={{ color: "#ffb199", fontWeight: 600 }}>add notes for your team</span>{" "}
              so everyone stays on the same page.
            </div>

            {/* Feature bullets */}
            <div style={{
              background: "rgba(255,106,82,0.06)", border: "1px solid rgba(255,106,82,0.18)",
              borderRadius: 14, padding: "16px 20px", marginBottom: 28, textAlign: "left",
            }}>
              {[
                ["📖", "Full software documentation & feature guides"],
                ["📝", "Add & manage notes for your team"],
                ["🔍", "Searchable — find any info instantly"],
              ].map(([icon, text]) => (
                <div key={text} style={{
                  display: "flex", alignItems: "center", gap: 12,
                  fontSize: 13, color: "var(--color-text-secondary)", padding: "6px 0",
                }}>
                  <span style={{ fontSize: 16, flexShrink: 0 }}>{icon}</span>
                  <span>{text}</span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <button
              onClick={dismissSkipNotice}
              style={{
                width: "100%", height: 50, borderRadius: 13, border: "none",
                background: "linear-gradient(135deg, #ff6a52 0%, #c81f30 100%)",
                color: "#f5f0eb", fontFamily: "var(--font-sans)", fontSize: 15, fontWeight: 700,
                cursor: "pointer", letterSpacing: "-0.01em",
                boxShadow: "0 0 28px rgba(255,106,82,0.4), 0 8px 20px rgba(0,0,0,0.3)",
                transition: "opacity 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
              onMouseLeave={e => e.currentTarget.style.opacity = "1"}
            >
              Go to Dashboard →
            </button>
          </div>
        </div>
      )}

      {/* Dynamic ambient glow — large, shifts color with step */}
      <div style={{
        position: "absolute", width: 900, height: 900, borderRadius: "50%",
        background: `radial-gradient(circle,${current.color}14 0%,transparent 65%)`,
        top: "50%", left: "50%", transform: "translate(-50%,-50%)",
        pointerEvents: "none", transition: "background 0.6s ease",
      }} />
      {/* Secondary smaller orb — offset for depth */}
      <div style={{
        position: "absolute", width: 400, height: 400, borderRadius: "50%",
        background: `radial-gradient(circle,${current.color}0e 0%,transparent 70%)`,
        top: "20%", left: "65%", pointerEvents: "none", transition: "background 0.6s ease",
      }} />

      {/* -- Keyboard hint + Skip Intro (top right) -- */}
      <div style={{
        position: "absolute", top: 20, right: 24,
        display: "flex", alignItems: "center", gap: 14,
      }}>
        <div style={{
          fontSize: 12, color: "#d9cfc6",
          display: "flex", alignItems: "center", gap: 6, fontFamily: "var(--font-mono)",
        }}>
          <kbd style={{ padding:"3px 8px",borderRadius:5,background:"rgba(255,255,255,0.14)",border:"1px solid rgba(255,255,255,0.35)",color:"#f5f0eb",fontSize:11 }}>Space</kbd>
          <span>to advance</span>
        </div>
        <button
          onClick={skipIntro}
          style={{
            height: 36, padding: "0 16px", borderRadius: 9,
            border: "1px solid rgba(255,255,255,0.45)",
            background: "rgba(255,255,255,0.14)",
            color: "#f5f0eb",
            fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 600,
            cursor: "pointer", transition: "all 0.14s",
            display: "flex", alignItems: "center", gap: 5,
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.24)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.14)"; }}
        >
          <span style={{ fontSize: 14 }}>⏭</span> Skip Intro
        </button>
      </div>

      {/* -- Step counter (top left) -- */}
      <div style={{
        position: "absolute", top: 20, left: 24,
        fontSize: 13, fontWeight: 600, color: "#f5f0eb", fontFamily: "var(--font-mono)",
        display: "flex", alignItems: "center", gap: 8,
      }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: current.color, boxShadow: `0 0 10px ${current.color}` }} />
        Step {step + 1} of {TOUR_STEPS.length}
      </div>

      {/* -- Main card -- */}
      <div className="tour-card" style={{
        width: "100%", maxWidth: 620,
        background: "linear-gradient(160deg,#140806 0%,#120705 50%,#0a0403 100%)",
        border: `1px solid ${current.color}30`, borderRadius: 26,
        boxShadow: `0 50px 140px rgba(0,0,0,0.85),0 0 0 1px ${current.color}18,0 0 80px ${current.color}08`,
        overflow: "hidden", position: "relative",
        transition: "border-color 0.5s ease,box-shadow 0.5s ease",
      }}>
        <TourParticles color={current.color} />

        {/* Top progress bar — continuous fill */}
        <div style={{ height: 3, background: "rgba(255,255,255,0.05)", position: "relative" }}>
          <div style={{
            height: "100%", background: current.gradient,
            width: `${progress}%`, transition: "width 0.5s cubic-bezier(0.34,1.56,0.64,1),background 0.4s",
            borderRadius: "0 999px 999px 0",
            boxShadow: `0 0 10px ${current.color}60`,
          }} />
        </div>

        {/* Header row */}
        <div style={{ padding: "16px 26px 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {/* XP pill */}
          <div style={{
            padding: "4px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700,
            background: `${current.color}18`, color: current.color,
            border: `1px solid ${current.color}38`, fontFamily: "var(--font-display)",
            transition: "all 0.35s", display: "flex", alignItems: "center", gap: 5,
          }}>
            <span>⚡</span>
            <span style={{ fontVariantNumeric: "tabular-nums" }}>{xp} XP</span>
          </div>
          {/* Step dots */}
          <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
            {TOUR_STEPS.map((s2, i) => (
              <div key={i} style={{
                width: i === step ? 22 : 6, height: 6, borderRadius: 999,
                background: i < step ? current.color : i === step ? current.color : "rgba(255,255,255,0.1)",
                opacity: i > step ? 0.35 : 1,
                transition: "width 0.4s cubic-bezier(0.34,1.56,0.64,1),background 0.3s,opacity 0.3s",
                boxShadow: i === step ? `0 0 8px ${current.color}80` : "none",
              }} />
            ))}
          </div>
        </div>

        {/* Step content */}
        <div className="tour-step-slide" key={step} style={{ padding: "22px 26px 26px" }}>
          {/* Icon + subtitle row */}
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 18 }}>
            <div style={{
              width: 64, height: 64, borderRadius: 18, flexShrink: 0,
              background: `${current.color}15`, border: `1.5px solid ${current.color}38`,
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28,
              boxShadow: `0 0 28px ${current.color}22,inset 0 1px 0 rgba(255,255,255,0.08)`,
              transition: "all 0.35s ease",
            }}>{current.emoji}</div>
            <div>
              <div style={{
                display: "inline-flex", alignItems: "center", padding: "3px 11px",
                borderRadius: 999, marginBottom: 6,
                background: `${current.color}12`, border: `1px solid ${current.color}28`,
                fontSize: 10, fontWeight: 700, color: current.color,
                letterSpacing: "0.06em", textTransform: "uppercase",
              }}>{current.subtitle}</div>
              <div style={{
                fontSize: 22, fontWeight: 800, color: "var(--color-text-primary)",
                fontFamily: "var(--font-display)", letterSpacing: "-0.03em", lineHeight: 1.2,
              }}>{current.title}</div>
            </div>
          </div>

          {/* Typewriter description */}
          <div style={{
            fontSize: 14, color: "var(--color-text-secondary)", lineHeight: 1.75,
            minHeight: 56, marginBottom: 16,
          }}>
            {current.description.slice(0, shownChars)}
            {typing && <span style={{ opacity: 0.7, animation: "pulse 0.55s ease-in-out infinite" }}>|</span>}
          </div>

          {/* Feature visual — mini preview of the section */}
          {!typing && (
            <div style={{ marginBottom: 18, animation: "fadeUp 0.35s 0.05s ease both" }}>
              <StepVisual stepId={current.id} color={current.color} />
            </div>
          )}

          {/* Tip box — shown after typing */}
          {current.tip && !typing && !current.isBadgeStep && (
            <div style={{
              padding: "11px 15px", borderRadius: 11, marginBottom: 18,
              background: `${current.color}0a`, border: `1px solid ${current.color}22`,
              fontSize: 12.5, color: "var(--color-text-secondary)", lineHeight: 1.65,
              animation: "fadeUp 0.3s 0.12s ease both", display: "flex", gap: 9, alignItems: "flex-start",
            }}>
              <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>💡</span>
              <span>{current.tip.replace("💡 Pro tip: ","").replace("💡 ","")}</span>
            </div>
          )}

          {/* CTA button */}
          <button
            onClick={advance}
            onMouseEnter={() => setBtnHover(true)}
            onMouseLeave={() => setBtnHover(false)}
            style={{
              width: "100%", height: 52, borderRadius: 14, border: "none",
              background: current.gradient, color: "#f5f0eb",
              fontFamily: "var(--font-sans)", fontSize: 15, fontWeight: 700,
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 9,
              boxShadow: `0 0 32px ${current.color}40,0 8px 24px rgba(0,0,0,0.3),inset 0 1px 0 rgba(255,255,255,0.22)`,
              transform: btnHover ? "translateY(-2px) scale(1.01)" : "translateY(0) scale(1)",
              transition: "transform 0.18s cubic-bezier(0.34,1.56,0.64,1),box-shadow 0.18s",
              letterSpacing: "-0.01em",
            }}
          >
            {typing
              ? <><span style={{ fontSize: 17 }}>⏭</span> Skip typing</>
              : isLast
                ? <><span style={{ fontSize: 19 }}>🚀</span> Launch Dashboard &mdash; Let's Go!</>
                : isFirst
                  ? <><span style={{ fontSize: 17 }}>▶</span> Start Tour &nbsp;<span style={{ opacity: 0.75, fontSize: 13 }}>+{TOUR_STEPS[1].xp} XP</span></>
                  : <><span style={{ fontSize: 14, opacity: 0.85 }}>Continue</span> &nbsp;→&nbsp; <span style={{ opacity: 0.7, fontSize: 13 }}>+{Math.max(0,(TOUR_STEPS[step + 1]?.xp ?? current.xp) - current.xp)} XP</span></>
            }
          </button>

          {/* Back + "why no skip" row */}
          {!isFirst && !isLast && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 12 }}>
              <button onClick={() => setStep(s => s - 1)} style={{
                height: 38, padding: "0 18px", borderRadius: 9,
                border: "1px solid rgba(255,255,255,0.4)", background: "rgba(255,255,255,0.12)",
                color: "#f5f0eb", fontFamily: "var(--font-sans)",
                fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all 0.12s",
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.22)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.12)"; }}
              >← Back</button>
              <div style={{ fontSize: 12, color: "#d9cfc6", display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ fontSize: 13 }}>🔒</span>
                <span>Complete the tour to unlock the dashboard</span>
              </div>
            </div>
          )}
          {isFirst && !typing && (
            <div style={{ marginTop: 10, textAlign: "center", fontSize: 12, color: "#d9cfc6" }}>
              ✦ Takes ~2 minutes &nbsp;·&nbsp; Earn 100 XP &nbsp;·&nbsp; Learn every feature
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// -- Full-screen loading spinner (shown while auth state initialises) -----------
function AppLoader() {
  return (
    <div style={{
      minHeight: "100vh", background: "var(--bg-base)",
      display: "flex", alignItems: "center", justifyContent: "center",
      flexDirection: "column", gap: 24, position: "relative", overflow: "hidden",
    }}>
      {/* Ambient glow */}
      <div style={{ position: "absolute", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,106,82,0.08) 0%, transparent 70%)", top: "50%", left: "50%", transform: "translate(-50%,-50%)", pointerEvents: "none" }} />

      {/* Logo mark */}
      <div style={{ position: "relative" }}>
        <div style={{
          width: 56, height: 56, borderRadius: 16,
          background: "var(--grad-primary)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 22, fontWeight: 800, color: "#f5f0eb",
          fontFamily: "var(--font-display)",
          boxShadow: "0 0 40px rgba(255,106,82,0.45), 0 0 80px rgba(200,31,48,0.2)",
          animation: "glow-pulse 2s ease-in-out infinite",
          letterSpacing: "-0.02em",
        }}>Ω</div>
      </div>

      {/* Brand name */}
      <div style={{ textAlign: "center" }}>
        <div style={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 700, color: "var(--color-text-primary)", letterSpacing: "-0.03em" }}>AI Workflow</div>
        <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginTop: 3, letterSpacing: "0.1em", textTransform: "uppercase" }}>Coordinator</div>
      </div>

      {/* Loading bar */}
      <div style={{ width: 180, height: 2, background: "rgba(255,255,255,0.06)", borderRadius: 999, overflow: "hidden" }}>
        <div style={{ height: "100%", background: "var(--grad-primary)", borderRadius: 999, animation: "shimmer 1.5s ease-in-out infinite", backgroundSize: "200% 100%" }} />
      </div>
    </div>
  );
}

// -- Columns / nav config (unchanged) -----------------------------------------
const COLUMNS = [
  { status: "to_do",       label: "To Do"       },
  { status: "in_progress", label: "In Progress" },
  { status: "completed",   label: "Done"        },
  { status: "cancelled",   label: "Cancelled"   },
];

const NAV_ITEMS = [
  { icon: "⬡", label: "Dashboard",    badge: null },
  { icon: "✦", label: "Tasks",        badge: null },
  { icon: "◈", label: "Compliance",   badge: "5"  },
  { icon: "◉", label: "Knowledge",    badge: null },
  { icon: "▲", label: "Reports",      badge: null },
  { icon: "⊕", label: "Ownership",    badge: null },
  { icon: "⛓", label: "Integrations", badge: null },
  { icon: "🌐", label: "Locale",       badge: null },
  { icon: "💬", label: "Teams",        badge: null },
  { icon: "🔑", label: "API",          badge: null },
  { icon: "⚙", label: "Settings",     badge: null },
];

const TABS = [
  { label: "All Tasks",   filter: null          },
  { label: "To Do",       filter: "to_do"       },
  { label: "In Progress", filter: "in_progress" },
  { label: "Done",        filter: "completed"   },
];

// -- Sidebar -------------------------------------------------------------------
const NAV_GROUPS = [
  {
    label: "Core",
    items: [
      { icon: "dashboard", label: "Dashboard",    idx: 0 },
      { icon: "tasks",     label: "Tasks",        idx: 1 },
      { icon: "reports",   label: "Reports",      idx: 4 },
    ]
  },
  {
    label: "Manage",
    items: [
      { icon: "comply",    label: "Compliance",   idx: 2, badge: true },
      { icon: "know",      label: "Knowledge",    idx: 3 },
      { icon: "owner",     label: "Ownership",    idx: 5 },
    ]
  },
  {
    label: "Connect",
    items: [
      { icon: "integrate", label: "Integrations", idx: 6 },
      { icon: "locale",    label: "Locale",       idx: 7 },
      { icon: "teams",     label: "Teams",        idx: 8 },
      { icon: "api",       label: "API",          idx: 9 },
    ]
  },
  {
    label: "Account",
    items: [
      { icon: "settings",  label: "Settings",     idx: 10 },
    ]
  },
];

const NAV_ICONS = {
  dashboard: (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><rect x="1" y="1" width="5.5" height="5.5" rx="1.5" fill="currentColor" opacity="0.9"/><rect x="8.5" y="1" width="5.5" height="5.5" rx="1.5" fill="currentColor" opacity="0.5"/><rect x="1" y="8.5" width="5.5" height="5.5" rx="1.5" fill="currentColor" opacity="0.5"/><rect x="8.5" y="8.5" width="5.5" height="5.5" rx="1.5" fill="currentColor" opacity="0.9"/></svg>
  ),
  tasks: (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M2 3.5h11M2 7.5h8M2 11.5h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/><circle cx="12.5" cy="11.5" r="1.5" fill="currentColor" opacity="0.7"/></svg>
  ),
  reports: (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M1.5 13.5V9M5.5 13.5V5.5M9.5 13.5V7.5M13.5 13.5V2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
  ),
  comply: (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M7.5 1.5L13 4v4c0 2.8-2.5 5-5.5 5.5C4.5 13 2 10.8 2 8V4L7.5 1.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/><path d="M5 7.5l1.5 1.5L10 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
  ),
  know: (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M3 2.5h9a1 1 0 011 1v9a1 1 0 01-1 1H3a1 1 0 01-1-1v-9a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.4"/><path d="M5 6h5M5 8.5h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
  ),
  owner: (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><circle cx="7.5" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.4"/><circle cx="2.5" cy="11" r="1.5" stroke="currentColor" strokeWidth="1.3" opacity="0.7"/><circle cx="12.5" cy="11" r="1.5" stroke="currentColor" strokeWidth="1.3" opacity="0.7"/><path d="M4 11C4 9.5 5.5 8 7.5 8s3.5 1.5 3.5 3" stroke="currentColor" strokeWidth="1.3" opacity="0.7" strokeLinecap="round"/><path d="M1 13c0-1 .7-2 1.5-2M14 13c0-1-.7-2-1.5-2" stroke="currentColor" strokeWidth="1.3" opacity="0.5" strokeLinecap="round"/></svg>
  ),
  integrate: (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M5.5 4L2 7.5l3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M9.5 4L13 7.5l-3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M6.5 11.5l2-8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/></svg>
  ),
  locale: (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><circle cx="7.5" cy="7.5" r="5.5" stroke="currentColor" strokeWidth="1.4"/><path d="M7.5 2c-1.5 1.5-2.5 3.5-2.5 5.5s1 4 2.5 5.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/><path d="M7.5 2c1.5 1.5 2.5 3.5 2.5 5.5s-1 4-2.5 5.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/><path d="M2 7.5h11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" opacity="0.7"/></svg>
  ),
  teams: (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><circle cx="5" cy="5.5" r="2" stroke="currentColor" strokeWidth="1.4"/><circle cx="10.5" cy="5.5" r="2" stroke="currentColor" strokeWidth="1.4" opacity="0.7"/><path d="M1.5 13c0-1.7 1.6-3 3.5-3s3.5 1.3 3.5 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><path d="M10.5 10.5c1.6.3 3 1.5 3 2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" opacity="0.6"/></svg>
  ),
  api: (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><rect x="1.5" y="3.5" width="12" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.4"/><path d="M4.5 7.5l1.5-1.5L4.5 4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" opacity="0.8"/><path d="M8 8h2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" opacity="0.8"/></svg>
  ),
  settings: (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><circle cx="7.5" cy="7.5" r="2" stroke="currentColor" strokeWidth="1.4"/><path d="M7.5 1.5v1M7.5 12.5v1M1.5 7.5h1M12.5 7.5h1M3.4 3.4l.7.7M10.9 10.9l.7.7M3.4 11.6l.7-.7M10.9 4.1l.7-.7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" opacity="0.7"/></svg>
  ),
};

function Sidebar({ activeNav, onNavChange, navBadges = {}, collapsed, onToggleCollapse, mobileOpen, onCloseMobile }) {
  const { user, logout } = useAuth();

  const initials = user?.name
    ? user.name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2)
    : "AW";

  const roleLabel = { architect: "Architect", navigator: "Navigator", operator: "Operator", solo: "Solo" }[user?.role] || "Member";
  const roleColor = { architect: "#d99a3f", navigator: "#ff6a52", operator: "#3fae7d", solo: "#c81f30" }[user?.role] || "#8a8078";

  const winW = useWindowWidth();
  const isPhone = winW < 640;
  // Auto-collapse sidebar on narrow (tablet) screens. On phones the sidebar
  // isn't a permanent icon-rail at all — it's a full-width off-canvas
  // drawer, toggled open/closed, so it never eats into the small viewport.
  const effectiveCollapsed = !isPhone && (collapsed || winW < 900);
  const sidebarW = isPhone ? 240 : (effectiveCollapsed ? 64 : 228);

  return (
    <motion.aside
      initial={{ x: -18, opacity: 0 }}
      animate={isPhone ? { x: mobileOpen ? 0 : -260, opacity: 1 } : { x: 0, opacity: 1 }}
      transition={{ duration: isPhone ? 0.28 : 0.45, ease: [0.16, 1, 0.3, 1] }}
      style={{
      position: "fixed", left: 0, top: 0, bottom: 0,
      width: sidebarW,
      background: "var(--bg-sidebar)",
      borderRight: "1px solid var(--border-glass)",
      display: "flex", flexDirection: "column", zIndex: 55,
      overflow: "hidden", transition: "width 0.25s cubic-bezier(0.4,0,0.2,1)",
      boxShadow: isPhone && mobileOpen ? "0 0 40px rgba(0,0,0,0.5)" : "none",
    }}>
      {/* Subtle ambient gradient */}
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(160deg, rgba(255,106,82,0.04) 0%, transparent 40%, rgba(200,31,48,0.03) 100%)", pointerEvents: "none" }} />

      {/* Top accent line */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: "var(--grad-primary)", opacity: 0.5 }} />

      {/* Logo */}
      <div style={{ padding: effectiveCollapsed ? "20px 14px" : "20px 18px", display: "flex", alignItems: "center", gap: 10, borderBottom: "1px solid var(--border-glass)", position: "relative", flexShrink: 0 }}>
        <div style={{
          width: 34, height: 34, borderRadius: 10, flexShrink: 0,
          background: "var(--grad-primary)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 800, color: "#f5f0eb",
          boxShadow: "0 0 20px rgba(255,106,82,0.4)",
          letterSpacing: "-0.02em",
        }}>Ω</div>

        {!effectiveCollapsed && (
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 700, color: "var(--color-text-primary)", letterSpacing: "-0.02em", lineHeight: 1.2 }}>AI Workflow</div>
            <div style={{ fontSize: 10, color: "var(--color-text-tertiary)", marginTop: 1, letterSpacing: "0.08em", textTransform: "uppercase" }}>Coordinator</div>
          </div>
        )}

        {/* Collapse toggle (desktop/tablet) or Close button (phone drawer) */}
        <button onClick={isPhone ? onCloseMobile : onToggleCollapse} style={{
          width: 22, height: 22, borderRadius: 6, border: "1px solid var(--border-glass)",
          background: "rgba(255,255,255,0.04)", cursor: "pointer", color: "var(--color-text-tertiary)",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          transition: "all 0.15s",
        }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.09)"; e.currentTarget.style.color = "var(--color-text-primary)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.color = "var(--color-text-tertiary)"; }}
          title={isPhone ? "Close menu" : (effectiveCollapsed ? "Expand sidebar" : "Collapse sidebar")}
        >
          {isPhone ? (
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M1.5 1.5l7 7M8.5 1.5l-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          ) : (
            <motion.svg
              width="10" height="10" viewBox="0 0 10 10" fill="none"
              animate={{ rotate: collapsed ? 180 : 0 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            >
              <path d="M7 2L3 5l4 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </motion.svg>
          )}
        </button>
      </div>

      {/* Nav groups */}
      <motion.nav
        variants={staggerContainer} initial="hidden" animate="show"
        style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: effectiveCollapsed ? "10px 8px" : "10px 10px", display: "flex", flexDirection: "column", gap: 0 }}
      >
        {NAV_GROUPS.map(group => (
          <div key={group.label} style={{ marginBottom: 4 }}>
            {!effectiveCollapsed && (
              <div style={{ fontSize: 9.5, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--color-text-muted)", padding: "10px 8px 5px", fontFamily: "var(--font-display)" }}>
                {group.label}
              </div>
            )}
            {collapsed && <div style={{ height: 8 }} />}

            {group.items.map(item => {
              const isActive = activeNav === item.idx;
              // Dynamic badge: live count from navBadges, fallback to static NAV_ITEMS badge
              const dynamicBadge = navBadges[item.idx];
              const staticBadge  = item.badge ? NAV_ITEMS[item.idx]?.badge : null;
              const badgeValue   = dynamicBadge ?? staticBadge ?? null;
              // Badge color: compliance/issues = red, tasks/dashboard = blue, others = purple
              const badgeColor   =
                item.idx === 2 ? "var(--grad-danger)"   :  // Compliance — red (issues)
                item.idx === 1 ? "linear-gradient(135deg,#ff6a52,#d99a3f)" :  // Tasks — blue
                item.idx === 0 ? "linear-gradient(135deg,#d99a3f,#c9702e)" :  // Dashboard — amber (active)
                item.idx === 4 ? "linear-gradient(135deg,#3fae7d,#d99a3f)" :  // Reports — green (completed)
                item.idx === 5 ? "linear-gradient(135deg,#c81f30,#ff6a52)" :  // Ownership — violet
                "var(--grad-danger)";
              return (
                <motion.div key={item.label}
                  variants={fadeUpItem}
                  role="button" tabIndex={0} title={collapsed ? item.label : undefined}
                  aria-current={isActive ? "page" : undefined}
                  onClick={() => { onNavChange(item.idx); if (isPhone) onCloseMobile?.(); }}
                  onKeyDown={e => { if (e.key === "Enter") { onNavChange(item.idx); if (isPhone) onCloseMobile?.(); } }}
                  style={{
                    display: "flex", alignItems: "center", gap: effectiveCollapsed ? 0 : 10,
                    padding: collapsed ? "9px" : "8px 10px",
                    borderRadius: 10, cursor: "pointer",
                    justifyContent: collapsed ? "center" : "flex-start",
                    position: "relative", marginBottom: 1,
                    color: isActive ? "#f5f0eb" : "var(--color-text-secondary)",
                    transition: "color 0.15s cubic-bezier(0.4,0,0.2,1)",
                  }}
                  onMouseEnter={e => {
                    if (!isActive) {
                      e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                      e.currentTarget.style.color = "var(--color-text-primary)";
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive) {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.color = "var(--color-text-secondary)";
                    }
                  }}
                >
                  {/* Animated active pill — slides between items with a spring,
                      instead of each item just snapping to its own static bg */}
                  {isActive && (
                    <motion.div
                      layoutId="sidebarActivePill"
                      transition={{ type: "spring", stiffness: 480, damping: 38 }}
                      style={{
                        position: "absolute", inset: 0, borderRadius: 10,
                        background: "linear-gradient(135deg, rgba(255,106,82,0.22) 0%, rgba(200,31,48,0.18) 100%)",
                        border: "1px solid rgba(255,106,82,0.3)",
                        boxShadow: "0 0 16px rgba(255,106,82,0.12)",
                        zIndex: 0,
                      }}
                    />
                  )}

                  {/* Active left bar */}
                  {isActive && <div style={{ position: "absolute", left: -10, top: "50%", transform: "translateY(-50%)", width: 3, height: "60%", borderRadius: "0 3px 3px 0", background: "var(--grad-primary)", boxShadow: "0 0 8px rgba(255,106,82,0.6)", zIndex: 1 }} />}

                  <span style={{ position: "relative", zIndex: 1, color: isActive ? "#ff6a52" : "inherit", transition: "color 0.15s", flexShrink: 0 }}>
                    {NAV_ICONS[item.icon]}
                  </span>

                  {!effectiveCollapsed && (
                    <span style={{ position: "relative", zIndex: 1, flex: 1, fontSize: 13, fontWeight: isActive ? 600 : 400, letterSpacing: "-0.01em", whiteSpace: "nowrap" }}>
                      {item.label}
                    </span>
                  )}

                  {!effectiveCollapsed && badgeValue !== null && (
                    <span style={{
                      position: "relative", zIndex: 1,
                      minWidth: 20, height: 18, padding: "0 5px", borderRadius: 999,
                      fontSize: 10, fontWeight: 700, color: "#f5f0eb",
                      background: badgeColor,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      transition: "all 0.2s cubic-bezier(0.34,1.56,0.64,1)",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                    }}>{badgeValue}</span>
                  )}
                  {collapsed && badgeValue !== null && (
                    <div style={{ position: "absolute", top: 4, right: 4, minWidth: 14, height: 14, borderRadius: 999, background: badgeColor, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 700, color: "#f5f0eb", padding: "0 3px" }}>
                      {badgeValue > 99 ? "99+" : badgeValue}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        ))}
      </motion.nav>

      {/* User footer */}
      <div style={{ padding: collapsed ? "12px 8px" : "12px 10px", borderTop: "1px solid var(--border-glass)", flexShrink: 0 }}>
        <div style={{
          display: "flex", alignItems: "center", gap: effectiveCollapsed ? 0 : 10,
          padding: collapsed ? "8px" : "8px 10px",
          borderRadius: 10, background: "rgba(255,255,255,0.04)",
          border: "1px solid var(--border-glass)",
          justifyContent: collapsed ? "center" : "flex-start",
          position: "relative", overflow: "hidden",
        }}>
          {/* Avatar */}
          <div style={{
            width: 30, height: 30, borderRadius: 9, flexShrink: 0,
            background: `linear-gradient(135deg, ${roleColor}90, ${roleColor}50)`,
            border: `1px solid ${roleColor}40`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "var(--font-display)", fontSize: 11, fontWeight: 700, color: "#f5f0eb",
          }}>{initials}</div>

          {!effectiveCollapsed && (
            <>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", letterSpacing: "-0.01em" }}>
                  {user?.name || "User"}
                </div>
                <div style={{ fontSize: 10, color: roleColor, fontWeight: 500, letterSpacing: "0.02em" }}>{roleLabel}</div>
              </div>

              <button onClick={logout} title="Sign out" style={{
                background: "transparent", border: "none", cursor: "pointer",
                color: "var(--color-text-tertiary)", padding: "4px", borderRadius: 6,
                display: "flex", alignItems: "center", transition: "color 0.15s",
                flexShrink: 0,
              }}
                onMouseEnter={e => e.currentTarget.style.color = "var(--accent-rose)"}
                onMouseLeave={e => e.currentTarget.style.color = "var(--color-text-tertiary)"}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9.5 4.5L12 7l-2.5 2.5M12 7H5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M5 2H3a1 1 0 00-1 1v8a1 1 0 001 1h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
              </button>
            </>
          )}
        </div>
      </div>
    </motion.aside>
  );
}

// -- SVG icons (unchanged) -----------------------------------------------------
const IconCheckbox = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <rect x="2" y="2" width="16" height="16" rx="4" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M6 10l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconProgress = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2"/>
    <circle cx="10" cy="10" r="3" fill="currentColor" opacity="0.6"/>
  </svg>
);
const IconDone = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M7 10l2.5 2.5L13 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Metric values now genuinely count up/down to their new total (not just
// fade in) whenever they change — driven by a framer-motion motion value
// rather than a CSS entrance animation, so it retriggers correctly on
// every update, not just first mount.
function AnimatedNumber({ value }) {
  const mv = useMotionValue(0);
  const rounded = useTransform(mv, v => Math.round(v));
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const controls = animate(mv, value, { duration: 0.7, ease: [0.16, 1, 0.3, 1] });
    const unsub = rounded.on("change", v => setDisplay(v));
    return () => { controls.stop(); unsub(); };
  }, [value]);

  return <span>{display}</span>;
}

function Sparkline({ color }) {
  return (
    <svg width="64" height="28" viewBox="0 0 64 28" fill="none" aria-hidden="true">
      <polyline points="0,22 10,18 20,20 30,12 40,14 52,6 64,8"
        stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
        fill="none" opacity="0.8" />
    </svg>
  );
}

// -- Segment 10: Ownership Graph view -----------------------------------------
function OwnershipGraph() {
  const { user, token } = useAuth();
  const API = BASE_URL;
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [selected, setSelected] = useState(null); // selected assignee node
  const [filter, setFilter]     = useState("");

  useEffect(() => {
    if (!token) return;
    const params = new URLSearchParams();
    if (user?.workspace?.id) params.set("workspace_id", user.workspace.id);
    else if (user?.id)       params.set("owner_id", user.id);

    fetch(`${API}/tasks/graph?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => { if (!r.ok) throw new Error("Failed to load graph"); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [API, user, token]);

  const PRIORITY_COLOR = { critical: "#ff4d5e", high: "#c9702e", medium: "#f0b354", low: "#6cc79a" };
  const STATUS_COLOR   = { to_do: "#ff6a52", in_progress: "#d99a3f", completed: "#3fae7d", cancelled: "#9a908a" };

  const nodes = useMemo(() => {
    if (!data?.nodes) return [];
    return data.nodes.filter(n =>
      !filter || n.assignee.toLowerCase().includes(filter.toLowerCase())
    );
  }, [data, filter]);

  if (loading) return (
    <main style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
        <div style={{ width: 24, height: 24, border: "2px solid rgba(255,106,82,0.2)", borderTopColor: "#ff6a52", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
        <span style={{ fontSize: 13, color: "var(--color-text-tertiary)" }}>Loading ownership data…</span>
      </div>
    </main>
  );

  if (error) return (
    <main style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⚠</div>
        <p style={{ color: "#ff4d5e", fontSize: 14 }}>{error}</p>
      </div>
    </main>
  );

  const selectedNode = selected ? data?.nodes?.find(n => n.assignee === selected) : null;

  return (
    <main className="page-enter" style={{ flex: 1, padding: "clamp(14px, 2.5vw, 28px) clamp(12px, 2.5vw, 28px) 40px", display: "flex", flexDirection: "column", gap: 24 }}>

      {/* Header */}
      <header className="mobile-header-pad" style={{
        position: "sticky", top: 0, zIndex: 40, minHeight: 56,
        background: "rgba(13,15,30,0.88)", backdropFilter: "blur(16px)",
        borderBottom: "1px solid var(--border-glass)",
        padding: "10px clamp(12px, 2.5vw, 28px)", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        margin: "0 calc(-1 * clamp(12px, 2.5vw, 28px))",
      }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--color-text-primary)", letterSpacing: "-0.02em", fontFamily: "var(--font-display)" }}>Ownership Graph</div>
          <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 1 }}>
            {data?.total_tasks ?? 0} tasks · {data?.total_owners ?? 0} owners
          </div>
        </div>
        <div style={{ flex: 1, maxWidth: 300, position: "relative" }}>
          <svg style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", width: 14, height: 14, color: "var(--color-text-tertiary)", pointerEvents: "none" }} viewBox="0 0 16 16" fill="none">
            <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" strokeWidth="1.3"/>
            <path d="M10 10l3.5 3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
          </svg>
          <input
            value={filter} onChange={e => setFilter(e.target.value)}
            placeholder="Filter by person…"
            style={{
              width: "100%", height: 36, padding: "0 14px 0 32px",
              background: "rgba(255,255,255,0.05)", border: "1px solid var(--border-glass)",
              borderRadius: 999, fontFamily: "var(--font-sans)", fontSize: 13,
              color: "var(--color-text-primary)", outline: "none",
            }}
            onFocus={e => { e.target.style.borderColor = "rgba(255,106,82,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(255,106,82,0.12)"; }}
            onBlur={e => { e.target.style.borderColor = "var(--border-glass)"; e.target.style.boxShadow = "none"; }}
          />
        </div>
      </header>

      {/* Summary pills */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
        {[
          { label: "Total Tasks",   value: data?.total_tasks,  color: "#ff6a52" },
          { label: "Owners",        value: data?.total_owners, color: "#c81f30" },
          { label: "In Progress",   value: nodes.reduce((s, n) => s + n.in_progress, 0), color: "#d99a3f" },
          { label: "Completed",     value: nodes.reduce((s, n) => s + n.completed,   0), color: "#3fae7d" },
          { label: "Critical",      value: nodes.reduce((s, n) => s + n.critical,    0), color: "#ff4d5e" },
        ].map(p => (
          <div key={p.label} style={{
            padding: "6px 14px", borderRadius: 999,
            background: "rgba(255,255,255,0.05)", border: "1px solid var(--border-glass)",
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: p.color, flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{p.label}</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--color-text-primary)" }}>{p.value ?? 0}</span>
          </div>
        ))}
      </div>

      {/* Main grid: nodes list + detail panel */}
      <div className="ownership-split-grid" style={{ display: "grid", gridTemplateColumns: selectedNode ? "1fr 360px" : "1fr", gap: 20, alignItems: "start" }}>

        {/* Ownership nodes grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 14 }}>
          {nodes.length === 0 && (
            <div style={{ gridColumn: "1/-1", textAlign: "center", padding: "60px 0", color: "var(--color-text-tertiary)", fontSize: 14 }}>
              No ownership data found.
            </div>
          )}
          {nodes.map(node => {
            const isSelected = selected === node.assignee;
            const completionPct = node.total > 0 ? Math.round((node.completed / node.total) * 100) : 0;
            return (
              <div
                key={node.assignee}
                role="button" tabIndex={0}
                onClick={() => setSelected(isSelected ? null : node.assignee)}
                onKeyDown={e => e.key === "Enter" && setSelected(isSelected ? null : node.assignee)}
                className={`owner-card${isSelected ? " selected" : ""}`}
              >
                {/* Top accent */}
                {isSelected && <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: "linear-gradient(90deg,#ff6a52,#c81f30)", borderRadius: "16px 16px 0 0" }} />}

                {/* Assignee name + initials */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                    background: "linear-gradient(135deg, #ff6a52 0%, #c81f30 100%)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, fontWeight: 700, color: "#f5f0eb",
                    boxShadow: "0 0 12px rgba(255,106,82,0.3)",
                  }}>
                    {node.assignee.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{node.assignee}</div>
                    <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 1 }}>{node.total} task{node.total !== 1 ? "s" : ""}</div>
                  </div>
                </div>

                {/* Status bar */}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <span style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>Completion</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#3fae7d" }}>{completionPct}%</span>
                  </div>
                  <div className="progress-track">
                    <div className="progress-fill" style={{ width: `${completionPct}%`, background: "linear-gradient(90deg,#3fae7d,#d99a3f)", boxShadow: "0 0 8px rgba(63,174,125,0.4)" }} />
                  </div>
                </div>

                {/* Status mini-badges */}
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {[
                    { key: "to_do",       label: "To Do"  },
                    { key: "in_progress", label: "Active" },
                    { key: "completed",   label: "Done"   },
                  ].map(s => node[s.key] > 0 && (
                    <span key={s.key} style={{
                      padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 500,
                      background: `${STATUS_COLOR[s.key]}18`,
                      color: STATUS_COLOR[s.key],
                      border: `1px solid ${STATUS_COLOR[s.key]}40`,
                    }}>
                      {node[s.key]} {s.label}
                    </span>
                  ))}
                  {node.critical > 0 && (
                    <span style={{
                      padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 500,
                      background: "rgba(255,77,94,0.1)", color: "#ff4d5e",
                      border: "1px solid rgba(255,77,94,0.3)",
                    }}>🔴 {node.critical} Critical</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Detail panel */}
        {selectedNode && (
          <div style={{
            background: "rgba(255,255,255,0.04)", backdropFilter: "blur(12px)",
            border: "1px solid var(--border-glass)", borderRadius: 16, padding: "20px",
            position: "sticky", top: 80, maxHeight: "calc(100vh - 120px)", overflowY: "auto",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--color-text-primary)" }}>{selectedNode.assignee}</div>
              <button onClick={() => setSelected(null)} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--color-text-tertiary)", fontSize: 16, lineHeight: 1 }}>✕</button>
            </div>

            {/* Priority breakdown */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-tertiary)", marginBottom: 8 }}>Priority Breakdown</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {["critical", "high", "medium", "low"].map(p => (
                  <div key={p} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: PRIORITY_COLOR[p], flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: "var(--color-text-secondary)", flex: 1, textTransform: "capitalize" }}>{p}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text-primary)" }}>{selectedNode[p]}</span>
                    <div style={{ width: 60, height: 4, borderRadius: 999, background: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${selectedNode.total > 0 ? (selectedNode[p] / selectedNode.total) * 100 : 0}%`, borderRadius: 999, background: PRIORITY_COLOR[p] }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Task list */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-tertiary)", marginBottom: 8 }}>All Tasks</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {selectedNode.tasks.map(task => (
                  <div key={task.id} style={{
                    padding: "10px 12px", borderRadius: 10,
                    background: "rgba(255,255,255,0.04)", border: "1px solid var(--border-glass)",
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: "var(--color-text-primary)", marginBottom: 5, lineHeight: 1.4 }}>{task.title}</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 10, padding: "1px 7px", borderRadius: 999, background: `${STATUS_COLOR[task.status] || "#9a908a"}18`, color: STATUS_COLOR[task.status] || "#9a908a", border: `1px solid ${STATUS_COLOR[task.status] || "#9a908a"}30` }}>
                        {task.status.replace("_", " ")}
                      </span>
                      <span style={{ fontSize: 10, padding: "1px 7px", borderRadius: 999, background: `${PRIORITY_COLOR[task.priority] || "#9a908a"}18`, color: PRIORITY_COLOR[task.priority] || "#9a908a", border: `1px solid ${PRIORITY_COLOR[task.priority] || "#9a908a"}30` }}>
                        {task.priority}
                      </span>
                      <SourceBadge source={task.source} compact />
                      {task.deadline && (
                        <span style={{ fontSize: 10, padding: "1px 7px", borderRadius: 999, background: "rgba(255,255,255,0.05)", color: "var(--color-text-tertiary)", border: "1px solid var(--border-glass)" }}>
                          📅 {formatDeadline(task.deadline, user?.timezone)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

// -- Tasks Page ----------------------------------------------------------------
function TasksPage() {
  const { token, isArchitect, isNavigator, user } = useAuth();
  const API = BASE_URL;

  // Reuse the same useTasks hook that Dashboard uses — already handles auth
  const taskFilters = user?.role === "operator" ? { assignee_id: user.id }
    : user?.role === "navigator"                ? { team_id: user.team_id }
    : {};
  const { tasks, loading, error: taskError, submitting: hookSub, moveTask, removeTask, addTask, reload } = useTasks(taskFilters);

  const [search, setSearch]       = useState("");
  const [statusFilter, setStatus] = useState("all");
  const [priorityFilter, setPri]  = useState("all");
  const [showForm, setShowForm]   = useState(false);
  const [editTask, setEditTask]   = useState(null);
  const [submitting, setSub]      = useState(false);
  const [error, setError]         = useState(null);
  const [successMsg, setSuccess]  = useState(null);

  // Merge hook error with local error
  const displayError = error || taskError;

  // Form state
  const [form, setForm] = useState({ title: "", assignee: "", priority: "medium", deadline: "", description: "" });

  const hdrs = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const load = reload;

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(null), 3000); };

  const openCreate = () => {
    setForm({ title: "", assignee: "", priority: "medium", deadline: "", description: "" });
    setEditTask(null);
    setShowForm(true);
  };

  const openEdit = (t) => {
    setForm({
      title: t.title || t.task_description || "",
      assignee: t.assignee || "",
      priority: t.priority || "medium",
      deadline: t.deadline ? t.deadline.slice(0, 10) : "",
      description: t.task_description || "",
    });
    setEditTask(t);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) return;
    setSub(true);
    try {
      if (editTask) {
        const r = await fetch(`${API}/tasks/${editTask.id}`, {
          method: "PUT",
          headers: hdrs,
          body: JSON.stringify({ title: form.title, assignee: form.assignee, priority: form.priority, deadline: form.deadline || null, task_description: form.description }),
        });
        if (!r.ok) throw new Error("Update failed");
        flash("Task updated ✓");
        reload();
      } else {
        const msg = `${form.priority === "high" ? "URGENT " : ""}create task ${form.assignee ? `@${form.assignee}` : ""} ${form.title}${form.deadline ? ` by ${form.deadline}` : ""}`;
        await addTask(msg);
        flash("Task created ✓");
      }
      setShowForm(false);
      load();
    } catch (e) { setError(e.message); }
    finally { setSub(false); }
  };

  const changeStatus = async (taskId, newStatus) => {
    try {
      await moveTask(taskId, newStatus);
    } catch (e) { setError("Status update failed"); }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await removeTask(taskId);
      flash("Task deleted");
    } catch { setError("Delete failed"); }
  };

  const filtered = useMemo(() => tasks.filter(t => {
    const q = search.toLowerCase();
    const matchQ = !q || (t.title || t.task_description || "").toLowerCase().includes(q) || (t.assignee || "").toLowerCase().includes(q);
    const matchS = statusFilter === "all" || t.status === statusFilter;
    const matchP = priorityFilter === "all" || (t.priority || "medium") === priorityFilter;
    return matchQ && matchS && matchP;
  }), [tasks, search, statusFilter, priorityFilter]);

  const PRIORITY_COLOR = { high: "#ff4d5e", medium: "#d99a3f", low: "#3fae7d" };
  const STATUS_COLOR   = { to_do: "#ff6a52", in_progress: "#ff8a4c", completed: "#3fae7d", cancelled: "#9a908a" };
  const STATUS_LABEL   = { to_do: "To Do", in_progress: "In Progress", completed: "Done", cancelled: "Cancelled" };

  const pill = (color, label) => (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"2px 9px", borderRadius:999, fontSize:11, fontWeight:600, background:`${color}18`, color, border:`1px solid ${color}33` }}>
      <span style={{ width:5, height:5, borderRadius:"50%", background:color }} />{label}
    </span>
  );

  const counts = useMemo(() => ({
    all: tasks.length,
    to_do: tasks.filter(t => t.status === "to_do").length,
    in_progress: tasks.filter(t => t.status === "in_progress").length,
    completed: tasks.filter(t => t.status === "completed").length,
  }), [tasks]);

  const inputStyle = {
    width:"100%", height:38, padding:"0 12px",
    background:"rgba(255,255,255,0.06)", border:"1px solid var(--border-glass)",
    borderRadius:8, fontFamily:"var(--font-sans)", fontSize:13,
    color:"var(--color-text-primary)", outline:"none",
    boxSizing:"border-box",
  };
  const labelStyle = { fontSize:11, fontWeight:600, color:"var(--color-text-secondary)", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:5, display:"block" };

  return (
    <>
      {/* Topbar */}
      <header style={{ position:"sticky", top:0, zIndex:40, minHeight:56, background:"rgba(13,15,30,0.88)", backdropFilter:"blur(16px)", WebkitBackdropFilter:"blur(16px)", borderBottom:"1px solid var(--border-glass)", padding:"10px clamp(12px,2.5vw,28px)", display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }} className="mobile-header-pad">
        <div>
          <div style={{ fontSize:15, fontWeight:700, color:"var(--color-text-primary)", letterSpacing:"-0.02em" }}>Tasks</div>
          <div style={{ fontSize:11, color:"var(--color-text-tertiary)" }}>{counts.all} total · {counts.in_progress} in progress</div>
        </div>

        {/* Search */}
        <div style={{ flex:1, maxWidth:320, position:"relative" }}>
          <svg style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", width:13, height:13, color:"var(--color-text-tertiary)", pointerEvents:"none" }} viewBox="0 0 16 16" fill="none"><circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" strokeWidth="1.3"/><path d="M10 10l3.5 3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tasks or assignees…" style={{ ...inputStyle, paddingLeft:30, borderRadius:999, height:36 }} />
        </div>

        {/* Status filter */}
        <div style={{ display:"flex", gap:3, background:"rgba(255,255,255,0.04)", border:"1px solid var(--border-glass)", borderRadius:999, padding:3 }}>
          {[["all","All"], ["to_do","To Do"], ["in_progress","In Progress"], ["completed","Done"]].map(([val, lbl]) => (
            <button key={val} onClick={() => setStatus(val)} style={{ padding:"4px 12px", borderRadius:999, fontSize:12, fontWeight:600, cursor:"pointer", border:"none", fontFamily:"var(--font-sans)", background:statusFilter===val?"linear-gradient(135deg,#ff6a52,#c81f30)":"transparent", color:statusFilter===val?"#f5f0eb":"var(--color-text-secondary)", transition:"all 0.15s" }}>{lbl}</button>
          ))}
        </div>

        {/* Priority filter */}
        <select value={priorityFilter} onChange={e => setPri(e.target.value)} style={{ height:34, padding:"0 10px", background:"rgba(255,255,255,0.06)", border:"1px solid var(--border-glass)", borderRadius:8, fontFamily:"var(--font-sans)", fontSize:12, color:"var(--color-text-primary)", cursor:"pointer", outline:"none" }}>
          <option value="all" style={{background:"#1a0a08",color:"#efe7df"}}>All Priorities</option>
          <option value="high" style={{background:"#1a0a08",color:"#efe7df"}}>🔴 High</option>
          <option value="medium" style={{background:"#1a0a08",color:"#efe7df"}}>🟡 Medium</option>
          <option value="low" style={{background:"#1a0a08",color:"#efe7df"}}>🟢 Low</option>
        </select>

        <button onClick={openCreate} style={{ height:36, padding:"0 18px", borderRadius:999, border:"none", background:"var(--grad-primary)", color:"#f5f0eb", fontFamily:"var(--font-sans)", fontSize:13, fontWeight:600, cursor:"pointer", whiteSpace:"nowrap", boxShadow:"0 0 20px rgba(255,106,82,0.35)", marginLeft:"auto" }}>
          + New Task
        </button>
      </header>

      <main style={{ flex:1, padding:"clamp(14px, 2vw, 24px) clamp(12px, 2.5vw, 28px) 40px" }}>
        {/* Flash messages */}
        {successMsg && (
          <div style={{ padding:"10px 16px", borderRadius:8, background:"rgba(63,174,125,0.12)", border:"1px solid rgba(63,174,125,0.3)", color:"#3fae7d", fontSize:13, fontWeight:600, marginBottom:16 }}>✓ {successMsg}</div>
        )}
        {displayError && (
          <div style={{ padding:"10px 16px", borderRadius:8, background:"rgba(255,77,94,0.1)", border:"1px solid rgba(255,77,94,0.25)", color:"#ff4d5e", fontSize:13, marginBottom:16, display:"flex", justifyContent:"space-between" }}>
            <span>⚠ {displayError}</span>
            <button onClick={() => setError(null)} style={{ background:"none", border:"none", color:"#ff4d5e", cursor:"pointer", fontSize:16 }}>×</button>
          </div>
        )}

        {/* Summary cards */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(130px,1fr))", gap:14, marginBottom:24 }}>
          {[
            { label:"Total Tasks", value:counts.all, color:"#ff6a52" },
            { label:"To Do", value:counts.to_do, color:"#ff8a4c" },
            { label:"In Progress", value:counts.in_progress, color:"#d99a3f" },
            { label:"Completed", value:counts.completed, color:"#3fae7d" },
          ].map(m => (
            <div key={m.label} style={{ background:"rgba(255,255,255,0.04)", border:"1px solid var(--border-glass)", borderRadius:14, padding:"16px 18px", position:"relative", overflow:"hidden" }}>
              <div style={{ position:"absolute", top:0, left:0, right:0, height:2, background:m.color, opacity:0.7 }} />
              <div style={{ fontSize:11, fontWeight:600, color:"var(--color-text-secondary)", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:6 }}>{m.label}</div>
              <div style={{ fontSize:28, fontWeight:700, color:m.color }}>{loading ? "&mdash;" : m.value}</div>
            </div>
          ))}
        </div>

        {/* Task table */}
        <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid var(--border-glass)", borderRadius:16, overflow:"hidden" }}>
          <div style={{ overflowX:"auto", WebkitOverflowScrolling:"touch" }}>
          {/* Table header */}
          <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 100px 110px 130px 120px", gap:0, padding:"10px 18px", borderBottom:"1px solid var(--border-glass)", background:"rgba(255,255,255,0.03)", minWidth:680 }}>
            {["Task", "Assignee", "Priority", "Status", "Deadline", "Actions"].map(h => (
              <div key={h} style={{ fontSize:11, fontWeight:700, color:"var(--color-text-tertiary)", textTransform:"uppercase", letterSpacing:"0.06em" }}>{h}</div>
            ))}
          </div>

          {loading ? (
            <div style={{ padding:"48px 0", textAlign:"center" }}>
              <div style={{ width:24, height:24, border:"2px solid rgba(255,106,82,0.2)", borderTopColor:"#ff6a52", borderRadius:"50%", animation:"spin 0.7s linear infinite", margin:"0 auto 10px" }} />
              <div style={{ fontSize:13, color:"var(--color-text-tertiary)" }}>Loading tasks…</div>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding:"48px 0", textAlign:"center" }}>
              <div style={{ fontSize:36, marginBottom:10, opacity:0.3 }}>✦</div>
              <div style={{ fontSize:14, color:"var(--color-text-tertiary)" }}>No tasks found</div>
              <div style={{ fontSize:12, color:"var(--color-text-tertiary)", marginTop:4 }}>Try changing filters or create a new task</div>
            </div>
          ) : (
            filtered.map((t, i) => {
              const pri   = t.priority || "medium";
              const st    = t.status || "to_do";
              const title = t.title || t.task_description || "Untitled";
              return (
                <div key={t.id} style={{ display:"grid", gridTemplateColumns:"2fr 1fr 100px 110px 130px 120px", gap:0, padding:"13px 18px", minWidth:680, borderBottom: i < filtered.length-1 ? "1px solid rgba(255,255,255,0.04)" : "none", alignItems:"center", transition:"background 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.03)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  {/* Title */}
                  <div style={{ display:"flex", alignItems:"center", gap:8, minWidth:0, paddingRight:12 }}>
                    <span style={{ fontSize:13, color:"var(--color-text-primary)", fontWeight:500, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", minWidth:0 }} title={title}>{title}</span>
                    <span style={{ flexShrink:0, display:"inline-flex" }}><SourceBadge source={t.source} compact /></span>
                  </div>

                  {/* Assignee */}
                  <div style={{ fontSize:12, color:"var(--color-text-secondary)", display:"flex", alignItems:"center", gap:6 }}>
                    {t.assignee ? (
                      <><span style={{ width:22, height:22, borderRadius:"50%", background:"var(--grad-primary)", display:"inline-flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:700, color:"#f5f0eb", flexShrink:0 }}>{t.assignee[0]?.toUpperCase()}</span>{t.assignee}</>
                    ) : <span style={{ color:"var(--color-text-tertiary)" }}>Unassigned</span>}
                  </div>

                  {/* Priority */}
                  <div>{pill(PRIORITY_COLOR[pri] || "#d99a3f", pri.charAt(0).toUpperCase()+pri.slice(1))}</div>

                  {/* Status */}
                  <div>
                    <select value={st} onChange={e => changeStatus(t.id, e.target.value)}
                      style={{ fontSize:11, fontWeight:600, background:`${STATUS_COLOR[st] || "#ff6a52"}18`, color:STATUS_COLOR[st] || "#ff6a52", border:`1px solid ${STATUS_COLOR[st] || "#ff6a52"}33`, borderRadius:999, padding:"3px 8px", cursor:"pointer", outline:"none", fontFamily:"var(--font-sans)" }}>
                      {Object.entries(STATUS_LABEL).map(([v,l]) => <option key={v} value={v} style={{background:"#1a0a08",color:"#efe7df"}}>{l}</option>)}
                    </select>
                  </div>

                  {/* Deadline */}
                  <div style={{ fontSize:12, color: parseDeadline(t.deadline) && parseDeadline(t.deadline) < new Date() ? "#ff4d5e" : "var(--color-text-secondary)" }}>
                    {t.deadline ? formatDeadline(t.deadline, user?.timezone) : <span style={{ color:"var(--color-text-tertiary)" }}>&mdash;</span>}
                  </div>

                  {/* Actions */}
                  <div style={{ display:"flex", gap:6 }}>
                    {(isArchitect || isNavigator) && (
                      <button onClick={() => openEdit(t)} title="Edit" style={{ width:28, height:28, borderRadius:7, border:"1px solid var(--border-glass)", background:"transparent", color:"var(--color-text-secondary)", cursor:"pointer", fontSize:13, display:"flex", alignItems:"center", justifyContent:"center" }}
                        onMouseEnter={e => { e.currentTarget.style.background="rgba(255,106,82,0.15)"; e.currentTarget.style.color="#ff6a52"; }}
                        onMouseLeave={e => { e.currentTarget.style.background="transparent"; e.currentTarget.style.color="var(--color-text-secondary)"; }}>✎</button>
                    )}
                    {isArchitect && (
                      <button onClick={() => deleteTask(t.id)} title="Delete" style={{ width:28, height:28, borderRadius:7, border:"1px solid var(--border-glass)", background:"transparent", color:"var(--color-text-secondary)", cursor:"pointer", fontSize:14, display:"flex", alignItems:"center", justifyContent:"center" }}
                        onMouseEnter={e => { e.currentTarget.style.background="rgba(255,77,94,0.12)"; e.currentTarget.style.color="#ff4d5e"; }}
                        onMouseLeave={e => { e.currentTarget.style.background="transparent"; e.currentTarget.style.color="var(--color-text-secondary)"; }}>✕</button>
                    )}
                  </div>
                </div>
              );
            })
          )}
          </div>{/* end scroll wrapper */}
        </div>
      </main>

      {/* Create / Edit Modal */}
      {showForm && (
        <div className="modal-backdrop" onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <div className="modal-box" style={{ width: "min(500px, 100%)", padding: "30px 30px 26px" }}>
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <div>
                <div style={{ fontSize: 17, fontWeight: 700, color: "var(--color-text-primary)", letterSpacing: "-0.02em", fontFamily: "var(--font-display)" }}>{editTask ? "Edit Task" : "New Task"}</div>
                <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 2 }}>{editTask ? "Update task details" : "Add to your workflow"}</div>
              </div>
              <button onClick={() => setShowForm(false)} style={{ width: 32, height: 32, borderRadius: "50%", border: "1px solid var(--border-glass)", background: "rgba(255,255,255,0.04)", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={labelStyle}>Task Title *</label>
                <input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="What needs to be done?" className="field-input" style={{ height: 42 }} />
              </div>
              <div>
                <label style={labelStyle}>Assignee</label>
                <input value={form.assignee} onChange={e => setForm(f => ({...f, assignee: e.target.value}))} placeholder="username or name" className="field-input" style={{ height: 42 }} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px,1fr))", gap: 14 }}>
                <div>
                  <label style={labelStyle}>Priority</label>
                  <select value={form.priority} onChange={e => setForm(f => ({...f, priority: e.target.value}))} className="field-input" style={{ height: 42, cursor: "pointer" }}>
                    <option value="high" style={{ background: "#1a0a08", color: "#efe7df" }}>🔴 High</option>
                    <option value="medium" style={{ background: "#1a0a08", color: "#efe7df" }}>🟡 Medium</option>
                    <option value="low" style={{ background: "#1a0a08", color: "#efe7df" }}>🟢 Low</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Deadline</label>
                  <input type="date" value={form.deadline} onChange={e => setForm(f => ({...f, deadline: e.target.value}))} className="field-input" style={{ height: 42, colorScheme: "dark" }} />
                </div>
              </div>
              {editTask && (
                <div>
                  <label style={labelStyle}>Description</label>
                  <textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} rows={3} placeholder="Additional details…" className="field-input" style={{ height: "auto", padding: "11px 13px", resize: "vertical", lineHeight: 1.6 }} />
                </div>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24, paddingTop: 20, borderTop: "1px solid var(--border-glass)" }}>
              <button onClick={() => setShowForm(false)} className="btn-ghost">Cancel</button>
              <button onClick={handleSubmit} disabled={submitting || !form.title.trim()} className="btn-primary" style={{ opacity: (submitting || !form.title.trim()) ? 0.55 : 1 }}>
                {submitting ? "Saving…" : editTask ? "Save Changes" : "✦ Create Task"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// -- Reports Page --------------------------------------------------------------
function ReportsPage() {
  const { user } = useAuth();
  const taskFilters = user?.role === "operator" ? { assignee_id: user.id }
    : user?.role === "navigator"                ? { team_id: user.team_id }
    : {};
  const { tasks, loading } = useTasks(taskFilters);

  const [range, setRange] = useState(30); // days

  const now = new Date();

  const stats = useMemo(() => {
    if (!tasks.length) return null;
    const cutoff = new Date(now - range * 86400000);
    const inRange = tasks.filter(t => new Date(t.created_at) >= cutoff);

    const byStatus = { to_do: 0, in_progress: 0, completed: 0, cancelled: 0 };
    tasks.forEach(t => { if (byStatus[t.status] !== undefined) byStatus[t.status]++; });

    const byPriority = { high: 0, medium: 0, low: 0, critical: 0 };
    tasks.forEach(t => { const p = t.priority || "medium"; if (byPriority[p] !== undefined) byPriority[p]++; });

    // Assignee leaderboard
    const assigneeCounts = {};
    tasks.forEach(t => {
      if (t.assignee) {
        if (!assigneeCounts[t.assignee]) assigneeCounts[t.assignee] = { total: 0, done: 0 };
        assigneeCounts[t.assignee].total++;
        if (t.status === "completed") assigneeCounts[t.assignee].done++;
      }
    });
    const leaderboard = Object.entries(assigneeCounts)
      .map(([name, d]) => ({ name, total: d.total, done: d.done, rate: d.total ? Math.round(d.done / d.total * 100) : 0 }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 8);

    // Daily creation trend (last 14 days)
    const trend = [];
    for (let i = 13; i >= 0; i--) {
      const day = new Date(now - i * 86400000);
      const label = day.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      const count = tasks.filter(t => {
        const d = new Date(t.created_at);
        return d.toDateString() === day.toDateString();
      }).length;
      trend.push({ label, count });
    }

    // Overdue
    const overdue = tasks.filter(t =>
      t.deadline && parseDeadline(t.deadline) < now && t.status !== "completed" && t.status !== "cancelled"
    );

    const completionRate = tasks.length ? Math.round(byStatus.completed / tasks.length * 100) : 0;
    const avgPerDay = range > 0 ? (inRange.length / range).toFixed(1) : 0;

    return { byStatus, byPriority, leaderboard, trend, overdue, completionRate, avgPerDay, inRange: inRange.length };
  }, [tasks, range]);

  const maxTrend = stats ? Math.max(...stats.trend.map(d => d.count), 1) : 1;

  const Card = ({ children, style }) => (
    <div className="pcard" style={{ padding: "22px 24px", ...style }}>{children}</div>
  );

  const SectionTitle = ({ children }) => (
    <div style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text-tertiary)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 14 }}>{children}</div>
  );

  const STATUS_C = { to_do: "#ff6a52", in_progress: "#d99a3f", completed: "#3fae7d", cancelled: "#9a908a" };
  const STATUS_L = { to_do: "To Do", in_progress: "In Progress", completed: "Done", cancelled: "Cancelled" };
  const PRI_C    = { critical: "#ff4d5e", high: "#ff4d5e", medium: "#d99a3f", low: "#3fae7d" };

  return (
    <>
      {/* Topbar */}
      <header className="page-header">
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--color-text-primary)", letterSpacing: "-0.02em", fontFamily: "var(--font-display)" }}>Reports</div>
          <div style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>Workspace analytics & performance</div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 4, background: "rgba(255,255,255,0.04)", border: "1px solid var(--border-glass)", borderRadius: 999, padding: 3 }}>
          {[7, 14, 30, 90].map(d => (
            <button key={d} onClick={() => setRange(d)} style={{ padding: "4px 14px", borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer", border: "none", fontFamily: "var(--font-sans)", background: range === d ? "linear-gradient(135deg,#ff6a52,#c81f30)" : "transparent", color: range === d ? "#f5f0eb" : "var(--color-text-secondary)", transition: "all 0.15s" }}>
              {d}d
            </button>
          ))}
        </div>
      </header>

      <main className="page-enter" style={{ flex: 1, padding: "clamp(14px, 2vw, 24px) clamp(12px, 2.5vw, 28px) 40px", display: "flex", flexDirection: "column", gap: 20 }}>

        {loading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 0" }}>
            <div style={{ width: 28, height: 28, border: "2px solid rgba(255,106,82,0.2)", borderTopColor: "#ff6a52", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
          </div>
        ) : !stats ? (
          <div style={{ textAlign: "center", padding: "80px 0", color: "var(--color-text-tertiary)" }}>
            <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.3 }}>▲</div>
            <div style={{ fontSize: 14 }}>No data yet &mdash; create some tasks to see reports</div>
          </div>
        ) : (
          <>
            {/* KPI row */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px,1fr))", gap: 14 }}>
              {[
                { label: "Total Tasks", value: tasks.length, sub: `${stats.inRange} in last ${range}d`, color: "#ff6a52" },
                { label: "Completed", value: stats.byStatus.completed, sub: `${stats.completionRate}% completion rate`, color: "#3fae7d" },
                { label: "Overdue", value: stats.overdue.length, sub: stats.overdue.length ? "Need attention" : "All on track ✓", color: stats.overdue.length ? "#ff4d5e" : "#3fae7d" },
                { label: "Avg / Day", value: stats.avgPerDay, sub: `Tasks created per day`, color: "#ff8a4c" },
              ].map(m => (
                <TiltPCard key={m.label} style={{ padding: "20px 22px" }}>
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: m.color, opacity: 0.85 }} />
                  <div className="glow-orb" style={{ top: -20, left: -20, width: 80, height: 80, background: m.color, opacity: 0.06 }} />
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-tertiary)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8, fontFamily: "var(--font-display)" }}>{m.label}</div>
                  <div style={{ fontSize: 34, fontWeight: 800, color: m.color, lineHeight: 1, marginBottom: 5, fontFamily: "var(--font-display)", letterSpacing: "-0.03em" }}>{m.value}</div>
                  <div style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>{m.sub}</div>
                </TiltPCard>
              ))}
            </div>

            {/* Charts row */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px,1fr))", gap: 18 }}>

              {/* Status breakdown */}
              <Card>
                <SectionTitle>Task Status Breakdown</SectionTitle>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {Object.entries(STATUS_L).map(([key, label]) => {
                    const count = stats.byStatus[key] || 0;
                    const pct = tasks.length ? Math.round(count / tasks.length * 100) : 0;
                    return (
                      <div key={key}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                          <span style={{ fontSize: 12, color: "var(--color-text-secondary)", display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ width: 8, height: 8, borderRadius: "50%", background: STATUS_C[key], display: "inline-block" }} />{label}
                          </span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: STATUS_C[key] }}>{count} <span style={{ fontWeight: 400, color: "var(--color-text-tertiary)" }}>({pct}%)</span></span>
                        </div>
                        <div className="progress-track" style={{ height: 6 }}>
                          <div className="progress-fill" style={{ width: `${pct}%`, background: STATUS_C[key] }} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Completion ring */}
                <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px solid var(--border-glass)", display: "flex", alignItems: "center", gap: 16 }}>
                  <div style={{ position: "relative", width: 70, height: 70, flexShrink: 0 }}>
                    <svg viewBox="0 0 36 36" style={{ width: 70, height: 70, transform: "rotate(-90deg)" }}>
                      <circle cx="18" cy="18" r="15" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="3" />
                      <circle cx="18" cy="18" r="15" fill="none" stroke="#3fae7d" strokeWidth="3"
                        strokeDasharray={`${stats.completionRate * 0.942} 94.2`}
                        strokeLinecap="round" />
                    </svg>
                    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#3fae7d" }}>
                      {stats.completionRate}%
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)" }}>Completion Rate</div>
                    <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 3 }}>{stats.byStatus.completed} of {tasks.length} tasks done</div>
                  </div>
                </div>
              </Card>

              {/* Priority breakdown */}
              <Card>
                <SectionTitle>Priority Distribution</SectionTitle>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {Object.entries(PRI_C).map(([key, color]) => {
                    const count = stats.byPriority[key] || 0;
                    if (!count) return null;
                    const pct = tasks.length ? Math.round(count / tasks.length * 100) : 0;
                    return (
                      <div key={key}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                          <span style={{ fontSize: 12, color: "var(--color-text-secondary)", display: "flex", alignItems: "center", gap: 6, textTransform: "capitalize" }}>
                            <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, display: "inline-block" }} />{key}
                          </span>
                          <span style={{ fontSize: 12, fontWeight: 700, color }}>{count} <span style={{ fontWeight: 400, color: "var(--color-text-tertiary)" }}>({pct}%)</span></span>
                        </div>
                        <div className="progress-track" style={{ height: 6 }}>
                          <div className="progress-fill" style={{ width: `${pct}%`, background: color }} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Overdue alert */}
                {stats.overdue.length > 0 && (
                  <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px solid var(--border-glass)" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#ff4d5e", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>⚠ Overdue Tasks ({stats.overdue.length})</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 110, overflowY: "auto" }}>
                      {stats.overdue.map(t => (
                        <div key={t.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontSize: 12, color: "var(--color-text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}>{t.title || t.task_description}</span>
                          <span style={{ fontSize: 11, color: "#ff4d5e", flexShrink: 0 }}>{formatDeadline(t.deadline, user?.timezone, { month: "short", day: "numeric", year: undefined })}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            </div>

            {/* Activity trend */}
            <Card>
              <SectionTitle>Task Creation Trend &mdash; Last 14 Days</SectionTitle>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 100 }}>
                {stats.trend.map((d, i) => (
                  <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                    <div style={{ fontSize: 10, color: "var(--color-text-tertiary)", fontWeight: 600 }}>{d.count || ""}</div>
                    <div style={{ width: "100%", borderRadius: "4px 4px 0 0", background: d.count ? "linear-gradient(180deg,#c81f30,#ff6a52)" : "rgba(255,255,255,0.05)", transition: "height 0.5s cubic-bezier(.4,0,.2,1)", height: `${Math.max(4, (d.count / maxTrend) * 80)}px`, minHeight: 4 }}
                      title={`${d.label}: ${d.count} task${d.count !== 1 ? "s" : ""}`}
                    />
                    <div style={{ fontSize: 9, color: "var(--color-text-tertiary)", textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", width: "100%", textOverflow: "ellipsis" }}>{i % 2 === 0 ? d.label : ""}</div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Leaderboard */}
            <Card>
              <SectionTitle>Team Leaderboard &mdash; Tasks by Assignee</SectionTitle>
              {stats.leaderboard.length === 0 ? (
                <div style={{ fontSize: 13, color: "var(--color-text-tertiary)", textAlign: "center", padding: "20px 0" }}>No assignee data yet</div>
              ) : (
                <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 0, minWidth: 420 }}>
                  {stats.leaderboard.map((person, i) => (
                    <div key={person.name} style={{ display: "grid", gridTemplateColumns: "28px 1fr 80px 80px 100px", alignItems: "center", gap: 12, padding: "11px 0", borderBottom: i < stats.leaderboard.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none" }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: i === 0 ? "#d99a3f" : i === 1 ? "#9a908a" : i === 2 ? "#8a5419" : "var(--color-text-tertiary)", textAlign: "center" }}>#{i + 1}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ width: 28, height: 28, borderRadius: "50%", background: `hsl(${(person.name.charCodeAt(0) * 37) % 360},60%,50%)`, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#f5f0eb", flexShrink: 0 }}>{person.name[0]?.toUpperCase()}</span>
                        <span style={{ fontSize: 13, color: "var(--color-text-primary)", fontWeight: 500 }}>{person.name}</span>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#ff6a52", textAlign: "center" }}>{person.total} <span style={{ fontSize: 10, fontWeight: 400, color: "var(--color-text-tertiary)" }}>tasks</span></div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#3fae7d", textAlign: "center" }}>{person.done} <span style={{ fontSize: 10, fontWeight: 400, color: "var(--color-text-tertiary)" }}>done</span></div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ flex: 1, height: 5, background: "rgba(255,255,255,0.06)", borderRadius: 999, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${person.rate}%`, background: person.rate >= 75 ? "#3fae7d" : person.rate >= 40 ? "#d99a3f" : "#ff4d5e", borderRadius: 999 }} />
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", minWidth: 32 }}>{person.rate}%</span>
                      </div>
                    </div>
                  ))}
                </div>
                </div>
              )}
            </Card>
          </>
        )}
      </main>
    </>
  );
}


// -- Compliance Page -----------------------------------------------------------
function CompliancePage() {
  const { user } = useAuth();
  const taskFilters = user?.role === "operator" ? { assignee_id: user.id }
    : user?.role === "navigator"                ? { team_id: user.team_id }
    : {};
  const { tasks, loading } = useTasks(taskFilters);

  const [activeTab, setActiveTab] = useState("overdue");

  const now = new Date();

  const compliance = useMemo(() => {
    if (!tasks.length) return null;

    // Overdue tasks (past deadline, not done/cancelled)
    const overdue = tasks.filter(t =>
      t.deadline && parseDeadline(t.deadline) < now &&
      t.status !== "completed" && t.status !== "cancelled"
    ).sort((a, b) => parseDeadline(a.deadline) - parseDeadline(b.deadline));

    // Unassigned tasks
    const unassigned = tasks.filter(t =>
      !t.assignee && t.status !== "completed" && t.status !== "cancelled"
    );

    // Stale tasks — in_progress for more than 7 days
    const stale = tasks.filter(t => {
      if (t.status !== "in_progress") return false;
      const updated = new Date(t.updated_at || t.created_at);
      return (now - updated) > 7 * 86400000;
    });

    // No-deadline tasks (active, no deadline set)
    const noDeadline = tasks.filter(t =>
      !t.deadline && t.status !== "completed" && t.status !== "cancelled"
    );

    // High priority not started
    const highNotStarted = tasks.filter(t =>
      (t.priority === "high" || t.priority === "critical") && t.status === "to_do"
    );

    // Compliance score (0-100)
    const issues = overdue.length + unassigned.length + stale.length + highNotStarted.length;
    const score = Math.max(0, Math.round(100 - (issues / Math.max(tasks.length, 1)) * 100));

    // Audit log — recent task activity (simulate from task data)
    const recent = [...tasks]
      .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
      .slice(0, 20);

    return { overdue, unassigned, stale, noDeadline, highNotStarted, score, issues, recent };
  }, [tasks]);

  const TABS = [
    { key: "overdue",        label: "Overdue",          badge: compliance?.overdue.length },
    { key: "unassigned",     label: "Unassigned",        badge: compliance?.unassigned.length },
    { key: "stale",          label: "Stale (7d+)",       badge: compliance?.stale.length },
    { key: "high_priority",  label: "High Not Started",  badge: compliance?.highNotStarted.length },
    { key: "audit",          label: "Audit Log",         badge: null },
  ];

  const currentList =
    activeTab === "overdue"       ? compliance?.overdue :
    activeTab === "unassigned"    ? compliance?.unassigned :
    activeTab === "stale"         ? compliance?.stale :
    activeTab === "high_priority" ? compliance?.highNotStarted :
    compliance?.recent;

  const STATUS_C = { to_do: "#ff6a52", in_progress: "#d99a3f", completed: "#3fae7d", cancelled: "#9a908a" };
  const STATUS_L = { to_do: "To Do", in_progress: "In Progress", completed: "Done", cancelled: "Cancelled" };
  const PRI_C    = { critical: "#ff4d5e", high: "#ff4d5e", medium: "#d99a3f", low: "#3fae7d" };

  const scoreColor = !compliance ? "#ff6a52"
    : compliance.score >= 80 ? "#3fae7d"
    : compliance.score >= 50 ? "#d99a3f"
    : "#ff4d5e";

  const daysSince = (dateStr) => {
    const d = dateStr ? Math.floor((now - new Date(dateStr)) / 86400000) : null;
    if (d === null) return "—";
    if (d === 0) return "Today";
    if (d === 1) return "Yesterday";
    return `${d}d ago`;
  };

  const daysOverdue = (dateStr) => {
    if (!dateStr) return "";
    const parsed = parseDeadline(dateStr);
    if (!parsed) return "";
    const d = Math.floor((now - parsed) / 86400000);
    return d > 0 ? `${d}d overdue` : "Due today";
  };

  return (
    <>
      {/* Topbar */}
      <header className="page-header">
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--color-text-primary)", letterSpacing: "-0.02em", fontFamily: "var(--font-display)" }}>Compliance</div>
          <div style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>Task health monitoring & audit trail</div>
        </div>
        {compliance && (
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>Compliance Score</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: scoreColor, letterSpacing: "-0.03em" }}>{compliance.score}<span style={{ fontSize: 13, fontWeight: 500 }}>%</span></div>
            <div style={{ width: 80, height: 6, background: "rgba(255,255,255,0.06)", borderRadius: 999, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${compliance.score}%`, background: scoreColor, borderRadius: 999, transition: "width 0.6s ease" }} />
            </div>
          </div>
        )}
      </header>

      <main className="page-enter" style={{ flex: 1, padding: "clamp(14px, 2vw, 24px) clamp(12px, 2.5vw, 28px) 40px", display: "flex", flexDirection: "column", gap: 20 }}>

        {loading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 0" }}>
            <div style={{ width: 28, height: 28, border: "2px solid rgba(255,106,82,0.2)", borderTopColor: "#ff6a52", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
          </div>
        ) : !compliance ? (
          <div style={{ textAlign: "center", padding: "80px 0", color: "var(--color-text-tertiary)" }}>
            <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.3 }}>◈</div>
            <div>No tasks to analyse yet</div>
          </div>
        ) : (
          <>
            {/* Score cards */}
            <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px,1fr))", gap: 12 }}>
              {[
                { label: "Overdue",          value: compliance.overdue.length,          color: compliance.overdue.length ? "#ff4d5e" : "#3fae7d",  icon: "⏰" },
                { label: "Unassigned",       value: compliance.unassigned.length,       color: compliance.unassigned.length ? "#d99a3f" : "#3fae7d", icon: "👤" },
                { label: "Stale (7d+)",      value: compliance.stale.length,            color: compliance.stale.length ? "#d99a3f" : "#3fae7d",     icon: "💤" },
                { label: "High Not Started", value: compliance.highNotStarted.length,   color: compliance.highNotStarted.length ? "#ff4d5e" : "#3fae7d", icon: "🔴" },
                { label: "No Deadline",      value: compliance.noDeadline.length,       color: compliance.noDeadline.length > 3 ? "#d99a3f" : "#3fae7d", icon: "📅" },
              ].map(m => (
                <TiltPCard key={m.label} style={{ padding: "18px 16px 16px" }}>
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: m.color, opacity: 0.8 }} />
                  <div className="glow-orb" style={{ top: -20, left: -20, width: 80, height: 80, background: m.color, opacity: 0.06 }} />
                  <div style={{ fontSize: 20, marginBottom: 8 }}>{m.icon}</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: m.color, lineHeight: 1, marginBottom: 5, fontFamily: "var(--font-display)" }}>{m.value}</div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: "var(--color-text-tertiary)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{m.label}</div>
                </TiltPCard>
              ))}
            </div>
            </div>

            {/* Compliance score bar */}
            <div className="section-card" style={{ display: "flex", alignItems: "center", gap: 24 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-tertiary)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 }}>Overall Health</div>
                <div style={{ fontSize: 32, fontWeight: 800, color: scoreColor, fontFamily: "var(--font-display)", letterSpacing: "-0.03em" }}>{compliance.score}<span style={{ fontSize: 16, fontWeight: 500 }}>%</span></div>
              </div>
              <div style={{ flex: 1 }}>
                <div className="progress-track" style={{ height: 8 }}>
                  <div className="progress-fill" style={{ width: `${compliance.score}%`, background: `linear-gradient(90deg, ${scoreColor}, ${scoreColor}bb)`, boxShadow: `0 0 10px ${scoreColor}55` }} />
                </div>
              </div>
              <div style={{ padding: "6px 14px", borderRadius: 999, background: scoreColor + "18", border: `1px solid ${scoreColor}33`, fontSize: 12, color: scoreColor, fontWeight: 600 }}>
                {compliance.issues === 0 ? "✅ All healthy" : `${compliance.issues} issue${compliance.issues !== 1 ? "s" : ""}`}
              </div>
            </div>

            {/* Tabs */}
            <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid var(--border-glass)", borderRadius: 16, overflow: "hidden" }}>
              {/* Tab bar */}
              <div style={{ display: "flex", borderBottom: "1px solid var(--border-glass)", background: "rgba(255,255,255,0.02)" }}>
                {TABS.map(tab => (
                  <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{ flex: 1, padding: "12px 8px", fontSize: 12, fontWeight: 600, cursor: "pointer", border: "none", fontFamily: "var(--font-sans)", background: activeTab === tab.key ? "rgba(255,106,82,0.12)" : "transparent", color: activeTab === tab.key ? "#ff6a52" : "var(--color-text-secondary)", borderBottom: activeTab === tab.key ? "2px solid #ff6a52" : "2px solid transparent", transition: "all 0.15s", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                    {tab.label}
                    {tab.badge !== null && tab.badge !== undefined && (
                      <span style={{ minWidth: 18, height: 18, borderRadius: 999, background: tab.badge > 0 ? (tab.key === "audit" ? "#ff6a52" : "#ff4d5e") : "rgba(255,255,255,0.1)", color: "#f5f0eb", fontSize: 10, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "0 5px" }}>{tab.badge}</span>
                    )}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              <div style={{ minHeight: 200, overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                {/* Table header */}
                {activeTab !== "audit" && (
                  <div style={{ display: "grid", gridTemplateColumns: activeTab === "overdue" ? "2fr 1fr 100px 110px 120px" : "2fr 1fr 100px 110px", gap: 0, padding: "9px 18px", borderBottom: "1px solid rgba(255,255,255,0.04)", background: "rgba(255,255,255,0.02)", minWidth: activeTab === "overdue" ? 620 : 520 }}>
                    {["Task", "Assignee", "Priority", "Status", activeTab === "overdue" ? "Overdue" : null].filter(Boolean).map(h => (
                      <div key={h} style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-tertiary)", textTransform: "uppercase", letterSpacing: "0.07em" }}>{h}</div>
                    ))}
                  </div>
                )}

                {!currentList || currentList.length === 0 ? (
                  <div style={{ padding: "40px 0", textAlign: "center" }}>
                    <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.3 }}>✓</div>
                    <div style={{ fontSize: 13, color: "var(--color-text-tertiary)" }}>
                      {activeTab === "audit" ? "No recent activity" : "No issues found — looking good!"}
                    </div>
                  </div>
                ) : activeTab === "audit" ? (
                  <div>
                    {currentList.map((t, i) => (
                      <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "11px 18px", borderBottom: i < currentList.length - 1 ? "1px solid rgba(255,255,255,0.03)" : "none" }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.02)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: STATUS_C[t.status] || "#ff6a52", flexShrink: 0 }} />
                        <div style={{ flex: 1, fontSize: 13, color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.title || t.task_description || "Untitled"}</div>
                        <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", flexShrink: 0 }}>{t.assignee || "Unassigned"}</div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: STATUS_C[t.status], flexShrink: 0, minWidth: 70, textAlign: "right" }}>{STATUS_L[t.status]}</div>
                        <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", flexShrink: 0, minWidth: 70, textAlign: "right" }}>{daysSince(t.updated_at || t.created_at)}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div>
                    {currentList.map((t, i) => {
                      const pri = t.priority || "medium";
                      const st  = t.status || "to_do";
                      const title = t.title || t.task_description || "Untitled";
                      return (
                        <div key={t.id} style={{ display: "grid", gridTemplateColumns: activeTab === "overdue" ? "2fr 1fr 100px 110px 120px" : "2fr 1fr 100px 110px", gap: 0, padding: "12px 18px", minWidth: activeTab === "overdue" ? 620 : 520, borderBottom: i < currentList.length - 1 ? "1px solid rgba(255,255,255,0.03)" : "none", alignItems: "center" }}
                          onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.02)"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <div style={{ fontSize: 13, color: "var(--color-text-primary)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: 12 }} title={title}>{title}</div>
                          <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{t.assignee || <span style={{ color: "var(--color-text-tertiary)" }}>&mdash;</span>}</div>
                          <div>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600, background: `${PRI_C[pri] || "#d99a3f"}18`, color: PRI_C[pri] || "#d99a3f", border: `1px solid ${PRI_C[pri] || "#d99a3f"}33`, textTransform: "capitalize" }}>{pri}</span>
                          </div>
                          <div>
                            <span style={{ fontSize: 11, fontWeight: 600, color: STATUS_C[st], background: `${STATUS_C[st]}18`, border: `1px solid ${STATUS_C[st]}33`, borderRadius: 999, padding: "2px 8px" }}>{STATUS_L[st]}</span>
                          </div>
                          {activeTab === "overdue" && (
                            <div style={{ fontSize: 12, fontWeight: 600, color: "#ff4d5e" }}>{daysOverdue(t.deadline)}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </main>
    </>
  );
}


// -- Knowledge Page ------------------------------------------------------------
function KnowledgePage() {
  const { token, user, isArchitect, isNavigator } = useAuth();

  const [activeTab, setActiveTab]   = useState("guide");   // "guide" | "notes"
  const [notes, setNotes]           = useState([]);
  const [search, setSearch]         = useState("");
  const [category, setCategory]     = useState("all");
  const [showForm, setShowForm]     = useState(false);
  const [editNote, setEditNote]     = useState(null);
  const [viewNote, setViewNote]     = useState(null);
  const [submitting, setSub]        = useState(false);
  const [successMsg, setSuccess]    = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);
  const [form, setForm]             = useState({ title: "", body: "", category: "general", pinned: false });

  const STORAGE_KEY = `kb_notes_${user?.workspace?.id || "default"}`;

  const SAMPLE_NOTES = [
    { id: 1, title: "How to create tasks via Slack", body: "Mention the bot with: @bot create task @assignee task title by YYYY-MM-DD\n\nPriority keywords: URGENT, HIGH PRIORITY, LOW PRIORITY\n\nThe bot will auto-assign based on @mention.", category: "guide", pinned: true, author: "System", created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
    { id: 2, title: "Role permissions overview", body: "Architect — Full access: create, edit, delete tasks, manage workspace, view all reports.\n\nNavigator — Team lead: create and edit tasks for their team, view team reports.\n\nOperator — Team member: view and update status of assigned tasks only.", category: "policy", pinned: true, author: "System", created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
    { id: 3, title: "API integration guide", body: "Use the API page to generate an API key.\n\nEndpoints:\nPOST /api/v1/tasks — create a task\nGET /api/v1/tasks — list tasks\nPUT /api/v1/tasks/{id} — update a task\n\nPass your key in: X-API-Key: your_key_here", category: "technical", pinned: false, author: "System", created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
  ];

  const loadNotes = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      setNotes(stored ? JSON.parse(stored) : SAMPLE_NOTES);
    } catch { setNotes(SAMPLE_NOTES); }
  }, [STORAGE_KEY]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  const saveNotes = (updated) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    setNotes(updated);
  };

  const CATEGORIES = [
    { value: "all",       label: "All Notes" },
    { value: "general",   label: "General" },
    { value: "guide",     label: "Guides" },
    { value: "policy",    label: "Policies" },
    { value: "technical", label: "Technical" },
    { value: "meeting",   label: "Meeting Notes" },
  ];

  const CAT_COLOR = {
    general:   "#ff6a52",
    guide:     "#3fae7d",
    policy:    "#ff8a4c",
    technical: "#d99a3f",
    meeting:   "#ff4d5e",
  };

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(null), 3000); };

  const handleSave = () => {
    if (!form.title.trim()) return;
    setSub(true);
    try {
      if (editNote) {
        const updated = notes.map(n => n.id === editNote.id
          ? { ...n, ...form, updated_at: new Date().toISOString() }
          : n
        );
        saveNotes(updated);
        flash("Note updated ✓");
      } else {
        const newNote = {
          id: Date.now(),
          ...form,
          author: user?.name || "You",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        saveNotes([newNote, ...notes]);
        flash("Note created ✓");
      }
      setShowForm(false);
      setEditNote(null);
    } finally { setSub(false); }
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this note?")) return;
    saveNotes(notes.filter(n => n.id !== id));
    if (viewNote?.id === id) setViewNote(null);
    flash("Note deleted");
  };

  const handlePin = (id) => {
    saveNotes(notes.map(n => n.id === id ? { ...n, pinned: !n.pinned } : n));
  };

  const openEdit = (note) => {
    setForm({ title: note.title, body: note.body, category: note.category, pinned: note.pinned });
    setEditNote(note);
    setShowForm(true);
  };

  const openCreate = () => {
    setForm({ title: "", body: "", category: "general", pinned: false });
    setEditNote(null);
    setShowForm(true);
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return notes
      .filter(n => {
        const matchQ = !q || n.title.toLowerCase().includes(q) || n.body.toLowerCase().includes(q);
        const matchC = category === "all" || n.category === category;
        return matchQ && matchC;
      })
      .sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return new Date(b.updated_at) - new Date(a.updated_at);
      });
  }, [notes, search, category]);

  const catCounts = useMemo(() => {
    const counts = {};
    notes.forEach(n => { counts[n.category] = (counts[n.category] || 0) + 1; });
    return counts;
  }, [notes]);

  const formatDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  const inputStyle = { width: "100%", padding: "9px 12px", background: "rgba(255,255,255,0.05)", border: "1px solid var(--border-glass)", borderRadius: 8, fontFamily: "var(--font-sans)", fontSize: 13, color: "var(--color-text-primary)", outline: "none", boxSizing: "border-box" };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5, display: "block" };

  // ── Software Guide sections ────────────────────────────────────────────────
  const GUIDE_SECTIONS = [
    {
      id: "overview",
      icon: "🧠",
      title: "What is AI Workflow Coordinator?",
      color: "#ff6a52",
      body: `AI Workflow Coordinator is a smart task management platform built for teams. It combines role-based dashboards, AI-powered task extraction, Slack integration, and a compliance engine — all in one place.\n\nWhether you're a solo operator or managing a full workspace with multiple teams, the platform adapts to your role so you always see exactly what's relevant to you.`,
    },
    {
      id: "roles",
      icon: "👥",
      title: "Roles & Permissions",
      color: "#ff8a4c",
      body: `🏛️ Architect (Owner/Manager)\nFull access to all tasks, all team members, workspace settings, billing, and reports. Can create, edit, and delete anything. Manages invite codes and API keys.\n\n🧭 Navigator (Team Lead)\nSees all tasks assigned to their team. Can create and edit tasks. Has access to team reports and compliance.\n\n⚙️ Operator (Team Member)\nSees only tasks assigned to them. Can update task status and add comments.\n\n🚀 Solo\nPrivate task board with no team visibility. Perfect for freelancers or personal use.`,
    },
    {
      id: "tasks",
      icon: "✅",
      title: "Managing Tasks",
      color: "#3fae7d",
      body: `Creating Tasks\n• Click "+ Add Task" on the Dashboard or Tasks page\n• Fill in title, assignee, priority, deadline, and category\n• AI can auto-extract tasks from plain text descriptions\n\nTask Statuses\n• To Do → In Progress → In Review → Completed\n• Cancelled is available for dropped tasks\n\nKanban Board\nDrag and drop tasks between columns on the Tasks page to update their status visually.\n\nPriorities\n• Critical — urgent blockers\n• High — important, should be done soon\n• Medium — normal priority\n• Low — do when time allows`,
    },
    {
      id: "slack",
      icon: "💬",
      title: "Slack Integration",
      color: "#d99a3f",
      body: `Connect your workspace to Slack to manage tasks without leaving your chat.\n\nSetup\n1. Go to Settings → Integrations\n2. Click "Add to Slack" and authorize the bot\n3. Invite the bot to your channel: /invite @AIWorkflow\n\nSlack Commands\n• @bot create task @john Fix the login bug by 2025-06-01\n• @bot list tasks — shows open tasks\n• @bot status [task id] done — marks a task complete\n\nThe bot understands priority keywords automatically:\n• URGENT or CRITICAL → critical priority\n• HIGH PRIORITY → high\n• LOW PRIORITY → low`,
    },
    {
      id: "compliance",
      icon: "🛡️",
      title: "Compliance & Reporting",
      color: "#ff4d5e",
      body: `The Compliance page flags tasks that need attention:\n\n• Overdue — past their deadline and not completed\n• Unassigned — no owner set\n• Stale — not updated in 7+ days\n• High priority & not started — critical/high tasks still in To Do\n\nReports\nThe Reports page shows completed tasks, team performance, and output over time. Architects see workspace-wide data; Navigators see their team's data.\n\nUse compliance weekly to keep your team on track and catch bottlenecks early.`,
    },
    {
      id: "api",
      icon: "🔌",
      title: "API & Integrations",
      color: "#3fae7d",
      body: `Generate an API Key\n1. Go to the API page in the sidebar\n2. Click "Generate API Key"\n3. Copy and store it securely — it won't be shown again\n\nKey Endpoints\nGET  /api/v1/tasks          — list tasks\nPOST /api/v1/tasks          — create a task\nPUT  /api/v1/tasks/{id}     — update a task\nDELETE /api/v1/tasks/{id}   — delete a task\n\nAuthentication\nSend your key in every request header:\nX-API-Key: your_key_here\n\nPagination\nUse ?skip=0&limit=50 to paginate large task lists.`,
    },
    {
      id: "tips",
      icon: "💡",
      title: "Tips & Best Practices",
      color: "#ff6a52",
      body: `🔔 Stay on top of work\n• Check the Compliance page weekly to catch overdue or unassigned tasks\n• Pin critical notes in the Knowledge Base so your team sees them first\n\n📋 Keep tasks clean\n• Always set a deadline and assignee when creating tasks\n• Use categories to group related work\n• Archive completed sprints using bulk status updates\n\n🤝 Team collaboration\n• Navigators: use the Team Notes tab to document processes your team follows\n• Architects: share the workspace invite code via Settings so new members can join\n• Use Slack integration to reduce context-switching`,
    },
  ];

  return (
    <>
      {/* Topbar */}
      <header className="page-header">
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--color-text-primary)", letterSpacing: "-0.02em", fontFamily: "var(--font-display)" }}>Knowledge Panel</div>
          <div style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>Software guides · team notes · documentation</div>
        </div>

        {/* Tab toggle */}
        <div style={{ display: "flex", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 10, padding: 3, gap: 3 }}>
          {[
            { id: "guide", label: "📖 Software Guide" },
            { id: "notes", label: "📝 Team Notes" },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                height: 32, padding: "0 16px", borderRadius: 8, border: "none",
                fontFamily: "var(--font-sans)", fontSize: 12, fontWeight: 600,
                cursor: "pointer", transition: "all 0.18s",
                background: activeTab === tab.id ? "linear-gradient(135deg, #ff6a52 0%, #c81f30 100%)" : "transparent",
                color: activeTab === tab.id ? "#f5f0eb" : "var(--color-text-muted)",
                boxShadow: activeTab === tab.id ? "0 0 16px rgba(255,106,82,0.3)" : "none",
              }}
            >{tab.label}</button>
          ))}
        </div>

        {/* Notes tab extras */}
        {activeTab === "notes" && (
          <>
            <div style={{ flex: 1, maxWidth: 280, position: "relative" }}>
              <svg style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 13, height: 13, color: "var(--color-text-tertiary)", pointerEvents: "none" }} viewBox="0 0 16 16" fill="none"><circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" strokeWidth="1.3"/><path d="M10 10l3.5 3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search notes…" style={{ ...inputStyle, paddingLeft: 30, borderRadius: 999, padding: "0 14px 0 30px", height: 36 }} />
            </div>
            <select value={category} onChange={e => setCategory(e.target.value)} style={{ height: 34, padding: "0 10px", background: "rgba(255,255,255,0.05)", border: "1px solid var(--border-glass)", borderRadius: 8, fontFamily: "var(--font-sans)", fontSize: 12, color: "var(--color-text-primary)", cursor: "pointer", outline: "none" }}>
              {CATEGORIES.map(c => (
                <option key={c.value} value={c.value} style={{ background: "#1a0a08", color: "#efe7df" }}>
                  {c.label} {c.value !== "all" && catCounts[c.value] ? `(${catCounts[c.value]})` : ""}
                </option>
              ))}
            </select>
            <button onClick={openCreate} style={{ height: 36, padding: "0 18px", borderRadius: 999, border: "none", background: "var(--grad-primary)", color: "#f5f0eb", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", boxShadow: "0 0 24px rgba(255,106,82,0.35), inset 0 1px 0 rgba(255,255,255,0.12)", marginLeft: "auto" }}>
              + New Note
            </button>
          </>
        )}
      </header>

      <main style={{ flex: 1, padding: "clamp(14px, 2vw, 24px) clamp(12px, 2.5vw, 28px) 40px", display: "flex", gap: 20 }}>

        {/* ── SOFTWARE GUIDE TAB ── */}
        {activeTab === "guide" && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12 }}>

            {/* Intro banner */}
            <div style={{
              padding: "20px 24px", borderRadius: 16,
              background: "linear-gradient(135deg, rgba(255,106,82,0.1) 0%, rgba(200,31,48,0.08) 100%)",
              border: "1px solid rgba(255,106,82,0.25)",
              display: "flex", alignItems: "center", gap: 18,
            }}>
              <div style={{ fontSize: 36, flexShrink: 0 }}>📚</div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "var(--color-text-primary)", marginBottom: 4, fontFamily: "var(--font-display)" }}>
                  Everything you need to know about AI Workflow Coordinator
                </div>
                <div style={{ fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.6 }}>
                  Click any section below to expand it. Use the <strong style={{ color: "#ffb199" }}>Team Notes</strong> tab to add your own notes for your team.
                </div>
              </div>
            </div>

            {/* Accordion sections */}
            {GUIDE_SECTIONS.map((section) => {
              const isOpen = expandedSection === section.id;
              return (
                <div
                  key={section.id}
                  style={{
                    borderRadius: 14,
                    border: `1px solid ${isOpen ? section.color + "40" : "rgba(255,255,255,0.07)"}`,
                    background: isOpen ? `${section.color}08` : "rgba(255,255,255,0.025)",
                    overflow: "hidden",
                    transition: "border-color 0.2s, background 0.2s",
                  }}
                >
                  {/* Section header — always visible */}
                  <button
                    onClick={() => setExpandedSection(isOpen ? null : section.id)}
                    style={{
                      width: "100%", padding: "16px 20px",
                      display: "flex", alignItems: "center", gap: 14,
                      background: "transparent", border: "none", cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <div style={{
                      width: 42, height: 42, borderRadius: 12, flexShrink: 0,
                      background: `${section.color}15`, border: `1px solid ${section.color}30`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 20,
                    }}>{section.icon}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--color-text-primary)", letterSpacing: "-0.01em" }}>{section.title}</div>
                    </div>
                    <div style={{
                      width: 24, height: 24, borderRadius: "50%",
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: "rgba(255,255,255,0.04)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 11, color: "var(--color-text-muted)",
                      transition: "transform 0.2s",
                      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                      flexShrink: 0,
                    }}>▼</div>
                  </button>

                  {/* Expanded body */}
                  {isOpen && (
                    <div style={{
                      padding: "0 20px 20px 76px",
                      fontSize: 13, color: "var(--color-text-secondary)",
                      lineHeight: 1.85, whiteSpace: "pre-wrap",
                      borderTop: `1px solid ${section.color}18`,
                      paddingTop: 16,
                      animation: "fadeUp 0.25s ease both",
                    }}>
                      {section.body}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── TEAM NOTES TAB ── */}
        {activeTab === "notes" && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 16 }}>
            {successMsg && (
              <div style={{ padding: "10px 16px", borderRadius: 8, background: "rgba(63,174,125,0.12)", border: "1px solid rgba(63,174,125,0.3)", color: "#3fae7d", fontSize: 13, fontWeight: 600 }}>✓ {successMsg}</div>
            )}

            {filtered.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 0", color: "var(--color-text-tertiary)" }}>
                <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.3 }}>◉</div>
                <div style={{ fontSize: 14, marginBottom: 6 }}>No notes found</div>
                <div style={{ fontSize: 12 }}>Try a different search or create a new note</div>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14, alignContent: "start" }}>
                {filtered.map(note => (
                  <TiltPCard key={note.id}
                    onClick={() => setViewNote(note)}
                    tiltMax={4}
                    style={{ padding: "18px 20px", cursor: "pointer",
                      border: `1px solid ${viewNote?.id === note.id ? "rgba(255,106,82,0.5)" : "var(--border-glass)"}`,
                      background: viewNote?.id === note.id ? "rgba(255,106,82,0.06)" : "rgba(255,255,255,0.032)",
                    }}>
                    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2.5, background: CAT_COLOR[note.category] || "#ff6a52" }} />
                    <div className="glow-orb" style={{ top: -20, right: -20, width: 80, height: 80, background: CAT_COLOR[note.category] || "#ff6a52", opacity: 0.05 }} />
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--color-text-primary)", lineHeight: 1.35, flex: 1 }}>{note.title}</div>
                      {note.pinned && <span title="Pinned" style={{ fontSize: 13, flexShrink: 0 }}>📌</span>}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--color-text-secondary)", lineHeight: 1.6, marginBottom: 14, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical" }}>
                      {note.body}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span className="badge" style={{ background: `${CAT_COLOR[note.category] || "#ff6a52"}18`, color: CAT_COLOR[note.category] || "#ff6a52", borderColor: `${CAT_COLOR[note.category] || "#ff6a52"}33`, textTransform: "capitalize" }}>{note.category}</span>
                      <span style={{ fontSize: 10, color: "var(--color-text-tertiary)" }}>{formatDate(note.updated_at)}</span>
                    </div>
                  </TiltPCard>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Right — note viewer (notes tab only) */}
        {activeTab === "notes" && viewNote && (
          <div style={{ width: "clamp(280px, 30vw, 380px)", flexShrink: 0, background: "linear-gradient(160deg, rgba(20,22,46,0.95) 0%, rgba(14,17,36,0.98) 100%)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "var(--radius-xl)", padding: "24px 24px 20px", height: "fit-content", position: "sticky", top: 80, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: CAT_COLOR[viewNote.category] || "#ff6a52", borderRadius: "16px 16px 0 0" }} />
            <div className="glow-orb" style={{ top: -30, right: -30, width: 120, height: 120, background: CAT_COLOR[viewNote.category] || "#ff6a52", opacity: 0.06 }} />

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, gap: 8 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: "var(--color-text-primary)", lineHeight: 1.3, flex: 1 }}>{viewNote.title}</div>
              <button onClick={() => setViewNote(null)} style={{ width: 28, height: 28, borderRadius: "50%", border: "1px solid var(--border-glass)", background: "rgba(255,255,255,0.04)", color: "var(--color-text-tertiary)", cursor: "pointer", fontSize: 14, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
            </div>

            <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap", alignItems: "center" }}>
              <span className="badge" style={{ background: `${CAT_COLOR[viewNote.category] || "#ff6a52"}18`, color: CAT_COLOR[viewNote.category] || "#ff6a52", borderColor: `${CAT_COLOR[viewNote.category] || "#ff6a52"}33`, textTransform: "capitalize" }}>{viewNote.category}</span>
              <span style={{ fontSize: 10, color: "var(--color-text-tertiary)" }}>by {viewNote.author}</span>
              <span style={{ fontSize: 10, color: "var(--color-text-tertiary)" }}>· {formatDate(viewNote.updated_at)}</span>
            </div>

            <div style={{ fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.75, whiteSpace: "pre-wrap", marginBottom: 20, maxHeight: 380, overflowY: "auto", paddingRight: 4 }}>{viewNote.body}</div>

            <div style={{ display: "flex", gap: 8, borderTop: "1px solid var(--border-glass)", paddingTop: 16 }}>
              <button onClick={() => handlePin(viewNote.id)} className="btn-ghost" style={{ flex: 1, height: 34, fontSize: 12, background: viewNote.pinned ? "rgba(255,106,82,0.12)" : undefined, color: viewNote.pinned ? "#ff6a52" : undefined, borderColor: viewNote.pinned ? "rgba(255,106,82,0.3)" : undefined }}>
                {viewNote.pinned ? "📌 Pinned" : "📌 Pin"}
              </button>
              {(isArchitect || isNavigator) && (
                <button onClick={() => { openEdit(viewNote); setViewNote(null); }} className="btn-ghost" style={{ flex: 1, height: 34, fontSize: 12 }}>Edit</button>
              )}
              {isArchitect && (
                <button onClick={() => handleDelete(viewNote.id)} style={{ width: 34, height: 34, borderRadius: 9, border: "1px solid rgba(255,77,94,0.3)", background: "rgba(255,77,94,0.06)", color: "#ff4d5e", cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Create / Edit Note Modal */}
      {showForm && (
        <div className="modal-backdrop" onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <div className="modal-box" style={{ width: "min(540px, 100%)", padding: "30px 30px 26px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <div>
                <div style={{ fontSize: 17, fontWeight: 700, color: "var(--color-text-primary)", letterSpacing: "-0.02em", fontFamily: "var(--font-display)" }}>{editNote ? "Edit Note" : "New Team Note"}</div>
                <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 2 }}>Knowledge base entry</div>
              </div>
              <button onClick={() => setShowForm(false)} style={{ width: 32, height: 32, borderRadius: "50%", border: "1px solid var(--border-glass)", background: "rgba(255,255,255,0.04)", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={labelStyle}>Title *</label>
                <input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="Note title…" className="field-input" style={{ height: 42 }} />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "end" }}>
                <div>
                  <label style={labelStyle}>Category</label>
                  <select value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} className="field-input" style={{ height: 42, cursor: "pointer" }}>
                    {CATEGORIES.filter(c => c.value !== "all").map(c => (
                      <option key={c.value} value={c.value} style={{ background: "#1a0a08", color: "#efe7df" }}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ ...labelStyle, marginBottom: 8 }}>Pin</label>
                  <div onClick={() => setForm(f => ({...f, pinned: !f.pinned}))} style={{ width: 46, height: 26, borderRadius: 999, background: form.pinned ? "var(--accent-blue)" : "rgba(255,255,255,0.1)", cursor: "pointer", position: "relative", transition: "background 0.2s", boxShadow: form.pinned ? "0 0 12px rgba(255,106,82,0.4)" : "none" }}>
                    <div style={{ position: "absolute", top: 3, left: form.pinned ? 23 : 3, width: 20, height: 20, borderRadius: "50%", background: "#f5f0eb", transition: "left 0.2s", boxShadow: "0 1px 4px rgba(0,0,0,0.3)" }} />
                  </div>
                </div>
              </div>

              <div>
                <label style={labelStyle}>Content</label>
                <textarea value={form.body} onChange={e => setForm(f => ({...f, body: e.target.value}))} rows={8} placeholder="Write your note here… supports plain text and line breaks." className="field-input" style={{ height: "auto", padding: "11px 13px", resize: "vertical", lineHeight: 1.7 }} />
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24, paddingTop: 20, borderTop: "1px solid var(--border-glass)" }}>
              <button onClick={() => setShowForm(false)} className="btn-ghost">Cancel</button>
              <button onClick={handleSave} disabled={submitting || !form.title.trim()} className="btn-primary" style={{ opacity: !form.title.trim() ? 0.5 : 1 }}>
                {submitting ? "Saving…" : editNote ? "Save Changes" : "✦ Create Note"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}



// -- Settings Page -------------------------------------------------------------
function SettingsPage() {
  const { token, user, isArchitect, logout } = useAuth();
  const API = BASE_URL;
  const hdrs = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [activeTab, setActiveTab]   = useState("profile");
  const [successMsg, setSuccess]    = useState(null);
  const [errorMsg, setError]        = useState(null);
  const [saving, setSaving]         = useState(false);

  // Profile form
  const [profile, setProfile] = useState({
    name:     user?.name     || "",
    email:    user?.email    || "",
    username: user?.username || "",
    timezone: user?.timezone || "UTC",
  });

  // Password form
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [showPw, setShowPw] = useState({ current: false, next: false, confirm: false });

  // Workspace form (Architect only)
  const [workspace, setWorkspace] = useState({
    name:        user?.workspace?.name        || "",
    invite_code: user?.workspace?.invite_code || "",
    plan:        user?.workspace?.plan        || "free",
  });

  // Notification preferences (stored locally)
  const NOTIF_KEY = `notif_prefs_${user?.id || "u"}`;
  const [notif, setNotif] = useState(() => {
    try { return JSON.parse(localStorage.getItem(NOTIF_KEY)) || { email_overdue: true, email_daily: true, slack_mentions: true, browser_push: false }; }
    catch { return { email_overdue: true, email_daily: true, slack_mentions: true, browser_push: false }; }
  });

  // Billing state
  const [billingStatus, setBillingStatus] = useState(null);   // null=not loaded
  const [billingLoading, setBillingLoading] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);  // show confirm modal
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelDone, setCancelDone] = useState(false);

  // Load billing status when billing tab is opened
  const loadBillingStatus = async () => {
    if (billingStatus) return; // already loaded
    setBillingLoading(true);
    try {
      const r = await fetch(`${API}/auth/billing-status`, { headers: hdrs });
      const d = await r.json();
      setBillingStatus(d);
    } catch (e) {
      setBillingStatus({ status: "unknown", error: true });
    } finally {
      setBillingLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      const r = await fetch(`${API}/billing/cancel`, { method: "POST", headers: hdrs });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not cancel. Please try again.");
      setCancelConfirm(false);
      setCancelDone(true);
      setBillingStatus(prev => ({ ...prev, status: "cancelled" }));
      flash("Subscription cancelled. Access continues until end of billing period. Note: creating a new account will require management approval.");
    } catch (e) {
      setCancelConfirm(false);
      flash(e.message, true);
    } finally {
      setCancelLoading(false);
    }
  };

  const goToPortal = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/billing/portal`, { headers: hdrs });
      const d = await r.json();
      if (d.portal_url) window.open(d.portal_url, "_blank");
      else flash("Portal unavailable. Please contact support.", true);
    } catch (e) {
      flash("Could not open billing portal.", true);
    } finally {
      setSaving(false);
    }
  };

  const flash = (msg, isErr) => {
    if (isErr) { setError(msg); setTimeout(() => setError(null), 4000); }
    else       { setSuccess(msg); setTimeout(() => setSuccess(null), 3000); }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/users/me`, { method: "PUT", headers: hdrs, body: JSON.stringify(profile) });
      if (!r.ok) throw new Error("Update failed");
      flash("Profile saved ✓");
    } catch (e) { flash(e.message, true); }
    finally { setSaving(false); }
  };

  const savePassword = async () => {
    if (!pwForm.current || !pwForm.next) return flash("Fill all fields", true);
    if (pwForm.next !== pwForm.confirm)  return flash("Passwords don't match", true);
    if (pwForm.next.length < 8)          return flash("Password must be 8+ characters", true);
    setSaving(true);
    try {
      const r = await fetch(`${API}/users/me/password`, { method: "PUT", headers: hdrs, body: JSON.stringify({ current_password: pwForm.current, new_password: pwForm.next }) });
      if (!r.ok) throw new Error((await r.json()).detail || "Failed");
      flash("Password updated ✓");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (e) { flash(e.message, true); }
    finally { setSaving(false); }
  };

  const saveWorkspace = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/workspaces/me`, { method: "PUT", headers: hdrs, body: JSON.stringify({ name: workspace.name }) });
      if (!r.ok) throw new Error("Update failed");
      flash("Workspace updated ✓");
    } catch (e) { flash(e.message, true); }
    finally { setSaving(false); }
  };

  const saveNotif = () => {
    localStorage.setItem(NOTIF_KEY, JSON.stringify(notif));
    flash("Preferences saved ✓");
  };

  const TABS = [
    { key: "profile",       label: "Profile",        icon: "👤" },
    { key: "security",      label: "Security",        icon: "🔒" },
    { key: "workspace",     label: "Workspace",       icon: "🏢" },
    { key: "notifications", label: "Notifications",   icon: "🔔" },
    { key: "billing",       label: "Billing",         icon: "💳" },
    { key: "danger",        label: "Danger Zone",     icon: "⚠️" },
  ];

  const inputStyle = { width: "100%", height: 40, padding: "0 12px", background: "rgba(255,255,255,0.05)", border: "1px solid var(--border-glass)", borderRadius: 8, fontFamily: "var(--font-sans)", fontSize: 13, color: "var(--color-text-primary)", outline: "none", boxSizing: "border-box", transition: "border-color 0.15s, box-shadow 0.15s" };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5, display: "block" };
  const sectionStyle = { background: "rgba(255,255,255,0.03)", border: "1px solid var(--border-glass)", borderRadius: "var(--radius-lg)", padding: "22px 24px", marginBottom: 16 };
  const sectionTitle = { fontSize: 13, fontWeight: 700, color: "var(--color-text-primary)", marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid var(--border-glass)", letterSpacing: "-0.01em", fontFamily: "var(--font-display)" };

  const Toggle = ({ value, onChange, label, sub }) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
      <div>
        <div style={{ fontSize: 13, color: "var(--color-text-primary)", fontWeight: 500 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 2 }}>{sub}</div>}
      </div>
      <div onClick={onChange} style={{ width: 44, height: 24, borderRadius: 999, background: value ? "#ff6a52" : "rgba(255,255,255,0.1)", cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
        <div style={{ position: "absolute", top: 2, left: value ? 22 : 2, width: 20, height: 20, borderRadius: "50%", background: "#f5f0eb", transition: "left 0.2s", boxShadow: "0 1px 4px rgba(0,0,0,0.3)" }} />
      </div>
    </div>
  );

  const ROLE_BADGES = { architect: { label: "Architect", color: "#d99a3f" }, navigator: { label: "Navigator", color: "#ff6a52" }, operator: { label: "Operator", color: "#3fae7d" } };
  const role = ROLE_BADGES[user?.role] || ROLE_BADGES.operator;

  const TIMEZONES = ["UTC","America/New_York","America/Chicago","America/Denver","America/Los_Angeles","America/Sao_Paulo","Europe/London","Europe/Paris","Europe/Berlin","Europe/Moscow","Asia/Dubai","Asia/Karachi","Asia/Kolkata","Asia/Dhaka","Asia/Bangkok","Asia/Singapore","Asia/Tokyo","Asia/Shanghai","Australia/Sydney","Pacific/Auckland"];

  return (
    <>
      {/* Topbar */}
      <header className="page-header">
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--color-text-primary)", letterSpacing: "-0.02em", fontFamily: "var(--font-display)" }}>Settings</div>
          <div style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>Account, workspace & preferences</div>
        </div>

        {/* Role badge */}
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 11, fontWeight: 700, padding: "4px 12px", borderRadius: 999, background: `${role.color}18`, color: role.color, border: `1px solid ${role.color}33` }}>{role.label}</span>
        </div>
      </header>

      <main className="page-enter" style={{ flex: 1, padding: "clamp(14px, 2vw, 24px) clamp(12px, 2.5vw, 28px) 40px", display: "flex", gap: 20, maxWidth: 900, flexWrap: "wrap" }}>

        {/* Left sidebar tabs */}
        <div style={{ width: "clamp(120px, 20vw, 180px)", flexShrink: 0, display: "flex", flexDirection: "column", gap: 3 }}>
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => { setActiveTab(tab.key); if (tab.key === "billing") loadBillingStatus(); }}
              style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: "none", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: activeTab === tab.key ? 600 : 400, cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 10, transition: "all 0.15s",
                background: activeTab === tab.key ? "rgba(255,106,82,0.14)" : "transparent",
                color: activeTab === tab.key ? "#efe7df" : "var(--color-text-secondary)",
                borderLeft: `3px solid ${activeTab === tab.key ? "#ff6a52" : "transparent"}`,
                boxShadow: activeTab === tab.key ? "inset 0 0 0 1px rgba(255,106,82,0.2)" : "none",
              }}>
              <span style={{ fontSize: 15 }}>{tab.icon}</span>{tab.label}
            </button>
          ))}
        </div>

        {/* Right content */}
        <div style={{ flex: 1 }}>
          {/* Flash messages */}
          {successMsg && <div style={{ padding: "10px 16px", borderRadius: 8, background: "rgba(63,174,125,0.12)", border: "1px solid rgba(63,174,125,0.3)", color: "#3fae7d", fontSize: 13, fontWeight: 600, marginBottom: 16 }}>✓ {successMsg}</div>}
          {errorMsg   && <div style={{ padding: "10px 16px", borderRadius: 8, background: "rgba(255,77,94,0.1)", border: "1px solid rgba(255,77,94,0.25)", color: "#ff4d5e", fontSize: 13, marginBottom: 16 }}>⚠ {errorMsg}</div>}

          {/* -- PROFILE -- */}
          {activeTab === "profile" && (
            <>
              <div style={sectionStyle}>
                <div style={sectionTitle}>Personal Information</div>

                {/* Avatar */}
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 22 }}>
                  <div style={{ width: 64, height: 64, borderRadius: "50%", background: "var(--grad-primary)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, fontWeight: 700, color: "#f5f0eb", flexShrink: 0 }}>
                    {(profile.name || profile.username || "?")[0]?.toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "var(--color-text-primary)" }}>{profile.name || profile.username}</div>
                    <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginTop: 2 }}>{profile.email}</div>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: `${role.color}18`, color: role.color, border: `1px solid ${role.color}33`, marginTop: 6, display: "inline-block" }}>{role.label}</span>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px,1fr))", gap: 14 }}>
                  <div>
                    <label style={labelStyle}>Full Name</label>
                    <input value={profile.name} onChange={e => setProfile(p => ({...p, name: e.target.value}))} placeholder="Your full name" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Username</label>
                    <input value={profile.username} onChange={e => setProfile(p => ({...p, username: e.target.value}))} placeholder="@username" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Email</label>
                    <input value={profile.email} onChange={e => setProfile(p => ({...p, email: e.target.value}))} placeholder="email@example.com" style={inputStyle} type="email" />
                  </div>
                  <div>
                    <label style={labelStyle}>Timezone</label>
                    <select value={profile.timezone} onChange={e => setProfile(p => ({...p, timezone: e.target.value}))} style={{ ...inputStyle, cursor: "pointer" }}>
                      {TIMEZONES.map(tz => <option key={tz} value={tz} style={{ background: "#1a0a08", color: "#efe7df" }}>{tz}</option>)}
                    </select>
                  </div>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
                  <button onClick={saveProfile} disabled={saving} style={{ height: 38, padding: "0 22px", borderRadius: 8, border: "none", background: "var(--grad-primary)", color: "#f5f0eb", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: saving ? 0.7 : 1 }}>
                    {saving ? "Saving…" : "Save Profile"}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* -- SECURITY -- */}
          {activeTab === "security" && (
            <div style={sectionStyle}>
              <div style={sectionTitle}>Change Password</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {[
                  { key: "current", label: "Current Password" },
                  { key: "next",    label: "New Password" },
                  { key: "confirm", label: "Confirm New Password" },
                ].map(f => (
                  <div key={f.key}>
                    <label style={labelStyle}>{f.label}</label>
                    <div style={{ position: "relative" }}>
                      <input
                        type={showPw[f.key] ? "text" : "password"}
                        value={pwForm[f.key]}
                        onChange={e => setPwForm(p => ({...p, [f.key]: e.target.value}))}
                        placeholder="••••••••"
                        style={{ ...inputStyle, paddingRight: 40 }}
                      />
                      <button onClick={() => setShowPw(p => ({...p, [f.key]: !p[f.key]}))}
                        style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--color-text-tertiary)", cursor: "pointer", fontSize: 14 }}>
                        {showPw[f.key] ? "🙈" : "👁"}
                      </button>
                    </div>
                  </div>
                ))}

                {/* Password strength */}
                {pwForm.next && (
                  <div>
                    <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginBottom: 5 }}>Password strength</div>
                    <div style={{ height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 999, overflow: "hidden" }}>
                      <div style={{ height: "100%", borderRadius: 999, transition: "width 0.3s, background 0.3s",
                        width: pwForm.next.length < 6 ? "25%" : pwForm.next.length < 10 ? "60%" : "100%",
                        background: pwForm.next.length < 6 ? "#ff4d5e" : pwForm.next.length < 10 ? "#d99a3f" : "#3fae7d",
                      }} />
                    </div>
                  </div>
                )}

                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
                  <button onClick={savePassword} disabled={saving} style={{ height: 38, padding: "0 22px", borderRadius: 8, border: "none", background: "var(--grad-primary)", color: "#f5f0eb", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: saving ? 0.7 : 1 }}>
                    {saving ? "Updating…" : "Update Password"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* -- WORKSPACE -- */}
          {activeTab === "workspace" && (
            <>
              <div style={sectionStyle}>
                <div style={sectionTitle}>Workspace Settings</div>
                {!isArchitect && (
                  <div style={{ padding: "12px 14px", borderRadius: 8, background: "rgba(217,154,63,0.08)", border: "1px solid rgba(217,154,63,0.2)", color: "#d99a3f", fontSize: 12, marginBottom: 16 }}>
                    ⚠ Only Architects can edit workspace settings
                  </div>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div>
                    <label style={labelStyle}>Workspace Name</label>
                    <input value={workspace.name} onChange={e => setWorkspace(w => ({...w, name: e.target.value}))} disabled={!isArchitect} placeholder="Your workspace name" style={{ ...inputStyle, opacity: isArchitect ? 1 : 0.5 }} />
                  </div>
                  <div>
                    <label style={labelStyle}>Invite Code</label>
                    <div style={{ display: "flex", gap: 8 }}>
                      <input value={workspace.invite_code} readOnly style={{ ...inputStyle, opacity: 0.7, fontFamily: "monospace", letterSpacing: "0.1em" }} />
                      <button onClick={() => { navigator.clipboard.writeText(workspace.invite_code); flash("Invite code copied!"); }}
                        style={{ height: 40, padding: "0 14px", borderRadius: 8, border: "1px solid var(--border-glass)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12, fontFamily: "var(--font-sans)", whiteSpace: "nowrap" }}>
                        Copy
                      </button>
                    </div>
                    <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 5 }}>Share this code with teammates to join your workspace</div>
                  </div>
                  <div>
                    <label style={labelStyle}>Plan</label>
                    <div style={{ display: "flex", gap: 10 }}>
                      {["free", "pro", "team"].map(p => (
                        <div key={p} style={{ flex: 1, padding: "14px 16px", borderRadius: 10, border: `1px solid ${workspace.plan === p ? "#ff6a52" : "var(--border-glass)"}`, background: workspace.plan === p ? "rgba(255,106,82,0.1)" : "rgba(255,255,255,0.03)", cursor: "default" }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: workspace.plan === p ? "#ff6a52" : "var(--color-text-secondary)", textTransform: "capitalize", marginBottom: 4 }}>{p === "team" ? "Team" : p.charAt(0).toUpperCase()+p.slice(1)}</div>
                          <div style={{ fontSize: 10, color: "var(--color-text-tertiary)" }}>
                            {p === "free" ? "Up to 3 users" : p === "pro" ? "$19/mo · Unlimited users" : "$49/mo · Multi-workspace"}
                          </div>
                          {workspace.plan === p && <div style={{ fontSize: 10, fontWeight: 700, color: "#3fae7d", marginTop: 4 }}>✓ Current plan</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                {isArchitect && (
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
                    <button onClick={saveWorkspace} disabled={saving} style={{ height: 38, padding: "0 22px", borderRadius: 8, border: "none", background: "var(--grad-primary)", color: "#f5f0eb", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: saving ? 0.7 : 1 }}>
                      {saving ? "Saving…" : "Save Workspace"}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          {/* -- NOTIFICATIONS -- */}
          {activeTab === "notifications" && (
            <div style={sectionStyle}>
              <div style={sectionTitle}>Notification Preferences</div>
              <Toggle value={notif.email_overdue} onChange={() => setNotif(n => ({...n, email_overdue: !n.email_overdue}))} label="Email — Overdue tasks" sub="Get notified when tasks pass their deadline" />
              <Toggle value={notif.email_daily} onChange={() => setNotif(n => ({...n, email_daily: !n.email_daily}))} label="Email — Daily rollup" sub="Morning summary of tasks due today" />
              <Toggle value={notif.slack_mentions} onChange={() => setNotif(n => ({...n, slack_mentions: !n.slack_mentions}))} label="Slack mentions" sub="Receive pings when you're assigned a task" />
              <Toggle value={notif.browser_push} onChange={() => setNotif(n => ({...n, browser_push: !n.browser_push}))} label="Browser push notifications" sub="Real-time alerts in your browser" />
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
                <button onClick={saveNotif} style={{ height: 38, padding: "0 22px", borderRadius: 8, border: "none", background: "var(--grad-primary)", color: "#f5f0eb", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  Save Preferences
                </button>
              </div>
            </div>
          )}

          {/* -- BILLING -- */}
          {activeTab === "billing" && (
            <>
              {/* Cancel confirmation modal */}
              {cancelConfirm && (
                <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
                  <div style={{ background: "linear-gradient(160deg,#150806,#100604)", border: "1px solid rgba(255,77,94,0.3)", borderRadius: 20, padding: "32px 36px", maxWidth: 420, width: "100%", boxShadow: "0 24px 80px rgba(0,0,0,0.7)" }}>
                    <div style={{ width: 52, height: 52, borderRadius: 14, background: "rgba(255,77,94,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, marginBottom: 18 }}>⚠️</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "#efe7df", marginBottom: 10, fontFamily: "var(--font-display)" }}>Cancel your subscription?</div>
                    <div style={{ fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.7, marginBottom: 16 }}>
                      Your access will continue until the end of the current billing period. After that, your account will be locked.
                    </div>
                    <div style={{ padding: "12px 14px", borderRadius: 10, background: "rgba(255,77,94,0.08)", border: "1px solid rgba(255,77,94,0.25)", marginBottom: 24 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#ff4d5e", marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                        <span>🔒</span> Important — Account Access
                      </div>
                      <div style={{ fontSize: 12, color: "rgba(255,77,94,0.85)", lineHeight: 1.65 }}>
                        After cancellation, <strong style={{ color: "#ff4d5e" }}>you will not be able to create a new account</strong> on this platform until management reviews and approves your request. Please contact your workspace administrator if you need access reinstated.
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 10 }}>
                      <button onClick={() => setCancelConfirm(false)} disabled={cancelLoading}
                        style={{ flex: 1, height: 42, borderRadius: 10, border: "1px solid var(--border-glass)", background: "transparent", color: "var(--color-text-secondary)", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                        Keep Subscription
                      </button>
                      <button onClick={handleCancelSubscription} disabled={cancelLoading}
                        style={{ flex: 1, height: 42, borderRadius: 10, border: "none", background: cancelLoading ? "rgba(255,77,94,0.4)" : "#ff4d5e", color: "#f5f0eb", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 700, cursor: cancelLoading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                        {cancelLoading
                          ? <><span style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#f5f0eb", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} /> Cancelling…</>
                          : "Yes, Cancel"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid var(--border-glass)", borderRadius: "var(--radius-lg)", padding: "22px 24px", marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--color-text-primary)", marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid var(--border-glass)", letterSpacing: "-0.01em", fontFamily: "var(--font-display)" }}>
                  Subscription & Billing
                </div>

                {billingLoading && (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--color-text-tertiary)", fontSize: 13, padding: "20px 0" }}>
                    <span style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.15)", borderTopColor: "#ff6a52", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
                    Loading billing status…
                  </div>
                )}

                {!billingLoading && billingStatus && (() => {
                  const STATUS_INFO = {
                    active:    { label: "Active",    color: "#3fae7d", bg: "rgba(63,174,125,0.1)",  border: "rgba(63,174,125,0.25)",  icon: "✅" },
                    trialing:  { label: "Trial",     color: "#d99a3f", bg: "rgba(217,154,63,0.1)",  border: "rgba(217,154,63,0.25)",  icon: "⏱" },
                    past_due:  { label: "Past Due",  color: "#ff4d5e", bg: "rgba(255,77,94,0.1)", border: "rgba(255,77,94,0.25)", icon: "⚠️" },
                    cancelled: { label: "Cancelled", color: "#9a908a", bg: "rgba(156,163,175,0.1)", border: "rgba(156,163,175,0.2)",  icon: "✕" },
                    exempt:    { label: "Admin",     color: "#ff8a4c", bg: "rgba(167,139,250,0.1)", border: "rgba(167,139,250,0.25)", icon: "🛡" },
                    unknown:   { label: "Unknown",   color: "#9a908a", bg: "rgba(156,163,175,0.1)", border: "rgba(156,163,175,0.2)",  icon: "?" },
                  };
                  const st = billingStatus.status || "unknown";
                  const info = STATUS_INFO[st] || STATUS_INFO.unknown;
                  const isActive = st === "active";
                  const isTrialing = st === "trialing";
                  const isCancelled = st === "cancelled" || cancelDone;
                  const canCancel = (isActive || isTrialing) && !cancelDone;

                  return (
                    <>
                      {/* Status badge */}
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderRadius: 12, background: info.bg, border: `1px solid ${info.border}`, marginBottom: 20 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ fontSize: 20 }}>{info.icon}</span>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: info.color }}>Subscription {info.label}</div>
                            {billingStatus.days_left !== undefined && isTrialing && (
                              <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 2 }}>{billingStatus.days_left} days left in trial</div>
                            )}
                            {isCancelled && !cancelDone && (
                              <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 2 }}>Your access has ended. Resubscribe below.</div>
                            )}
                            {cancelDone && (
                              <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 2 }}>Cancellation confirmed. Access continues until end of billing period.</div>
                            )}
                          </div>
                        </div>
                        <div style={{ fontSize: 11, padding: "4px 12px", borderRadius: 999, background: info.border, color: info.color, fontWeight: 700, border: `1px solid ${info.border}` }}>{info.label}</div>
                      </div>

                      {/* Post-cancellation approval notice */}
                      {cancelDone && (
                        <div style={{ padding: "16px 18px", borderRadius: 12, background: "rgba(255,77,94,0.07)", border: "1px solid rgba(255,77,94,0.22)", marginBottom: 16 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#ff4d5e", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                            <span>🔒</span> Account Access After Cancellation
                          </div>
                          <div style={{ fontSize: 12, color: "rgba(255,77,94,0.8)", lineHeight: 1.7 }}>
                            Your current access will remain active until the end of the billing period. Once it expires, <strong style={{ color: "#ff4d5e" }}>you will not be able to create a new account</strong> on this platform without management approval. To request reinstatement, please contact your workspace administrator.
                          </div>
                        </div>
                      )}

                      {/* ── Re-enter card banner (migrated trial users only) ── */}
                      {isTrialing && !billingStatus.card_on_file && (
                        <div style={{
                          padding: "18px 20px",
                          borderRadius: 14,
                          background: "linear-gradient(135deg, rgba(200,31,48,0.1), rgba(255,106,82,0.08))",
                          border: "1px solid rgba(200,31,48,0.35)",
                          boxShadow: "0 0 24px rgba(200,31,48,0.1), inset 0 1px 0 rgba(255,255,255,0.04)",
                          marginBottom: 16,
                        }}>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                            <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(200,31,48,0.15)", border: "1px solid rgba(200,31,48,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>💳</div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: "#ffb199", marginBottom: 4 }}>Action required — Add your payment details</div>
                              <div style={{ fontSize: 12, color: "var(--color-text-secondary)", lineHeight: 1.65, marginBottom: 14 }}>
                                We recently migrated our payment system. Your trial and all your data are intact, but we need your card details to continue your subscription after the trial ends. No charge until your trial expires.
                              </div>
                              <button
                                onClick={async () => {
                                  try {
                                    const r = await fetch(`${API}/billing/checkout`, { method: "POST", headers: hdrs });
                                    const d = await r.json();
                                    if (d.checkout_url) window.location.href = d.checkout_url;
                                    else flash("Could not open checkout. Please try again.", true);
                                  } catch { flash("Could not open checkout. Please try again.", true); }
                                }}
                                style={{
                                  height: 38, padding: "0 20px", borderRadius: 9,
                                  border: "none",
                                  background: "linear-gradient(135deg,#7a1420,#ff6a52)",
                                  color: "#f5f0eb", fontFamily: "var(--font-sans)", fontSize: 13,
                                  fontWeight: 700, cursor: "pointer",
                                  boxShadow: "0 4px 14px rgba(124,58,237,0.4)",
                                  transition: "opacity 0.15s",
                                }}
                                onMouseOver={e => e.currentTarget.style.opacity = "0.88"}
                                onMouseOut={e => e.currentTarget.style.opacity = "1"}
                              >
                                Add Payment Details →
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Action buttons */}
                      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)" }}>Manage payment & invoices</div>
                            <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 2 }}>Update card, view billing history via Polar portal</div>
                          </div>
                          <button onClick={goToPortal} disabled={saving} style={{ height: 36, padding: "0 18px", borderRadius: 8, border: "1px solid var(--border-glass)", background: "transparent", color: "var(--color-text-secondary)", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
                            {saving ? "Opening…" : "Open Portal →"}
                          </button>
                        </div>

                        {canCancel && (
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0" }}>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: "#ff4d5e" }}>Cancel subscription</div>
                              <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 2 }}>You'll keep access until the end of the current billing period</div>
                            </div>
                            <button onClick={() => setCancelConfirm(true)}
                              style={{ height: 36, padding: "0 18px", borderRadius: 8, border: "1px solid rgba(255,77,94,0.4)", background: "rgba(255,77,94,0.08)", color: "#ff4d5e", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
                              Cancel Plan
                            </button>
                          </div>
                        )}

                        {isCancelled && (
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0" }}>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)" }}>Reactivate subscription</div>
                              <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 2 }}>Restore access by subscribing again</div>
                            </div>
                            <button onClick={async () => {
                              const r = await fetch(`${API}/billing/checkout`, { method: "POST", headers: hdrs });
                              const d = await r.json();
                              if (d.checkout_url) window.location.href = d.checkout_url;
                            }} style={{ height: 36, padding: "0 18px", borderRadius: 8, border: "none", background: "linear-gradient(135deg,#ff6a52,#c81f30)", color: "#f5f0eb", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
                              Resubscribe →
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            </>
          )}

          {/* -- DANGER ZONE -- */}
          {activeTab === "danger" && (
            <div style={{ ...sectionStyle, border: "1px solid rgba(255,77,94,0.25)", background: "rgba(255,77,94,0.04)" }}>
              <div style={{ ...sectionTitle, color: "#ff4d5e", borderBottomColor: "rgba(255,77,94,0.2)" }}>⚠ Danger Zone</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0", borderBottom: "1px solid rgba(255,77,94,0.1)" }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)" }}>Sign out</div>
                    <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 2 }}>Sign out of your account on this device</div>
                  </div>
                  <button onClick={logout} style={{ height: 36, padding: "0 18px", borderRadius: 8, border: "1px solid rgba(255,77,94,0.4)", background: "transparent", color: "#ff4d5e", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                    Sign Out
                  </button>
                </div>
                {isArchitect && (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0" }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)" }}>Delete workspace</div>
                      <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 2 }}>Permanently delete workspace and all data. This cannot be undone.</div>
                    </div>
                    <button onClick={() => flash("Contact support to delete your workspace", true)} style={{ height: 36, padding: "0 18px", borderRadius: 8, border: "1px solid rgba(255,77,94,0.4)", background: "rgba(255,77,94,0.08)", color: "#ff4d5e", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                      Delete Workspace
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
}

function PlaceholderPage({ label }) {
  return (
    <main style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 40, marginBottom: 16, opacity: 0.3 }}>🚧</div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: "var(--color-text-primary)", marginBottom: 8 }}>{label}</h2>
        <p style={{ fontSize: 13, color: "var(--color-text-tertiary)" }}>This section is coming soon.</p>
      </div>
    </main>
  );
}

// -- Dashboard — now role-aware ------------------------------------------------
// -----------------------------------------------------------------------------
// INSTRUCTIONS: Replace lines 3070–3318 in App.jsx (the entire Dashboard function)
// with the code below. Nothing else in App.jsx needs to change.
// -----------------------------------------------------------------------------

function Dashboard({ tasks, total, loading, error, submitting, moveTask, removeTask, addTask, reload, clearError }) {
  const { user, isArchitect, isNavigator, token } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter]       = useState("");
  const [activeTab, setActiveTab] = useState(0);  // 0 = Team, 1 = Individual

  // -- Fetch individual tasks (/tasks/my) separately ------------------------
  const [myTasks, setMyTasks]         = useState([]);
  const [myLoading, setMyLoading]     = useState(true);
  const [myError, setMyError]         = useState(null);

  const fetchMyTasks = useCallback(async (silent = false) => {
    if (!token) return;
    if (!silent) setMyLoading(true);
    setMyError(null);
    try {
      const res = await fetch(`${BASE_URL}/tasks/my?limit=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to load individual tasks");
      const data = await res.json();
      setMyTasks(data.tasks || []);
    } catch (e) {
      if (!silent) setMyError(e.message);
    } finally {
      if (!silent) setMyLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchMyTasks();
    // Silent background poll — mirrors the 15s poll useTasks() already does
    // for the Team list, so tasks created via Slack or email (which land
    // straight in the DB, with no push notification to the frontend) show
    // up here too without the user having to hit Refresh.
    const id = setInterval(() => fetchMyTasks(true), 15_000);
    return () => clearInterval(id);
  }, [fetchMyTasks]);

  // -- Role-aware labels -----------------------------------------------------
  const roleGreeting = isArchitect
    ? "Architect View — All workspace tasks"
    : isNavigator
      ? `Navigator View — ${user?.team_name || "Your team"} tasks`
      : user?.role === "solo"
        ? "Solo Dashboard — Your personal tasks"
        : "Operator View — Tasks assigned to you";

  // -- Source tasks depend on active tab -------------------------------------
  // Team tab  → `tasks` prop (role-scoped, passed from AuthenticatedApp)
  // Individual tab → `myTasks` (fetched from /tasks/my)
  const isIndividualTab = activeTab === 1;
  const sourceTasks     = isIndividualTab ? myTasks : tasks;
  const isLoadingSource = isIndividualTab ? myLoading : loading;

  // -- Kanban columns for the active tab ------------------------------------
  const columns = useMemo(() => {
    const filtered = sourceTasks.filter(t => {
      if (!filter) return true;
      const q = filter.toLowerCase();
      return (
        (t.assignee || "").toLowerCase().includes(q) ||
        (t.title || t.task_description || "").toLowerCase().includes(q)
      );
    });
    return COLUMNS.map(col => ({
      ...col,
      tasks: filtered.filter(t => t.status === col.status),
    }));
  }, [sourceTasks, filter]);

  // -- Counts for the active tab ---------------------------------------------
  const counts = useMemo(() => ({
    to_do:       sourceTasks.filter(t => t.status === "to_do").length,
    in_progress: sourceTasks.filter(t => t.status === "in_progress").length,
    completed:   sourceTasks.filter(t => t.status === "completed").length,
    cancelled:   sourceTasks.filter(t => t.status === "cancelled").length,
  }), [sourceTasks]);

  const METRICS = useMemo(() => [
    { label: "To Do",       value: counts.to_do,       icon: <IconCheckbox />, color: "#ff6a52", variant: "blue"  },
    { label: "In Progress", value: counts.in_progress, icon: <IconProgress />, color: "#d99a3f", variant: "amber" },
    { label: "Completed",   value: counts.completed,   icon: <IconDone />,     color: "#3fae7d", variant: "teal"  },
  ], [counts]);

  // Call /process-message directly with the auth token so the task gets
  // stamped with owner_id + workspace_id (the useTasks hook doesn't send auth).
  const handleModalAdd = useCallback(async (msg) => {
    const res = await fetch(`${BASE_URL}/process-message`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ message: msg, source: "dashboard" }),
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.detail || "Database error. Task could not be saved.");
    }
    setShowModal(false);
    reload();
    fetchMyTasks();
    return data;
  }, [token, reload, fetchMyTasks]);

  const handleReload = () => {
    reload();
    fetchMyTasks();
  };

  // -- Tab config ------------------------------------------------------------
  const SECTION_TABS = [
    {
      label: "Team",
      description: isArchitect
        ? "All workspace tasks"
        : isNavigator
          ? `${user?.team_name || "Team"} tasks`
          : "Tasks in your workspace",
      color: "#ff6a52",
    },
    {
      label: "Individual",
      description: "My assigned tasks",
      color: "#c81f30",
    },
  ];

  return (
    <>
      {/* Topbar */}
      <header className="page-header">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {/* Ambient brand core — small + calm, built for exactly this spot
              (see three/IgnitionCore.jsx usage note). Purely decorative. */}
          <div style={{ width: 34, height: 34, flexShrink: 0 }} aria-hidden="true">
            <IgnitionCore size={34} intensity={0.45} />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--color-text-primary)", letterSpacing: "-0.02em", fontFamily: "var(--font-display)", lineHeight: 1.2 }}>Dashboard</div>
            <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 1 }}>Your AI assistant team is ready</div>
          </div>
        </div>

        {/* Search */}
        <div style={{ flex: 1, maxWidth: 340, position: "relative" }}>
          <svg style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", width: 14, height: 14, color: "var(--color-text-tertiary)", pointerEvents: "none" }} viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" strokeWidth="1.3"/>
            <path d="M10 10l3.5 3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
          </svg>
          <input
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Search tasks or assignees…"
            aria-label="Search tasks or assignees"
            className="field-input"
            style={{ paddingLeft: 32, borderRadius: 999, height: 36 }}
            onFocus={e => { e.target.style.borderColor = "rgba(255,106,82,0.55)"; e.target.style.background = "rgba(255,106,82,0.07)"; e.target.style.boxShadow = "0 0 0 3px rgba(255,106,82,0.14)"; }}
            onBlur={e => { e.target.style.borderColor = "var(--border-glass)"; e.target.style.background = "rgba(255,255,255,0.04)"; e.target.style.boxShadow = "none"; }}
          />
        </div>

        {/* Status badges + actions */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto", flexWrap: "wrap" }}>
          {[
            { label: "to do",       count: counts.to_do,       color: "#d99a3f" },
            { label: "in progress", count: counts.in_progress, color: "#ff6a52" },
            { label: "done",        count: counts.completed,   color: "#3fae7d" },
            { label: "cancelled",   count: counts.cancelled,   color: "#9a908a" },
          ].map(s => (
            <span key={s.label} style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              padding: "4px 10px", borderRadius: 999, fontSize: 12, fontWeight: 500,
              color: "var(--color-text-secondary)", background: "rgba(255,255,255,0.05)",
              border: "1px solid var(--border-glass)",
            }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.color, flexShrink: 0 }} aria-hidden="true" />
              {/* Count pops in fresh whenever it changes, instead of just
                  silently updating the text node. */}
              <AnimatePresence mode="popLayout" initial={false}>
                <motion.span
                  key={s.count}
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6, position: "absolute" }}
                  transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                >
                  {s.count}
                </motion.span>
              </AnimatePresence>
              {s.label}
            </span>
          ))}

          <motion.button
            onClick={handleReload} title="Refresh" aria-label="Refresh tasks" disabled={isLoadingSource}
            className="btn-ghost has-tooltip" data-tip="Refresh"
            whileTap={{ scale: 0.9 }}
            style={{ width: 36, height: 36, padding: 0, justifyContent: "center", opacity: isLoadingSource ? 0.5 : 1, fontSize: 15, display: "flex", alignItems: "center" }}
          >
            <motion.span
              animate={isLoadingSource ? { rotate: 360 } : { rotate: 0 }}
              transition={isLoadingSource ? { duration: 0.8, repeat: Infinity, ease: "linear" } : { duration: 0.2 }}
              style={{ display: "inline-block" }}
            >↻</motion.span>
          </motion.button>

          <motion.button
            onClick={() => setShowModal(true)} disabled={submitting} aria-label="Create new task"
            className="btn-primary"
            whileHover={{ scale: submitting ? 1 : 1.03 }}
            whileTap={{ scale: submitting ? 1 : 0.97 }}
            style={{ borderRadius: 999, fontSize: 13, letterSpacing: "-0.01em", opacity: submitting ? 0.6 : 1 }}
          >{submitting ? "Adding…" : "✦ New task"}</motion.button>
        </div>
      </header>

      {/* Page body */}
      <main className="page-enter" style={{ flex: 1, padding: "clamp(14px, 2.5vw, 28px) clamp(12px, 2.5vw, 28px) 40px", display: "flex", flexDirection: "column", gap: 24 }}>

        {/* Hero row */}
        <div className="fade-up" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 20, flexWrap: "wrap" }}>
          <div>
            <h1 style={{
              fontSize: "clamp(21px, 6vw, 28px)", fontWeight: 800, letterSpacing: "-0.04em", lineHeight: 1.1,
              fontFamily: "var(--font-display)",
              background: "linear-gradient(135deg, #efe7df 0%, #ffb199 50%, #ffb199 100%)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
            }}>
              Hey, {user?.name?.split(" ")[0] || "there"} ✦
            </h1>
            <p style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginTop: 6, letterSpacing: "0.01em" }}>
              {roleGreeting} · {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <AddToSlackButton />
            <ConnectGmailButton />
            <ConnectWhatsAppButton />
            <EmailTaskInbox />
            <div style={{
              padding: "6px 14px", borderRadius: 999,
              background: "rgba(63,174,125,0.1)", border: "1px solid rgba(63,174,125,0.22)",
              fontSize: 12, fontWeight: 600, color: "#3fae7d",
              display: "flex", alignItems: "center", gap: 7,
            }}>
              <span className="pulse-dot" aria-hidden="true" />
              System Online
            </div>
          </div>
        </div>

        {/* Error banners */}
        {error && (
          <div role="alert" style={{
            padding: "12px 18px", borderRadius: 10, fontSize: 13,
            background: "rgba(255,77,94,0.1)", border: "1px solid rgba(255,77,94,0.25)",
            color: "#ff4d5e", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
          }}>
            <span>⚠ {error}</span>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={clearError} style={{ border: "1px solid rgba(255,77,94,0.3)", borderRadius: 6, background: "transparent", color: "#ff4d5e", cursor: "pointer", fontSize: 12, fontWeight: 600, padding: "3px 10px" }}>Dismiss</button>
              <button onClick={handleReload} style={{ border: "1px solid rgba(255,77,94,0.3)", borderRadius: 6, background: "transparent", color: "#ff4d5e", cursor: "pointer", fontSize: 12, fontWeight: 600, padding: "3px 10px" }}>Retry</button>
            </div>
          </div>
        )}
        {myError && isIndividualTab && (
          <div role="alert" style={{
            padding: "12px 18px", borderRadius: 10, fontSize: 13,
            background: "rgba(255,77,94,0.1)", border: "1px solid rgba(255,77,94,0.25)",
            color: "#ff4d5e", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
          }}>
            <span>⚠ {myError}</span>
            <button onClick={fetchMyTasks} style={{ border: "1px solid rgba(255,77,94,0.3)", borderRadius: 6, background: "transparent", color: "#ff4d5e", cursor: "pointer", fontSize: 12, fontWeight: 600, padding: "3px 10px" }}>Retry</button>
          </div>
        )}

        {/* Metrics */}
        <div className="fade-up delay-1 stagger" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px,1fr))", gap: 16 }}>
          {METRICS.map((m, i) => (
            <TiltPCard key={m.label} tiltMax={4} style={{
              backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
              padding: "22px 24px", boxShadow: "0 4px 24px rgba(0,0,0,0.3)",
              cursor: "default",
              animationDelay: `${0.05 + i * 0.05}s`,
            }}>
              <div aria-hidden="true" style={{
                position: "absolute", top: 0, left: 0, right: 0, height: 2, borderRadius: "var(--radius-lg) var(--radius-lg) 0 0",
                background: m.variant === "blue" ? "linear-gradient(90deg,#ff6a52,#c81f30)" : m.variant === "amber" ? "linear-gradient(90deg,#d99a3f,#c9702e)" : "linear-gradient(90deg,#3fae7d,#d99a3f)",
              }} />
              <div className="glow-orb" style={{ top: -10, left: -10, width: 100, height: 100, background: m.color, opacity: 0.07 }} />
              <div style={{ position: "absolute", right: 14, bottom: 14, opacity: 0.2 }}><Sparkline color={m.color} /></div>
              <div className="icon-bubble" style={{ width: 42, height: 42, marginBottom: 18, color: m.color, background: `${m.color}15`, border: `1px solid ${m.color}25`, boxShadow: `0 0 16px ${m.color}18` }}>{m.icon}</div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-tertiary)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6, fontFamily: "var(--font-display)" }}>{m.label}</div>
              <div style={{ fontSize: 38, fontWeight: 800, letterSpacing: "-0.04em", color: "var(--color-text-primary)", lineHeight: 1, fontFamily: "var(--font-display)" }}>
                {isLoadingSource ? <div className="skeleton" style={{ width: 60, height: 38, display: "inline-block" }} /> : <AnimatedNumber value={m.value} />}
              </div>
            </TiltPCard>
          ))}
        </div>

        {/* -- Team / Individual section tabs --------------------------------- */}
        <div className="fade-up delay-2">

          {/* Section switcher */}
          <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 20, background: "rgba(255,255,255,0.03)", border: "1px solid var(--border-glass)", borderRadius: 14, padding: 4, width: "fit-content" }}>
            {SECTION_TABS.map((tab, i) => {
              const isActive = activeTab === i;
              return (
                <button
                  key={tab.label}
                  onClick={() => setActiveTab(i)}
                  style={{
                    position: "relative", padding: "8px 20px", borderRadius: 10, fontSize: 13, fontWeight: 600,
                    cursor: "pointer", border: "1px solid transparent", fontFamily: "var(--font-sans)",
                    background: "transparent",
                    color: isActive ? tab.color : "var(--color-text-secondary)",
                    transition: "color 0.15s",
                    display: "flex", alignItems: "center", gap: 7,
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = "var(--color-text-primary)"; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = "var(--color-text-secondary)"; }}
                >
                  {/* Sliding active pill — shared layoutId means framer-motion
                      animates it smoothly from the previous tab's position/size
                      instead of the old instant background swap. */}
                  {isActive && (
                    <motion.div
                      layoutId="dashboardTabPill"
                      transition={{ type: "spring", stiffness: 500, damping: 36 }}
                      style={{
                        position: "absolute", inset: 0, borderRadius: 10,
                        background: `linear-gradient(135deg, ${tab.color}22, ${tab.color}14)`,
                        border: `1px solid ${tab.color}40`,
                        boxShadow: `0 0 16px ${tab.color}25`,
                      }}
                    />
                  )}
                  {/* Content sits above the pill */}
                  <span style={{ position: "relative", display: "flex", alignItems: "center", gap: 7 }}>
                    {/* Icon */}
                    {i === 0 ? (
                      <svg width="14" height="14" viewBox="0 0 15 15" fill="none">
                        <circle cx="5" cy="5.5" r="2" stroke="currentColor" strokeWidth="1.4"/>
                        <circle cx="10.5" cy="5.5" r="2" stroke="currentColor" strokeWidth="1.4" opacity="0.7"/>
                        <path d="M1.5 13c0-1.7 1.6-3 3.5-3s3.5 1.3 3.5 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                        <path d="M10.5 10.5c1.6.3 3 1.5 3 2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" opacity="0.6"/>
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 15 15" fill="none">
                        <circle cx="7.5" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.4"/>
                        <path d="M3 13c0-2.2 2-4 4.5-4s4.5 1.8 4.5 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                      </svg>
                    )}
                    {tab.label}
                    {/* Task count badge */}
                    <span style={{
                      minWidth: 20, height: 18, borderRadius: 999, fontSize: 10, fontWeight: 700,
                      background: isActive ? `${tab.color}25` : "rgba(255,255,255,0.07)",
                      color: isActive ? tab.color : "var(--color-text-tertiary)",
                      border: `1px solid ${isActive ? tab.color + "40" : "transparent"}`,
                      display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "0 5px",
                    }}>
                      {i === 0 ? (loading ? "…" : tasks.length) : (myLoading ? "…" : myTasks.length)}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          {/* Section description */}
          <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 3, height: 16, borderRadius: 999, background: SECTION_TABS[activeTab].color, opacity: 0.8 }} />
            <span style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>
              {activeTab === 0
                ? isArchitect
                  ? "Showing all tasks in your workspace — role-filtered by default for team members"
                  : isNavigator
                    ? `Showing tasks for ${user?.team_name || "your team"} — switch to Individual to see your own`
                    : "Your workspace tasks — switch to Individual to focus on what's assigned to you"
                : "Tasks assigned to you directly — your personal work queue"
              }
            </span>
          </div>

          {/* Kanban board — skipped entirely when there are no tasks at all,
              so we don't stack an empty 4-column board (each column saying
              "No tasks") on top of the illustrated empty state below it;
              that redundant block was extra height with nothing to show. */}
          {isLoadingSource && sourceTasks.length === 0 ? (
            <motion.div
              role="status" aria-label="Loading tasks"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}
              style={{ textAlign: "center", padding: "60px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}
            >
              {/* Two counter-rotating rings instead of a flat spinner — a
                  small nod to the same "layered/dimensional" motion used
                  elsewhere (Ignition core, tilt cards) rather than a plain
                  CSS spinner. */}
              <div style={{ position: "relative", width: 28, height: 28 }}>
                <div style={{ position: "absolute", inset: 0, border: "2px solid rgba(255,106,82,0.18)", borderTopColor: "#ff6a52", borderRadius: "50%", animation: "spin 0.9s linear infinite" }} />
                <div style={{ position: "absolute", inset: 5, border: "2px solid rgba(217,154,63,0.18)", borderBottomColor: "#d99a3f", borderRadius: "50%", animation: "spin 1.3s linear infinite reverse" }} />
              </div>
              <span style={{ fontSize: 13, color: "var(--color-text-tertiary)" }}>Loading tasks…</span>
            </motion.div>
          ) : sourceTasks.length === 0 ? null : (
            <div className="dash-kanban-scroll" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              {/* Crossfade the whole board when switching Team/Individual —
                  keyed on activeTab so AnimatePresence treats it as a fresh
                  mount and plays the stagger-in again, instead of the old
                  instant swap. */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  variants={staggerContainer}
                  initial="hidden"
                  animate="show"
                  exit={{ opacity: 0, transition: { duration: 0.15 } }}
                  className="dash-kanban-grid"
                  style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${COLUMNS.length}, minmax(220px, 1fr))`,
                    gap: 20, alignItems: "start",
                    minWidth: COLUMNS.length * 240,
                  }}
                >
                  {columns.map(col => (
                    <KanbanColumn
                      key={col.status} status={col.status} label={col.label}
                      tasks={col.tasks} onMove={moveTask} onDelete={removeTask}
                      timezone={user?.timezone}
                    />
                  ))}
                </motion.div>
              </AnimatePresence>
            </div>
          )}

          {/* Empty state */}
          {!isLoadingSource && sourceTasks.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              style={{ textAlign: "center", padding: "60px 0", border: "1px dashed var(--border-glass)", borderRadius: 16 }}
            >
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
                style={{ fontSize: 36, marginBottom: 10, opacity: 0.3 }}
              >
                {activeTab === 0 ? "👥" : "👤"}
              </motion.div>
              <div style={{ fontSize: 14, color: "var(--color-text-secondary)", marginBottom: 6 }}>
                {activeTab === 0 ? "No team tasks yet" : "No individual tasks yet"}
              </div>
              <div style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>
                {activeTab === 0
                  ? "Tasks created by your team will appear here"
                  : "Tasks assigned to you will appear here — create one or ask your team lead"}
              </div>
            </motion.div>
          )}
        </div>
      </main>

      <AnimatePresence>
        {showModal && (
          <div role="dialog" aria-modal="true" aria-label="Add new task" style={{ position: "fixed", inset: 0, zIndex: 100 }}>
            <AddTaskModal onClose={() => setShowModal(false)} onAdd={handleModalAdd} />
          </div>
        )}
      </AnimatePresence>
    </>
  );
}


// -- Segment 11: Integrations Page --------------------------------------------
function IntegrationsPage() {
  const { token } = useAuth();
  const API = BASE_URL;

  // Status of which integrations are configured
  const [status, setStatus]   = useState(null);
  const [loading, setLoading] = useState(true);

  // Per-service form state
  const [notion, setNotion]   = useState({ notion_token: "", notion_database_id: "" });
  const [jira, setJira]       = useState({ jira_base_url: "", jira_email: "", jira_api_token: "", jira_project_key: "" });
  const [trello, setTrello]   = useState({ trello_api_key: "", trello_token: "", trello_list_id: "" });

  // Save / import state
  const [saving, setSaving]     = useState(null);   // "notion" | "jira" | "trello"
  const [importing, setImporting] = useState(null);
  const [result, setResult]     = useState(null);   // last import result
  const [saveMsg, setSaveMsg]   = useState(null);

  const authHeaders = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetch(`${API}/integrations/status`, { headers: authHeaders })
      .then(r => r.json())
      .then(d => { setStatus(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  async function handleSave(service, payload) {
    setSaving(service);
    setSaveMsg(null);
    try {
      const r = await fetch(`${API}/integrations/config`, {
        method: "PUT", headers: authHeaders, body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (r.ok) {
        setSaveMsg({ service, type: "success", text: "Credentials saved!" });
        // Refresh status
        const sr = await fetch(`${API}/integrations/status`, { headers: authHeaders });
        setStatus(await sr.json());
      } else {
        setSaveMsg({ service, type: "error", text: d.detail || "Save failed." });
      }
    } catch {
      setSaveMsg({ service, type: "error", text: "Network error." });
    } finally {
      setSaving(null);
    }
  }

  async function handleImport(service) {
    setImporting(service);
    setResult(null);
    try {
      const r = await fetch(`${API}/integrations/${service}/import`, {
        method: "POST", headers: authHeaders, body: JSON.stringify({ source_id: null, limit: 50 }),
      });
      const d = await r.json();
      setResult({ service, ...d, error: r.ok ? null : (d.detail || "Import failed.") });
    } catch {
      setResult({ service, error: "Network error." });
    } finally {
      setImporting(null);
    }
  }

  const card = (children, extra = {}) => (
    <div className="integ-card" style={{ ...extra }}>{children}</div>
  );

  const field = (label, value, onChange, placeholder, type = "text") => (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text-secondary)", letterSpacing: "0.04em" }}>{label}</label>
      <input
        type={type} value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: "100%", height: 38, padding: "0 12px",
          background: "rgba(255,255,255,0.05)", border: "1px solid var(--border-glass)",
          borderRadius: 8, fontFamily: "var(--font-sans)", fontSize: 13,
          color: "var(--color-text-primary)", outline: "none",
        }}
        onFocus={e => { e.target.style.borderColor = "rgba(255,106,82,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(255,106,82,0.12)"; }}
        onBlur={e => { e.target.style.borderColor = "var(--border-glass)"; e.target.style.boxShadow = "none"; }}
      />
    </div>
  );

  const saveBtn = (service, onClick) => (
    <button onClick={onClick} disabled={saving === service} className="btn-primary" style={{ opacity: saving === service ? 0.55 : 1 }}>
      {saving === service ? "Saving…" : "Save credentials"}
    </button>
  );

  const importBtn = (service, configured) => (
    <button
      onClick={() => handleImport(service)}
      disabled={!configured || importing === service}
      title={!configured ? "Configure credentials first" : `Import tasks from ${service}`}
      style={{
        height: 36, padding: "0 18px", borderRadius: 8,
        border: "1px solid var(--border-glass)", background: "rgba(255,255,255,0.05)",
        color: configured ? "var(--color-text-primary)" : "var(--color-text-tertiary)",
        fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 600,
        cursor: !configured || importing === service ? "not-allowed" : "pointer",
        opacity: !configured || importing === service ? 0.5 : 1,
      }}
    >{importing === service ? "Importing…" : "↓ Import tasks"}</button>
  );

  const statusDot = (configured) => (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600,
      background: configured ? "rgba(63,174,125,0.12)" : "rgba(255,255,255,0.05)",
      border: `1px solid ${configured ? "rgba(63,174,125,0.3)" : "var(--border-glass)"}`,
      color: configured ? "#3fae7d" : "var(--color-text-tertiary)",
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: configured ? "#3fae7d" : "#6b6058" }} />
      {configured ? "Configured" : "Not configured"}
    </span>
  );

  const msgBanner = (service) => saveMsg?.service === service ? (
    <div style={{
      padding: "8px 12px", borderRadius: 8, fontSize: 12, fontWeight: 500,
      background: saveMsg.type === "success" ? "rgba(63,174,125,0.1)" : "rgba(255,77,94,0.1)",
      border: `1px solid ${saveMsg.type === "success" ? "rgba(63,174,125,0.3)" : "rgba(255,77,94,0.3)"}`,
      color: saveMsg.type === "success" ? "#3fae7d" : "#ff4d5e",
    }}>{saveMsg.text}</div>
  ) : null;

  if (loading) return (
    <main style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 24, height: 24, border: "2px solid rgba(255,106,82,0.2)", borderTopColor: "#ff6a52", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
    </main>
  );

  return (
    <main className="page-enter" style={{ flex: 1, padding: "clamp(14px, 2.5vw, 28px) clamp(12px, 2.5vw, 28px) 40px", display: "flex", flexDirection: "column", gap: 24, maxWidth: 860 }}>

      {/* Header */}
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--color-text-primary)", marginBottom: 6 }}>Integrations</h1>
        <p style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>
          Import your existing cards, pages, or issues from Notion, Jira, or Trello straight onto your Kanban board. Credentials are stored securely in your workspace settings. Items already imported won't be duplicated on repeat imports.
        </p>
      </div>

      {/* Last import result */}
      {result && (
        <div style={{
          padding: "14px 18px", borderRadius: 12,
          background: result.error ? "rgba(255,77,94,0.1)" : "rgba(63,174,125,0.08)",
          border: `1px solid ${result.error ? "rgba(255,77,94,0.3)" : "rgba(63,174,125,0.25)"}`,
          color: result.error ? "#ff4d5e" : "var(--color-text-primary)",
          fontSize: 13,
        }}>
          {result.error ? (
            <span>⚠ {result.error}</span>
          ) : (
            <span>
              ✅ <strong>{result.integration}</strong> import complete —{" "}
              <span style={{ color: "#3fae7d" }}>{result.imported} imported</span>
              {result.skipped > 0 && <span> · {result.skipped} already imported</span>}
              {result.failed > 0 && <span style={{ color: "#ff4d5e" }}>, {result.failed} failed</span>}
              {" "}out of {result.total_found} found
            </span>
          )}
          <button onClick={() => setResult(null)} style={{ float: "right", background: "transparent", border: "none", color: "inherit", cursor: "pointer", fontSize: 14, opacity: 0.6 }}>✕</button>
        </div>
      )}

      {/* -- Notion -- */}
      {card(
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: "#f5f0eb", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>𝓝</div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "var(--color-text-primary)" }}>Notion</div>
                <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginTop: 2 }}>Import pages from a Notion database as tasks</div>
              </div>
            </div>
            {statusDot(status?.notion_configured)}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px,1fr))", gap: 12 }}>
            {field("Integration Token", notion.notion_token, v => setNotion(p => ({ ...p, notion_token: v })), "secret_...", "password")}
            {field("Database ID", notion.notion_database_id, v => setNotion(p => ({ ...p, notion_database_id: v })), "xxxxxxxx-xxxx-...")}
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", alignItems: "center" }}>
            {msgBanner("notion")}
            {saveBtn("notion", () => handleSave("notion", notion))}
            {importBtn("notion", status?.notion_configured)}
          </div>
        </div>
      )}

      {/* -- Jira -- */}
      {card(
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: "#0052cc", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: "#f5f0eb", fontWeight: 800 }}>J</div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "var(--color-text-primary)" }}>Jira</div>
                <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginTop: 2 }}>Import issues from an Atlassian Jira project</div>
              </div>
            </div>
            {statusDot(status?.jira_configured)}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px,1fr))", gap: 12 }}>
            {field("Base URL", jira.jira_base_url, v => setJira(p => ({ ...p, jira_base_url: v })), "https://yourorg.atlassian.net")}
            {field("Project Key", jira.jira_project_key, v => setJira(p => ({ ...p, jira_project_key: v })), "PROJ")}
            {field("Email", jira.jira_email, v => setJira(p => ({ ...p, jira_email: v })), "you@company.com")}
            {field("API Token", jira.jira_api_token, v => setJira(p => ({ ...p, jira_api_token: v })), "Your Atlassian API token", "password")}
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", alignItems: "center" }}>
            {msgBanner("jira")}
            {saveBtn("jira", () => handleSave("jira", jira))}
            {importBtn("jira", status?.jira_configured)}
          </div>
        </div>
      )}

      {/* -- Trello -- */}
      {card(
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: "#0079bf", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: "#f5f0eb", fontWeight: 800 }}>T</div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "var(--color-text-primary)" }}>Trello</div>
                <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginTop: 2 }}>Import cards from a Trello list as tasks</div>
              </div>
            </div>
            {statusDot(status?.trello_configured)}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px,1fr))", gap: 12 }}>
            {field("API Key", trello.trello_api_key, v => setTrello(p => ({ ...p, trello_api_key: v })), "From trello.com/app-key", "password")}
            {field("Token", trello.trello_token, v => setTrello(p => ({ ...p, trello_token: v })), "OAuth token", "password")}
            {field("List ID", trello.trello_list_id, v => setTrello(p => ({ ...p, trello_list_id: v })), "Source list ID")}
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", alignItems: "center" }}>
            {msgBanner("trello")}
            {saveBtn("trello", () => handleSave("trello", trello))}
            {importBtn("trello", status?.trello_configured)}
          </div>
        </div>
      )}

    </main>
  );
}

// -- Segment 12: Locale Page ---------------------------------------------------
function LocalePage() {
  const { user, token } = useAuth();
  const API = BASE_URL;
  const isArchitect = user?.role === "architect";

  const [options, setOptions]           = useState(null);
  const [userLocale, setUserLocale]     = useState(null);
  const [wsLocale, setWsLocale]         = useState(null);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(null); // "user" | "workspace"
  const [saveMsg, setSaveMsg]           = useState(null);

  // Form state — user prefs
  const [lang, setLang]     = useState("en");
  const [tz, setTz]         = useState("UTC");
  const [curr, setCurr]     = useState("USD");

  // Form state — workspace defaults
  const [wsLang, setWsLang] = useState("en");
  const [wsTz, setWsTz]     = useState("UTC");
  const [wsCurr, setWsCurr] = useState("USD");

  const authHeaders = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  useEffect(() => {
    const safe = (p) => p.catch(() => null);
    Promise.all([
      safe(fetch(`${API}/locale/options`).then(r => r.ok ? r.json() : null)),
      safe(fetch(`${API}/locale/settings`, { headers: authHeaders }).then(r => r.ok ? r.json() : null)),
      isArchitect ? safe(fetch(`${API}/locale/workspace`, { headers: authHeaders }).then(r => r.ok ? r.json() : null)) : Promise.resolve(null),
    ]).then(([opts, ul, wl]) => {
      setOptions(opts);
      setUserLocale(ul);
      setLang(ul?.language || "en");
      setTz(ul?.timezone || "UTC");
      setCurr(ul?.currency || "USD");
      if (wl) {
        setWsLocale(wl);
        setWsLang(wl?.default_language || "en");
        setWsTz(wl?.default_timezone || "UTC");
        setWsCurr(wl?.default_currency || "USD");
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  async function handleSaveUser() {
    setSaving("user"); setSaveMsg(null);
    try {
      const r = await fetch(`${API}/locale/settings`, {
        method: "PUT", headers: authHeaders,
        body: JSON.stringify({ language: lang, timezone: tz, currency: curr }),
      });
      const d = await r.json();
      if (r.ok) { setUserLocale(d); setSaveMsg({ scope: "user", type: "success", text: "Your locale preferences saved!" }); }
      else setSaveMsg({ scope: "user", type: "error", text: d.detail || "Save failed." });
    } catch { setSaveMsg({ scope: "user", type: "error", text: "Network error." }); }
    finally { setSaving(null); }
  }

  async function handleSaveWorkspace() {
    setSaving("workspace"); setSaveMsg(null);
    try {
      const r = await fetch(`${API}/locale/workspace`, {
        method: "PUT", headers: authHeaders,
        body: JSON.stringify({ default_language: wsLang, default_timezone: wsTz, default_currency: wsCurr }),
      });
      const d = await r.json();
      if (r.ok) { setWsLocale(d); setSaveMsg({ scope: "workspace", type: "success", text: "Workspace locale defaults saved!" }); }
      else setSaveMsg({ scope: "workspace", type: "error", text: d.detail || "Save failed." });
    } catch { setSaveMsg({ scope: "workspace", type: "error", text: "Network error." }); }
    finally { setSaving(null); }
  }

  const selectStyle = {
    width: "100%", height: 38, padding: "0 12px",
    background: "#1a0a08", border: "1px solid var(--border-glass)",
    borderRadius: 8, fontFamily: "var(--font-sans)", fontSize: 13,
    color: "#efe7df", outline: "none", cursor: "pointer",
    appearance: "none", WebkitAppearance: "none",
  };
  const optStyle = { background: "#1a0a08", color: "#efe7df" };

  const labelStyle = { fontSize: 12, fontWeight: 600, color: "var(--color-text-secondary)", letterSpacing: "0.04em", marginBottom: 6, display: "block" };

  const card = (children) => (
    <div style={{
      background: "rgba(255,255,255,0.04)", backdropFilter: "blur(12px)",
      border: "1px solid var(--border-glass)", borderRadius: 16, padding: "22px 24px",
    }}>{children}</div>
  );

  const msgBanner = (scope) => saveMsg?.scope === scope ? (
    <div style={{
      padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 500,
      background: saveMsg.type === "success" ? "rgba(63,174,125,0.1)" : "rgba(255,77,94,0.1)",
      border: `1px solid ${saveMsg.type === "success" ? "rgba(63,174,125,0.3)" : "rgba(255,77,94,0.3)"}`,
      color: saveMsg.type === "success" ? "#3fae7d" : "#ff4d5e",
    }}>{saveMsg.text}</div>
  ) : null;

  const saveBtn = (scope, onClick) => (
    <button onClick={onClick} disabled={saving === scope} style={{
      height: 36, padding: "0 20px", borderRadius: 8, border: "none",
      background: "var(--grad-primary)",
      color: "#f5f0eb", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 600,
      cursor: saving === scope ? "not-allowed" : "pointer",
      opacity: saving === scope ? 0.6 : 1,
    }}>{saving === scope ? "Saving…" : "Save"}</button>
  );

  // Current display values for the summary pills
  const langLabel = options?.languages?.[lang] || lang;
  const currLabel = options?.currencies?.[curr] || curr;

  if (loading) return (
    <main style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 24, height: 24, border: "2px solid rgba(255,106,82,0.2)", borderTopColor: "#ff6a52", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
    </main>
  );

  return (
    <main style={{ flex: 1, padding: "clamp(14px, 2.5vw, 28px) clamp(12px, 2.5vw, 28px) 40px", display: "flex", flexDirection: "column", gap: 24, maxWidth: 780 }}>

      {/* Header */}
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--color-text-primary)", marginBottom: 6 }}>Locale &amp; Preferences</h1>
        <p style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>
          Set your language, timezone, and currency. Changes apply immediately to your account.
        </p>
      </div>

      {/* Current summary pills */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {[
          { label: "Language", value: langLabel, icon: "🌐" },
          { label: "Timezone", value: tz, icon: "🕐" },
          { label: "Currency", value: currLabel, icon: "💱" },
        ].map(p => (
          <div key={p.label} style={{
            padding: "6px 14px", borderRadius: 999,
            background: "rgba(255,106,82,0.1)", border: "1px solid rgba(255,106,82,0.25)",
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <span style={{ fontSize: 14 }}>{p.icon}</span>
            <span style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>{p.label}:</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#ff6a52" }}>{p.value}</span>
          </div>
        ))}
      </div>

      {/* -- User Locale Card -- */}
      {card(
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(255,106,82,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>👤</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "var(--color-text-primary)" }}>My Preferences</div>
              <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginTop: 2 }}>Applied to your account only</div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px,1fr))", gap: 16 }}>
            {/* Language */}
            <div>
              <label style={labelStyle}>Language</label>
              <div style={{ position: "relative" }}>
                <select value={lang} onChange={e => setLang(e.target.value)} style={selectStyle}
                  onFocus={e => { e.target.style.borderColor = "rgba(255,106,82,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(255,106,82,0.12)"; }}
                  onBlur={e => { e.target.style.borderColor = "var(--border-glass)"; e.target.style.boxShadow = "none"; }}
                >
                  {options && Object.entries(options.languages).map(([code, name]) => (
                    <option style={optStyle} key={code} value={code}>{name}</option>
                  ))}
                </select>
                <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "var(--color-text-tertiary)", fontSize: 11 }}>▾</span>
              </div>
            </div>

            {/* Timezone */}
            <div>
              <label style={labelStyle}>Timezone</label>
              <div style={{ position: "relative" }}>
                <select value={tz} onChange={e => setTz(e.target.value)} style={selectStyle}
                  onFocus={e => { e.target.style.borderColor = "rgba(255,106,82,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(255,106,82,0.12)"; }}
                  onBlur={e => { e.target.style.borderColor = "var(--border-glass)"; e.target.style.boxShadow = "none"; }}
                >
                  {options?.timezones?.map(t => (
                    <option style={optStyle} key={t} value={t}>{t}</option>
                  ))}
                </select>
                <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "var(--color-text-tertiary)", fontSize: 11 }}>▾</span>
              </div>
            </div>

            {/* Currency */}
            <div>
              <label style={labelStyle}>Currency</label>
              <div style={{ position: "relative" }}>
                <select value={curr} onChange={e => setCurr(e.target.value)} style={selectStyle}
                  onFocus={e => { e.target.style.borderColor = "rgba(255,106,82,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(255,106,82,0.12)"; }}
                  onBlur={e => { e.target.style.borderColor = "var(--border-glass)"; e.target.style.boxShadow = "none"; }}
                >
                  {options && Object.entries(options.currencies).map(([code, label]) => (
                    <option style={optStyle} key={code} value={code}>{label}</option>
                  ))}
                </select>
                <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "var(--color-text-tertiary)", fontSize: 11 }}>▾</span>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "flex-end" }}>
            {msgBanner("user")}
            {saveBtn("user", handleSaveUser)}
          </div>
        </div>
      )}

      {/* -- Workspace Locale Card (Architect only) -- */}
      {isArchitect && card(
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(200,31,48,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🏢</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "var(--color-text-primary)" }}>Workspace Defaults</div>
              <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginTop: 2 }}>Applied to new members who haven't set their own preferences</div>
            </div>
            <span style={{
              marginLeft: "auto", padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600,
              background: "rgba(200,31,48,0.15)", border: "1px solid rgba(200,31,48,0.3)", color: "#c81f30",
            }}>Architect only</span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px,1fr))", gap: 16 }}>
            <div>
              <label style={labelStyle}>Default Language</label>
              <div style={{ position: "relative" }}>
                <select value={wsLang} onChange={e => setWsLang(e.target.value)} style={selectStyle}>
                  {options && Object.entries(options.languages).map(([code, name]) => (
                    <option style={optStyle} key={code} value={code}>{name}</option>
                  ))}
                </select>
                <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "var(--color-text-tertiary)", fontSize: 11 }}>▾</span>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Default Timezone</label>
              <div style={{ position: "relative" }}>
                <select value={wsTz} onChange={e => setWsTz(e.target.value)} style={selectStyle}>
                  {options?.timezones?.map(t => (
                    <option style={optStyle} key={t} value={t}>{t}</option>
                  ))}
                </select>
                <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "var(--color-text-tertiary)", fontSize: 11 }}>▾</span>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Default Currency</label>
              <div style={{ position: "relative" }}>
                <select value={wsCurr} onChange={e => setWsCurr(e.target.value)} style={selectStyle}>
                  {options && Object.entries(options.currencies).map(([code, label]) => (
                    <option style={optStyle} key={code} value={code}>{label}</option>
                  ))}
                </select>
                <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "var(--color-text-tertiary)", fontSize: 11 }}>▾</span>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "flex-end" }}>
            {msgBanner("workspace")}
            {saveBtn("workspace", handleSaveWorkspace)}
          </div>
        </div>
      )}

      {/* Info box */}
      <div style={{
        padding: "14px 18px", borderRadius: 12,
        background: "rgba(255,255,255,0.03)", border: "1px solid var(--border-glass)",
        fontSize: 12, color: "var(--color-text-tertiary)", lineHeight: 1.7,
      }}>
        <strong style={{ color: "var(--color-text-secondary)" }}>ℹ How locale settings work</strong><br />
        Your personal settings override workspace defaults. Timezone affects how deadlines and timestamps are displayed throughout the app.
        Currency is used for any budget or cost fields. Language preference is stored and will power UI translations in a future release.
      </div>

    </main>
  );
}

// -- TeamsPage — Segment 9: Microsoft Teams Integration -----------------------
function TeamsPage() {
  const { token, user } = useAuth();
  const API = BASE_URL;
  const authHeaders = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
  const isArchitect = user?.role === "architect";

  const [status, setStatus]     = useState(null);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState(null);
  const [tenantId, setTenantId] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [chForm, setChForm]     = useState({ channel_id: "", channel_name: "", service_url: "", conversation_id: "" });

  useEffect(() => {
    Promise.all([
      fetch(`${API}/teams/status`,   { headers: authHeaders }).then(r => r.json()),
      fetch(`${API}/teams/channels`, { headers: authHeaders }).then(r => r.json()),
    ]).then(([s, ch]) => {
      setStatus(s); setTenantId(s?.tenant_id || "");
      setChannels(Array.isArray(ch) ? ch : []); setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  async function handleSaveConfig() {
    setSaving(true); setMsg(null);
    try {
      const r = await fetch(`${API}/teams/config`, { method: "PUT", headers: authHeaders, body: JSON.stringify({ tenant_id: tenantId }) });
      const d = await r.json();
      if (r.ok) { setStatus(s => ({ ...s, connected: true, tenant_id: tenantId })); setMsg({ type: "success", text: "Teams tenant ID saved!" }); }
      else setMsg({ type: "error", text: d.detail || "Save failed." });
    } catch { setMsg({ type: "error", text: "Network error." }); }
    finally { setSaving(false); }
  }

  async function handleDisconnect() {
    if (!window.confirm("Disconnect Teams from this workspace?")) return;
    setSaving(true);
    try {
      await fetch(`${API}/teams/config`, { method: "DELETE", headers: authHeaders });
      setStatus(s => ({ ...s, connected: false, tenant_id: null })); setTenantId(""); setMsg({ type: "success", text: "Teams disconnected." });
    } catch { setMsg({ type: "error", text: "Network error." }); }
    finally { setSaving(false); }
  }

  async function handleRegisterChannel(e) {
    e.preventDefault(); setSaving(true); setMsg(null);
    try {
      const r = await fetch(`${API}/teams/channels`, { method: "POST", headers: authHeaders, body: JSON.stringify(chForm) });
      const d = await r.json();
      if (r.ok) { setChannels(ch => [...ch, d]); setChForm({ channel_id: "", channel_name: "", service_url: "", conversation_id: "" }); setShowForm(false); setMsg({ type: "success", text: `Channel "${d.channel_name}" registered!` }); }
      else setMsg({ type: "error", text: d.detail || "Registration failed." });
    } catch { setMsg({ type: "error", text: "Network error." }); }
    finally { setSaving(false); }
  }

  async function handleRemoveChannel(id, name) {
    if (!window.confirm(`Remove channel "${name}"?`)) return;
    try {
      await fetch(`${API}/teams/channels/${id}`, { method: "DELETE", headers: authHeaders });
      setChannels(ch => ch.filter(c => c.id !== id)); setMsg({ type: "success", text: `Channel "${name}" removed.` });
    } catch { setMsg({ type: "error", text: "Network error." }); }
  }

  const inp = { width: "100%", height: 38, padding: "0 12px", background: "rgba(255,255,255,0.05)", border: "1px solid var(--border-glass)", borderRadius: 8, fontFamily: "var(--font-sans)", fontSize: 13, color: "var(--color-text-primary)", outline: "none" };
  const lbl = { fontSize: 12, fontWeight: 600, color: "var(--color-text-secondary)", letterSpacing: "0.04em", marginBottom: 6, display: "block" };
  const card = (ch) => <div style={{ background: "rgba(255,255,255,0.04)", backdropFilter: "blur(12px)", border: "1px solid var(--border-glass)", borderRadius: 16, padding: "22px 24px" }}>{ch}</div>;

  if (loading) return <main style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}><div style={{ width: 24, height: 24, border: "2px solid rgba(255,106,82,0.2)", borderTopColor: "#ff6a52", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} /></main>;

  return (
    <main style={{ flex: 1, padding: "clamp(14px, 2.5vw, 28px) clamp(12px, 2.5vw, 28px) 40px", display: "flex", flexDirection: "column", gap: 24, maxWidth: 820 }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--color-text-primary)", marginBottom: 6 }}>Microsoft Teams</h1>
        <p style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>Connect your workspace to Microsoft Teams. Create tasks directly from Teams channels by mentioning the bot.</p>
      </div>
      {msg && <div style={{ padding: "10px 16px", borderRadius: 10, fontSize: 13, fontWeight: 500, background: msg.type === "success" ? "rgba(63,174,125,0.1)" : "rgba(255,77,94,0.1)", border: `1px solid ${msg.type === "success" ? "rgba(63,174,125,0.3)" : "rgba(255,77,94,0.3)"}`, color: msg.type === "success" ? "#3fae7d" : "#ff4d5e" }}>{msg.text}</div>}
      {card(<div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: status?.connected ? "rgba(63,174,125,0.12)" : "rgba(255,255,255,0.06)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>💬</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--color-text-primary)" }}>Teams Connection</div>
            <div style={{ fontSize: 12, marginTop: 2, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: status?.connected ? "#3fae7d" : "#9a908a", display: "inline-block" }} />
              <span style={{ color: status?.connected ? "#3fae7d" : "var(--color-text-tertiary)" }}>{status?.connected ? `Connected &mdash; Tenant: ${status.tenant_id}` : "Not connected"}</span>
            </div>
          </div>
          {status?.connected && isArchitect && <button onClick={handleDisconnect} disabled={saving} style={{ height: 32, padding: "0 14px", borderRadius: 8, border: "1px solid rgba(255,77,94,0.3)", background: "rgba(255,77,94,0.08)", color: "#ff4d5e", fontFamily: "var(--font-sans)", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Disconnect</button>}
        </div>
        <div style={{ padding: "14px 16px", borderRadius: 10, background: "rgba(255,106,82,0.06)", border: "1px solid rgba(255,106,82,0.15)", fontSize: 12, color: "var(--color-text-secondary)", lineHeight: 1.8 }}>
          <strong style={{ color: "#ff6a52", display: "block", marginBottom: 6 }}>⚙ Setup Instructions</strong>
          1. Azure Portal → create a Bot resource → enable Teams channel.<br />
          2. Set <code style={{ background: "rgba(255,255,255,0.08)", padding: "1px 5px", borderRadius: 4 }}>TEAMS_APP_ID</code> and <code style={{ background: "rgba(255,255,255,0.08)", padding: "1px 5px", borderRadius: 4 }}>TEAMS_APP_SECRET</code> in Railway.<br />
          3. Set messaging endpoint: <code style={{ background: "rgba(255,255,255,0.08)", padding: "1px 5px", borderRadius: 4 }}>{status?.webhook_url || `${API}/teams/webhook`}</code><br />
          4. Paste your Azure AD Tenant ID below and save.
        </div>
        {isArchitect && <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <label style={lbl}>Azure AD Tenant ID</label>
          <div style={{ display: "flex", gap: 10 }}>
            <input value={tenantId} onChange={e => setTenantId(e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" style={{ ...inp, flex: 1 }} />
            <button onClick={handleSaveConfig} disabled={saving || !tenantId.trim()} style={{ height: 38, padding: "0 20px", borderRadius: 8, border: "none", background: "var(--grad-primary)", color: "#f5f0eb", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: (saving || !tenantId.trim()) ? 0.6 : 1 }}>{saving ? "Saving…" : "Save"}</button>
          </div>
        </div>}
      </div>)}
      {card(<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div><div style={{ fontSize: 15, fontWeight: 700, color: "var(--color-text-primary)" }}>Registered Channels</div><div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginTop: 2 }}>Channels that receive proactive task notifications</div></div>
          {isArchitect && <button onClick={() => setShowForm(f => !f)} style={{ height: 34, padding: "0 16px", borderRadius: 8, border: "1px solid rgba(255,106,82,0.3)", background: "rgba(255,106,82,0.08)", color: "#ff6a52", fontFamily: "var(--font-sans)", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{showForm ? "Cancel" : "+ Add Channel"}</button>}
        </div>
        {showForm && isArchitect && <div style={{ padding: 18, borderRadius: 12, background: "rgba(255,106,82,0.05)", border: "1px solid rgba(255,106,82,0.15)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px,1fr))", gap: 12 }}>
            {[["channel_name","Channel Name","#general"],["channel_id","Channel ID","19:abc@thread.tacv2"],["service_url","Service URL","https://smba.trafficmanager.net/…"],["conversation_id","Conversation ID","19:abc@thread.tacv2"]].map(([k, label, ph]) => (
              <div key={k}><label style={lbl}>{label}</label><input value={chForm[k]} onChange={e => setChForm(f => ({ ...f, [k]: e.target.value }))} placeholder={ph} style={inp} /></div>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
            <button onClick={handleRegisterChannel} disabled={saving || !chForm.channel_id || !chForm.channel_name} style={{ height: 36, padding: "0 20px", borderRadius: 8, border: "none", background: "var(--grad-primary)", color: "#f5f0eb", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{saving ? "Registering…" : "Register Channel"}</button>
          </div>
        </div>}
        {channels.length === 0 ? <div style={{ padding: 28, textAlign: "center", color: "var(--color-text-tertiary)", fontSize: 13, border: "1px dashed var(--border-glass)", borderRadius: 10 }}>No channels registered yet.{isArchitect && " Click \"+ Add Channel\" to register."}</div>
        : channels.map(ch => (
          <div key={ch.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", borderRadius: 10, background: "rgba(255,255,255,0.03)", border: "1px solid var(--border-glass)" }}>
            <div style={{ width: 34, height: 34, borderRadius: 8, background: "rgba(255,106,82,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>💬</div>
            <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)" }}>{ch.channel_name}</div><div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 2, fontFamily: "var(--font-mono)" }}>{ch.channel_id}</div></div>
            <div style={{ padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, background: "rgba(63,174,125,0.1)", border: "1px solid rgba(63,174,125,0.25)", color: "#3fae7d" }}>Active</div>
            {isArchitect && <button onClick={() => handleRemoveChannel(ch.id, ch.channel_name)} style={{ height: 28, padding: "0 12px", borderRadius: 6, border: "1px solid rgba(255,77,94,0.25)", background: "rgba(255,77,94,0.06)", color: "#ff4d5e", fontFamily: "var(--font-sans)", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Remove</button>}
          </div>
        ))}
      </div>)}
    </main>
  );
}

// -- ApiPage — Segment 13: Public REST API & Key Management --------------------
function ApiPage() {
  const { token, user } = useAuth();
  const API = BASE_URL;
  const authHeaders = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
  const isArchitect = user?.role === "architect";
  const BACKEND = API || "https://your-backend.railway.app";

  const [keys, setKeys]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [creating, setCreating]   = useState(false);
  const [msg, setMsg]             = useState(null);
  const [newKeyName, setNewKeyName] = useState("");
  const [revealedKey, setRevealedKey] = useState(null); // { id, key }
  const [copied, setCopied]       = useState(false);

  useEffect(() => {
    fetch(`${API}/api/v1/keys`, { headers: authHeaders })
      .then(r => r.json()).then(d => { setKeys(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  async function handleCreateKey() {
    if (!newKeyName.trim()) return;
    setCreating(true); setMsg(null); setRevealedKey(null);
    try {
      const r = await fetch(`${API}/api/v1/keys`, {
        method: "POST", headers: authHeaders,
        body: JSON.stringify({ name: newKeyName.trim() }),
      });
      const d = await r.json();
      if (r.ok) {
        setRevealedKey({ id: d.id, key: d.key, name: d.name });
        setKeys(k => [{ id: d.id, name: d.name, key_prefix: d.key_prefix, workspace_id: d.workspace_id, is_active: true, created_at: d.created_at }, ...k]);
        setNewKeyName("");
        setMsg({ type: "success", text: "API key created! Copy it now — it won't be shown again." });
      } else {
        setMsg({ type: "error", text: d.detail || "Failed to create key." });
      }
    } catch { setMsg({ type: "error", text: "Network error." }); }
    finally { setCreating(false); }
  }

  async function handleRevoke(id, name) {
    if (!window.confirm(`Revoke key "${name}"? Any integrations using it will stop working.`)) return;
    try {
      await fetch(`${API}/api/v1/keys/${id}`, { method: "DELETE", headers: authHeaders });
      setKeys(k => k.filter(key => key.id !== id));
      if (revealedKey?.id === id) setRevealedKey(null);
      setMsg({ type: "success", text: `Key "${name}" revoked.` });
    } catch { setMsg({ type: "error", text: "Network error." }); }
  }

  function copyKey(key) {
    navigator.clipboard.writeText(key).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  }

  const inp = { width: "100%", height: 38, padding: "0 12px", background: "rgba(255,255,255,0.05)", border: "1px solid var(--border-glass)", borderRadius: 8, fontFamily: "var(--font-sans)", fontSize: 13, color: "var(--color-text-primary)", outline: "none" };
  const card = (ch, extra = {}) => <div style={{ background: "rgba(255,255,255,0.04)", backdropFilter: "blur(12px)", border: "1px solid var(--border-glass)", borderRadius: 16, padding: "22px 24px", ...extra }}>{ch}</div>;

  const ENDPOINTS = [
    { method: "POST",   path: "/api/v1/tasks",       desc: "Create a task from Notion, Jira, Zapier, etc." },
    { method: "GET",    path: "/api/v1/tasks",        desc: "List tasks (supports ?status=, ?assignee=, ?priority=, ?skip=, ?limit=)" },
    { method: "GET",    path: "/api/v1/tasks/{id}",   desc: "Get a single task by ID" },
    { method: "PUT",    path: "/api/v1/tasks/{id}",   desc: "Update status, priority, assignee, or deadline" },
    { method: "DELETE", path: "/api/v1/tasks/{id}",   desc: "Cancel a task (sets status to cancelled)" },
  ];

  const METHOD_COLORS = { POST: "#3fae7d", GET: "#ff6a52", PUT: "#d99a3f", DELETE: "#ff4d5e" };

  if (loading) return <main style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}><div style={{ width: 24, height: 24, border: "2px solid rgba(255,106,82,0.2)", borderTopColor: "#ff6a52", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} /></main>;

  return (
    <main style={{ flex: 1, padding: "clamp(14px, 2.5vw, 28px) clamp(12px, 2.5vw, 28px) 40px", display: "flex", flexDirection: "column", gap: 24, maxWidth: 860 }}>
      <div className="mobile-header-pad">
        <h1 style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--color-text-primary)", marginBottom: 6 }}>Public API</h1>
        <p style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>
          Connect Notion, Jira, Trello, Zapier, or any tool to your workspace using API keys. All endpoints use <code style={{ background: "rgba(255,255,255,0.08)", padding: "1px 6px", borderRadius: 4, fontSize: 12 }}>X-API-Key</code> header authentication.
        </p>
      </div>

      {msg && <div style={{ padding: "10px 16px", borderRadius: 10, fontSize: 13, fontWeight: 500, background: msg.type === "success" ? "rgba(63,174,125,0.1)" : "rgba(255,77,94,0.1)", border: `1px solid ${msg.type === "success" ? "rgba(63,174,125,0.3)" : "rgba(255,77,94,0.3)"}`, color: msg.type === "success" ? "#3fae7d" : "#ff4d5e" }}>{msg.text}</div>}

      {/* Revealed key banner */}
      {revealedKey && (
        <div style={{ padding: "16px 20px", borderRadius: 12, background: "rgba(217,154,63,0.08)", border: "1px solid rgba(217,154,63,0.3)", display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#d99a3f" }}>⚠ Copy your API key now &mdash; it won't be shown again</div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <code style={{ flex: 1, fontFamily: "var(--font-mono)", fontSize: 12, background: "rgba(0,0,0,0.3)", padding: "8px 12px", borderRadius: 8, color: "#efe7df", wordBreak: "break-all" }}>{revealedKey.key}</code>
            <button onClick={() => copyKey(revealedKey.key)} style={{ height: 36, padding: "0 16px", borderRadius: 8, border: "none", background: copied ? "rgba(63,174,125,0.2)" : "rgba(217,154,63,0.2)", color: copied ? "#3fae7d" : "#d99a3f", fontFamily: "var(--font-sans)", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
              {copied ? "✓ Copied!" : "Copy"}
            </button>
          </div>
        </div>
      )}

      {/* Key management */}
      {card(<div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "var(--color-text-primary)" }}>API Keys</div>

        {isArchitect && (
          <div style={{ display: "flex", gap: 10 }}>
            <input value={newKeyName} onChange={e => setNewKeyName(e.target.value)} placeholder="Key name, e.g. Notion Integration" style={{ ...inp, flex: 1 }}
              onKeyDown={e => e.key === "Enter" && handleCreateKey()}
              onFocus={e => { e.target.style.borderColor = "rgba(255,106,82,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(255,106,82,0.12)"; }}
              onBlur={e => { e.target.style.borderColor = "var(--border-glass)"; e.target.style.boxShadow = "none"; }}
            />
            <button onClick={handleCreateKey} disabled={creating || !newKeyName.trim()} style={{ height: 38, padding: "0 20px", borderRadius: 8, border: "none", background: "var(--grad-primary)", color: "#f5f0eb", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: (creating || !newKeyName.trim()) ? 0.6 : 1 }}>
              {creating ? "Creating…" : "Generate Key"}
            </button>
          </div>
        )}

        {keys.length === 0 ? (
          <div style={{ padding: 28, textAlign: "center", color: "var(--color-text-tertiary)", fontSize: 13, border: "1px dashed var(--border-glass)", borderRadius: 10 }}>
            No API keys yet.{isArchitect && " Generate your first key above to start integrating."}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {keys.map(k => (
              <div key={k.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", borderRadius: 10, background: "rgba(255,255,255,0.03)", border: "1px solid var(--border-glass)" }}>
                <div style={{ width: 34, height: 34, borderRadius: 8, background: "rgba(255,106,82,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>🔑</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)" }}>{k.name}</div>
                  <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 2, fontFamily: "var(--font-mono)" }}>
                    {k.key_prefix}•••••••••••••••••••••••
                    {k.last_used_at && <span style={{ marginLeft: 10 }}>Last used: {new Date(k.last_used_at).toLocaleDateString()}</span>}
                  </div>
                </div>
                <div style={{ padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, background: "rgba(63,174,125,0.1)", border: "1px solid rgba(63,174,125,0.25)", color: "#3fae7d" }}>Active</div>
                {isArchitect && <button onClick={() => handleRevoke(k.id, k.name)} style={{ height: 28, padding: "0 12px", borderRadius: 6, border: "1px solid rgba(255,77,94,0.25)", background: "rgba(255,77,94,0.06)", color: "#ff4d5e", fontFamily: "var(--font-sans)", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Revoke</button>}
              </div>
            ))}
          </div>
        )}
      </div>)}

      {/* Endpoint reference */}
      {card(<div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "var(--color-text-primary)" }}>📖 Endpoint Reference</div>
        <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", fontFamily: "var(--font-mono)", marginBottom: 4 }}>Base URL: {BACKEND}</div>
        {ENDPOINTS.map(e => (
          <div key={e.path} style={{ display: "flex", gap: 14, alignItems: "flex-start", padding: "10px 14px", borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid var(--border-glass)", flexWrap: "wrap" }}>
            <span style={{ flex: "0 0 60px", fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700, color: METHOD_COLORS[e.method] }}>{e.method}</span>
            <code style={{ flex: "0 0 auto", fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--color-text-primary)", wordBreak: "break-all" }}>{e.path}</code>
            <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{e.desc}</span>
          </div>
        ))}
        <div style={{ marginTop: 4, padding: "14px 16px", borderRadius: 10, background: "rgba(255,106,82,0.05)", border: "1px solid rgba(255,106,82,0.12)", fontSize: 12, color: "var(--color-text-secondary)", lineHeight: 1.8 }}>
          <strong style={{ color: "#ff6a52" }}>Example request:</strong><br />
          <code style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "#efe7df", wordBreak: "break-all", overflowWrap: "anywhere" }}>
            curl -X POST {BACKEND}/api/v1/tasks \<br />
            &nbsp;&nbsp;-H "X-API-Key: sk_live_..." \<br />
            &nbsp;&nbsp;-H "Content-Type: application/json" \<br />
            &nbsp;&nbsp;-d {'\'{"title":"Fix payment bug","assignee":"ali","priority":"high"}\''}
          </code>
        </div>
      </div>)}

      {/* Full docs link */}
      <div style={{ padding: "14px 18px", borderRadius: 12, background: "rgba(255,255,255,0.03)", border: "1px solid var(--border-glass)", fontSize: 12, color: "var(--color-text-tertiary)" }}>
        📄 Full interactive API docs available at <code style={{ color: "#ff6a52", fontFamily: "var(--font-mono)" }}>{BACKEND}/docs</code> (FastAPI Swagger UI — no auth needed).
      </div>
    </main>
  );
}

// -- Feedback System (Segment 14) ---------------------------------------------

const FEEDBACK_TYPES = [
  { id: "bug",             label: "Bug Report",      emoji: "🐛", color: "#ff4d5e", desc: "Something is broken or not working right" },
  { id: "feedback",        label: "General Feedback", emoji: "💬", color: "#ff6a52", desc: "Share your thoughts or experience" },
  { id: "feature_request", label: "Feature Request",  emoji: "✨", color: "#c81f30", desc: "Suggest a new feature or improvement" },
];

const POSITIONS = ["Architect", "Navigator", "Operator", "Solo"];

function FeedbackModal({ onClose, user, token, currentPage }) {
  const [step, setStep]         = useState("type");
  const [type, setType]         = useState(null);
  const [title, setTitle]       = useState("");
  const [message, setMessage]   = useState("");
  const [userName, setUserName] = useState(user?.name || "");
  const [position, setPosition] = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");

  const selected = FEEDBACK_TYPES.find(t => t.id === type);

  const inputStyle = {
    width: "100%", padding: "10px 12px", background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, color: "#efe7df",
    fontSize: 13, outline: "none", boxSizing: "border-box",
  };

  const submit = async () => {
    if (!userName.trim())                              { setError("Please enter your name."); return; }
    if (!position)                                     { setError("Please select your position."); return; }
    if (!title.trim() || title.trim().length < 3)     { setError("Title must be at least 3 characters."); return; }
    if (!message.trim() || message.trim().length < 10) { setError("Please provide a bit more detail (10+ characters)."); return; }
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          type,
          title:        title.trim(),
          message:      message.trim(),
          page_context: currentPage || null,
          user_id:      user?.id    || null,
          user_email:   user?.email || null,
          user_name:    `${userName.trim()} (${position})`,
        }),
      });
      if (!res.ok) throw new Error("Server error");
      setStep("success");
    } catch {
      setError("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 1 }}
      style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "flex-end", justifyContent: "flex-end", padding: "0 clamp(8px,3vw,24px) 90px 0", pointerEvents: "none" }}>
      {/* Backdrop */}
      <motion.div
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)", pointerEvents: "all" }} />

      {/* Modal card */}
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 14, scale: 0.97 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        style={{
        position: "relative", pointerEvents: "all", width: "min(420px, calc(100vw - 16px))", background: "#0c0908",
        border: "1px solid rgba(255,255,255,0.1)", borderRadius: 20,
        boxShadow: "0 24px 80px rgba(0,0,0,0.7)",
        overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#efe7df", fontFamily: "var(--font-display)" }}>
              {step === "success" ? "🎉 Thanks!" : step === "form" ? `${selected?.emoji} ${selected?.label}` : "Share Feedback"}
            </div>
            <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 2 }}>
              {step === "success" ? "We've received your message" : step === "form" ? selected?.desc : "Help us improve the product"}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.06)", border: "none", borderRadius: 8, color: "var(--color-text-secondary)", cursor: "pointer", padding: "6px 10px", fontSize: 16, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: "20px 24px 24px" }}>

          {/* Step: choose type */}
          {step === "type" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {FEEDBACK_TYPES.map(t => (
                <button key={t.id} onClick={() => { setType(t.id); setStep("form"); }} style={{
                  display: "flex", alignItems: "center", gap: 14, padding: "14px 16px",
                  background: "rgba(255,255,255,0.03)", border: `1px solid rgba(255,255,255,0.07)`,
                  borderRadius: 12, cursor: "pointer", textAlign: "left", transition: "all 0.15s",
                }}
                  onMouseEnter={e => { e.currentTarget.style.background = `${t.color}18`; e.currentTarget.style.borderColor = `${t.color}50`; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.03)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"; }}
                >
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: `${t.color}22`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>{t.emoji}</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#efe7df" }}>{t.label}</div>
                    <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 2 }}>{t.desc}</div>
                  </div>
                  <div style={{ marginLeft: "auto", color: "var(--color-text-tertiary)", fontSize: 16 }}>→</div>
                </button>
              ))}
            </div>
          )}

          {/* Step: fill form */}
          {step === "form" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {/* Name + Position row */}
              <div style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, color: "var(--color-text-secondary)", display: "block", marginBottom: 6 }}>Your Name *</label>
                  <input
                    value={userName} onChange={e => setUserName(e.target.value)}
                    placeholder="e.g. John Smith"
                    style={inputStyle}
                    onFocus={e => e.target.style.borderColor = selected?.color}
                    onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, color: "var(--color-text-secondary)", display: "block", marginBottom: 6 }}>Position *</label>
                  <select
                    value={position} onChange={e => setPosition(e.target.value)}
                    style={{ ...inputStyle, appearance: "none", cursor: "pointer", color: position ? "#efe7df" : "var(--color-text-tertiary)" }}
                    onFocus={e => e.target.style.borderColor = selected?.color}
                    onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"}
                  >
                    <option value="" disabled>Select role…</option>
                    {POSITIONS.map(p => <option key={p} value={p} style={{ background: "#0c0908", color: "#efe7df" }}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, color: "var(--color-text-secondary)", display: "block", marginBottom: 6 }}>Title *</label>
                <input
                  value={title} onChange={e => setTitle(e.target.value)}
                  placeholder={type === "bug" ? "e.g. Compliance page shows blank screen" : type === "feature_request" ? "e.g. Export tasks to CSV" : "e.g. The dashboard feels very fast!"}
                  style={{
                    width: "100%", padding: "10px 12px", background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, color: "#efe7df",
                    fontSize: 13, outline: "none", boxSizing: "border-box",
                  }}
                  onFocus={e => e.target.style.borderColor = selected?.color}
                  onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: "var(--color-text-secondary)", display: "block", marginBottom: 6 }}>
                  Details * <span style={{ color: "var(--color-text-tertiary)", fontSize: 11 }}>({message.length}/5000)</span>
                </label>
                <textarea
                  value={message} onChange={e => setMessage(e.target.value)} rows={5}
                  placeholder={type === "bug" ? "Steps to reproduce, what you expected vs what happened..." : type === "feature_request" ? "Describe the feature, why it would help, any examples..." : "Tell us what you think — good or bad!"}
                  style={{
                    width: "100%", padding: "10px 12px", background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, color: "#efe7df",
                    fontSize: 13, outline: "none", resize: "vertical", boxSizing: "border-box",
                    fontFamily: "var(--font-sans)",
                  }}
                  onFocus={e => e.target.style.borderColor = selected?.color}
                  onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"}
                />
              </div>
              {currentPage && (
                <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", padding: "6px 10px", background: "rgba(255,255,255,0.03)", borderRadius: 8 }}>
                  📍 Page context will be attached: <strong style={{ color: "var(--color-text-secondary)" }}>{currentPage}</strong>
                </div>
              )}
              {error && <div style={{ fontSize: 12, color: "#ff4d5e", padding: "8px 12px", background: "rgba(255,77,94,0.1)", borderRadius: 8 }}>{error}</div>}
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => { setStep("type"); setError(""); }} style={{ flex: 1, padding: "10px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 13 }}>← Back</button>
                <button onClick={submit} disabled={loading} style={{
                  flex: 2, padding: "10px", background: selected ? `linear-gradient(135deg, ${selected.color}, ${selected.color}cc)` : "var(--grad-primary)",
                  border: "none", borderRadius: 10, color: "#f5f0eb", fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", fontSize: 13, opacity: loading ? 0.7 : 1,
                }}>
                  {loading ? "Sending…" : `Submit ${selected?.label}`}
                </button>
              </div>
            </div>
          )}

          {/* Step: success */}
          {step === "success" && (
            <div style={{ textAlign: "center", padding: "10px 0 4px" }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>🎉</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#efe7df", marginBottom: 8 }}>Feedback received!</div>
              <div style={{ fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.6, marginBottom: 24 }}>
                We read every submission and use it to make the product better. Thank you for taking the time!
              </div>
              <button onClick={onClose} style={{
                padding: "10px 24px", background: "var(--grad-primary)", border: "none", borderRadius: 10,
                color: "#f5f0eb", fontWeight: 600, cursor: "pointer", fontSize: 13,
              }}>Close</button>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

function FeedbackButton({ user, token, currentPage }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(true)}
        title="Share feedback"
        style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 1000,
          display: "flex", alignItems: "center", gap: 8,
          padding: "11px 18px 11px 14px",
          background: "linear-gradient(135deg, #ff6a52, #c81f30)",
          border: "none", borderRadius: 50, cursor: "pointer",
          boxShadow: "0 4px 20px rgba(255,106,82,0.45)",
          color: "#f5f0eb", fontWeight: 600, fontSize: 13,
          fontFamily: "var(--font-sans)",
          transition: "transform 0.15s, box-shadow 0.15s",
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.05)"; e.currentTarget.style.boxShadow = "0 6px 28px rgba(255,106,82,0.6)"; }}
        onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(255,106,82,0.45)"; }}
      >
        <span style={{ fontSize: 16 }}>💬</span>
        Feedback
      </button>

      <AnimatePresence>
        {open && <FeedbackModal onClose={() => setOpen(false)} user={user} token={token} currentPage={currentPage} />}
      </AnimatePresence>
    </>
  );
}

// -- Billing Wall (Segment 15) -------------------------------------------------

const ADMIN_EMAILS = (process.env.REACT_APP_ADMIN_EMAILS || "wahaj@acedengroup.com").split(",").map(e => e.trim().toLowerCase()).filter(Boolean);

function formatCardNumber(v) {
  return v.replace(/\D/g, "").slice(0, 16).replace(/(.{4})/g, "$1 ").trim();
}
function formatExpiry(v) {
  const d = v.replace(/\D/g, "").slice(0, 4);
  return d.length > 2 ? d.slice(0,2) + "/" + d.slice(2) : d;
}
function cardBrand(n) {
  const d = n.replace(/\s/g, "");
  if (/^4/.test(d)) return "💳 Visa";
  if (/^5[1-5]/.test(d)) return "💳 Mastercard";
  if (/^3[47]/.test(d)) return "💳 Amex";
  return "💳";
}

function BillingWall({ user, token, status, isNewUser, daysLeft, trialEndsAt, onSuccess, inlineMode = false }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  const isPastDue   = status === "past_due";
  const isCancelled = status === "cancelled";

  // Redirect user to Polar hosted checkout page.
  // Card details are entered securely on LS — never on our side.
  const goToCheckout = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/billing/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not create checkout. Please try again.");

      if (data.test_mode) {
        // Dev/test: backend already updated DB, verify and proceed
        const statusRes = await fetch(`${BASE_URL}/auth/billing-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const statusData = await statusRes.json();
        if (!statusData.show_wall) {
          onSuccess();
        } else {
          throw new Error("Test mode: billing status not updated. Check backend logs.");
        }
      } else {
        // Live mode: redirect to Polar hosted checkout
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  // Inline mode — just render the checkout button for use inside popup
  if (inlineMode) {
    return (
      <div>
        {error && (
          <div style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(255,77,94,0.1)", border: "1px solid rgba(255,77,94,0.25)", color: "#ff4d5e", fontSize: 12, marginBottom: 12 }}>
            ⚠ {error}
          </div>
        )}
        <button
          onClick={goToCheckout}
          disabled={loading}
          style={{
            width: "100%", height: 52, borderRadius: 14, border: "none",
            background: loading ? "rgba(255,106,82,0.5)" : "linear-gradient(135deg, #ff6a52 0%, #c81f30 100%)",
            color: "#f5f0eb", fontFamily: "var(--font-sans)", fontSize: 15, fontWeight: 700,
            cursor: loading ? "not-allowed" : "pointer", letterSpacing: "-0.01em",
            boxShadow: "0 0 28px rgba(255,106,82,0.4), 0 8px 20px rgba(0,0,0,0.3)",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}
        >
          {loading
            ? <><span style={{ display: "inline-block", width: 14, height: 14, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#f5f0eb", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} /> Redirecting to checkout…</>
            : "💳 Activate Free Trial →"
          }
        </button>
        <div style={{ marginTop: 10, fontSize: 11, color: "var(--color-text-tertiary)" }}>🔒 Secured by Polar · No charge for 7 days</div>
      </div>
    );
  }

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9998,
      background: "rgba(8,10,20,0.97)", backdropFilter: "blur(20px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "var(--font-sans)",
    }}>
      <div style={{ position: "absolute", top: "20%", left: "50%", transform: "translateX(-50%)", width: 600, height: 400, background: "radial-gradient(ellipse, rgba(255,106,82,0.12) 0%, transparent 70%)", pointerEvents: "none" }} />

      <div style={{
        position: "relative", width: "100%", maxWidth: 480, margin: "0 24px",
        background: "linear-gradient(160deg, rgba(20,22,46,0.98) 0%, rgba(14,17,36,0.99) 100%)",
        border: "1px solid rgba(255,255,255,0.1)", borderRadius: 24,
        boxShadow: "0 32px 100px rgba(0,0,0,0.8)", overflow: "hidden",
        animation: "slideUpIn 0.4s cubic-bezier(0.34,1.56,0.64,1)",
      }}>
        <div style={{ height: 3, background: "linear-gradient(90deg, #ff6a52, #c81f30, #ff4d6a)" }} />

        <div style={{ padding: "32px 36px 36px" }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: isNewUser ? "rgba(63,174,125,0.15)" : isPastDue ? "rgba(217,154,63,0.15)" : "rgba(255,106,82,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 20 }}>
            {isNewUser ? "🎉" : isPastDue ? "⚠️" : isCancelled ? "🔒" : "🚀"}
          </div>
          <div style={{ fontSize: 21, fontWeight: 700, color: "#efe7df", marginBottom: 8, fontFamily: "var(--font-display)" }}>
            {isNewUser ? "Start Your 7-Day Free Trial" : isPastDue ? "Payment Failed" : isCancelled ? "Subscription Ended" : "Your Free Trial Has Ended"}
          </div>
          <div style={{ fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.7, marginBottom: isCancelled ? 14 : 20 }}>
            {isNewUser
              ? "You'll be taken to our secure checkout to enter your card details. You won't be charged until the 7 days end — cancel anytime."
              : isPastDue
              ? "We couldn't process your last payment. Update your card to continue."
              : isCancelled
              ? "Your subscription has ended and your account is currently locked."
              : "Your trial has ended. Subscribe to keep access to all your tasks and workflows."}
          </div>
          {isCancelled && (
            <div style={{ padding: "14px 16px", borderRadius: 12, background: "rgba(255,77,94,0.08)", border: "1px solid rgba(255,77,94,0.25)", marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#ff4d5e", marginBottom: 5, display: "flex", alignItems: "center", gap: 6 }}>
                <span>🔒</span> Account Access Restricted
              </div>
              <div style={{ fontSize: 12, color: "rgba(255,77,94,0.85)", lineHeight: 1.7 }}>
                Following your cancellation, <strong style={{ color: "#ff4d5e" }}>you cannot create a new account</strong> on this platform. To regain access, either resubscribe below or contact your workspace administrator to request management approval for reinstatement.
              </div>
            </div>
          )}
          {isNewUser && daysLeft !== undefined && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20, padding: "10px 14px", borderRadius: 10, background: "rgba(63,174,125,0.08)", border: "1px solid rgba(63,174,125,0.2)", fontSize: 13, color: "#3fae7d", fontWeight: 600 }}>
              <span>⏱</span>
              <span>Free trial: <strong>{daysLeft} day{daysLeft === 1 ? "" : "s"} remaining</strong> &mdash; no charge until it ends</span>
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
            {["Unlimited tasks & team members", "AI-powered task creation from Slack", "Compliance & ownership reports", "Cancel anytime, no commitment"].map(f => (
              <div key={f} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "var(--color-text-secondary)" }}>
                <div style={{ width: 18, height: 18, borderRadius: 5, background: "rgba(255,106,82,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, flexShrink: 0 }}>✓</div>
                {f}
              </div>
            ))}
          </div>
          <div style={{ padding: "14px 18px", background: isNewUser ? "rgba(63,174,125,0.08)" : "rgba(255,106,82,0.08)", border: isNewUser ? "1px solid rgba(63,174,125,0.2)" : "1px solid rgba(255,106,82,0.2)", borderRadius: 12, marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{isNewUser ? "Free for 7 days, then" : "Monthly subscription"}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#efe7df", marginTop: 2 }}>$19.99 <span style={{ fontSize: 13, fontWeight: 400, color: "var(--color-text-secondary)" }}>/ user / month</span></div>
            </div>
            <div style={{ fontSize: 11, color: isNewUser ? "#3fae7d" : "#d99a3f", background: isNewUser ? "rgba(63,174,125,0.1)" : "rgba(34,211,238,0.1)", padding: "4px 10px", borderRadius: 20, fontWeight: 600 }}>{isNewUser ? "7 days FREE" : "Cancel anytime"}</div>
          </div>

          {error && <div style={{ fontSize: 12, color: "#ff4d5e", padding: "8px 12px", background: "rgba(255,77,94,0.1)", borderRadius: 8, marginBottom: 12 }}>{error}</div>}

          <button
            onClick={goToCheckout}
            disabled={loading}
            style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", background: loading ? "rgba(255,106,82,0.5)" : "linear-gradient(135deg, #ff6a52, #c81f30)", color: "#f5f0eb", fontWeight: 700, fontSize: 14, cursor: loading ? "not-allowed" : "pointer", boxShadow: loading ? "none" : "0 4px 20px rgba(255,106,82,0.4)", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
          >
            {loading
              ? <><span style={{ display: "inline-block", width: 14, height: 14, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#f5f0eb", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} /> Redirecting to checkout…</>
              : isNewUser ? "Continue to Secure Checkout →" : isPastDue ? "Update Payment Method →" : "Subscribe Now →"
            }
          </button>
          <div style={{ textAlign: "center", marginTop: 12, fontSize: 11, color: "var(--color-text-tertiary)" }}>
            {isNewUser ? "🔒 Secured by Polar · No charge for 7 days" : "🔒 Secured by Polar · Cancel anytime"}
          </div>
        </div>
      </div>
    </div>
  );
}

// -- Authenticated shell — wraps Dashboard with Sidebar ------------------------
function AuthenticatedApp() {
  const { user, isOnboarded, token } = useAuth();
  const [activeNav, setActiveNav]     = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const winWidth = useWindowWidth();
  const isPhone = winWidth < 640;
  const [showOnboarding, setShowOnboarding] = useState(false);
  // Local override: set true once user finishes onboarding so we don't loop
  // back even if AuthContext hasn't re-fetched isOnboarded from the server yet.
  const [onboardingDone, setOnboardingDone] = useState(false);
  const [showTour, setShowTour]       = useState(() => !localStorage.getItem("aw_tour_done"));
  const [billing, setBilling]         = useState(null);   // null=loading, object=loaded
  const [billingChecked, setBillingChecked] = useState(false);
  const [showBillingPopup, setShowBillingPopup] = useState(false);
  const [billingNonce, setBillingNonce] = useState(0);   // bump to force a billing re-check

  // The server answers 402 once the trial ends (even mid-session): re-check billing so the wall shows up.
  useEffect(() => {
    const onBillingRequired = () => setBillingNonce(n => n + 1);
    window.addEventListener("billing-required", onBillingRequired);
    return () => window.removeEventListener("billing-required", onBillingRequired);
  }, []);

  // -- Check billing status on mount ------------------------------------------
  useEffect(() => {
    if (!token || !user) return;
    // Exempt: your admin email(s) never see billing wall
    const adminEmails = (process.env.REACT_APP_ADMIN_EMAILS || "").split(",").map(e => e.trim().toLowerCase()).filter(Boolean);
    if (adminEmails.includes(user.email?.toLowerCase())) {
      setBilling({ status: "exempt", show_wall: false });
      setBillingChecked(true);
      return;
    }
    // On return from Polar checkout, clean the URL but STILL re-fetch
    // from the backend — the webhook may not have fired yet, so we poll with backoff
    const params = new URLSearchParams(window.location.search);
    const fromCheckout = params.get("billing") === "success" || params.get("billing") === "test_success";
    if (fromCheckout) {
      window.history.replaceState({}, "", window.location.pathname);
    }

    const checkBilling = (retriesLeft = 0) => {
      fetch(`${BASE_URL}/auth/billing-status`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.json())
        .then(d => {
          // If returning from checkout but webhook hasn't updated the DB yet, retry up to 4×
          if (fromCheckout && d.show_wall && retriesLeft < 4) {
            setTimeout(() => checkBilling(retriesLeft + 1), 1500 * (retriesLeft + 1));
            return;
          }
          setBilling(d);
          setBillingChecked(true);
        })
        .catch(() => {
          setBilling({ status: "trialing", show_wall: false });
          setBillingChecked(true);
        });
    };

    checkBilling();
  }, [token, user, billingNonce]);

  // Show billing popup for new trial users after 3 seconds on dashboard
  useEffect(() => {
    if (billing?.show_wall && !billing?.card_on_file && billing?.status === "trialing") {
      const timer = setTimeout(() => setShowBillingPopup(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [billing]);

  // Trial reminder: a small side card, shown at most once per day (dismissible)
  const [showTrialNote, setShowTrialNote] = useState(false);
  useEffect(() => {
    if (billing?.status !== "trialing" || billing?.days_left === undefined) return;
    const today = new Date().toDateString();
    let last = null;
    try { last = localStorage.getItem("aw_trial_note_day"); } catch (_) {}
    if (last !== today) setShowTrialNote(true);
  }, [billing]);
  const dismissTrialNote = () => {
    setShowTrialNote(false);
    try { localStorage.setItem("aw_trial_note_day", new Date().toDateString()); } catch (_) {}
  };

  // ✅ Role-based task filters passed to useTasks
  const taskFilters = useMemo(() => {
    if (!user) return {};
    if (user.role === "architect") return { workspace_id: user.workspace?.id };
    if (user.role === "navigator") return { workspace_id: user.workspace?.id, team_name: user.team_name };
    if (user.role === "operator") return { owner_id: user.id };
    if (user.role === "solo")     return { owner_id: user.id };
    return {};
  }, [user]);

  const { tasks, total, loading, error, submitting, moveTask, removeTask, addTask, reload, clearError } = useTasks(taskFilters);

  // -- Live nav badges derived from tasks --------------------------------------
  const navBadges = useMemo(() => {
    if (!tasks?.length) return {};
    const totalCount      = tasks.length;
    const inProgress      = tasks.filter(t => t.status === "in_progress").length;
    const completed       = tasks.filter(t => t.status === "completed").length;
    const now             = Date.now();
    const overdue         = tasks.filter(t => t.deadline && parseDeadline(t.deadline) < now && t.status !== "completed" && t.status !== "cancelled").length;
    const unassigned      = tasks.filter(t => !t.assignee).length;
    const stale           = tasks.filter(t => {
      const d = t.updated_at || t.created_at;
      return d && (now - new Date(d)) > 7 * 86400000 && t.status !== "completed" && t.status !== "cancelled";
    }).length;
    const highNotStarted  = tasks.filter(t => (t.priority === "high" || t.priority === "critical") && t.status === "to_do").length;
    const complianceIssues = overdue + unassigned + stale + highNotStarted;
    const owners          = new Set(tasks.map(t => t.assignee).filter(Boolean)).size;

    return {
      0:  inProgress   || null,   // Dashboard  — active tasks
      1:  totalCount   || null,   // Tasks      — total count
      2:  complianceIssues || null, // Compliance — issues
      4:  completed    || null,   // Reports    — completed
      5:  owners       || null,   // Ownership  — owners
    };
  }, [tasks]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") document.activeElement?.blur();
  }, []);

  // Check if Slack OAuth returned a token in the URL (after Slack login)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("token");
    if (urlToken) {
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // Step 1 — Billing: check subscription/trial before anything else
  // Show spinner while billing status is being fetched
  if (!billingChecked) {
    return (
      <div style={{ minHeight: "100vh", background: "var(--bg-page)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: 36, height: 36, borderRadius: "50%", border: "3px solid rgba(255,106,82,0.2)", borderTopColor: "#ff6a52", animation: "spin 0.8s linear infinite" }} />
      </div>
    );
  }

  // Show billing wall for: expired/cancelled/past_due — still full screen
  // For new trialing users — show dashboard first then popup
  const isNewTrialUser = billing?.show_wall && !billing?.card_on_file && billing?.status === "trialing" && !billing?.trial_expired;
  const isHardWall = billing?.show_wall && !isNewTrialUser;

  if (isHardWall) {
    return (
      <BillingWall
        user={user}
        token={token}
        status={billing.status}
        isNewUser={false}
        daysLeft={billing.days_left}
        trialEndsAt={billing.trial_ends_at}
        onSuccess={() => setBilling({ status: "active", show_wall: false })}
      />
    );
  }

  // Step 2 — Onboarding: after billing is sorted, new users complete role selection
  // onboardingDone is a local override so we don't loop back if AuthContext
  // hasn't re-fetched is_onboarded from the server yet after completion.
  if ((!isOnboarded && !onboardingDone) || showOnboarding) {
    return (
      <OnboardingPage
        onComplete={() => {
          setOnboardingDone(true);
          setShowOnboarding(false);
          // Always force the feature tour after role selection — clear any old skip flag
          localStorage.removeItem("aw_tour_done");
          setShowTour(true);
        }}
      />
    );
  }

  const currentNavLabel = NAV_ITEMS[activeNav]?.label;

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-page)", fontFamily: "var(--font-sans)", position: "relative" }} onKeyDown={handleKeyDown}>
      {showTour && <TourOverlay onComplete={() => setShowTour(false)} onSkip={() => { setShowTour(false); setActiveNav(3); }} />}

      {/* ── New Trial Billing Popup ── */}
      {showBillingPopup && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 10000,
          background: "rgba(4,5,12,0.85)", backdropFilter: "blur(12px)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: "20px",
          animation: "fadeUp 0.35s cubic-bezier(0.16,1,0.3,1) both",
        }}>
          <div style={{
            width: "100%", maxWidth: 480,
            background: "linear-gradient(160deg,#140806 0%,#120705 60%,#0a0403 100%)",
            border: "1px solid rgba(255,106,82,0.35)", borderRadius: 24,
            padding: "36px 32px",
            boxShadow: "0 40px 100px rgba(0,0,0,0.8), 0 0 60px rgba(255,106,82,0.08)",
            textAlign: "center",
          }}>
            {/* Icon */}
            <div style={{
              width: 70, height: 70, borderRadius: 20, margin: "0 auto 20px",
              background: "rgba(255,106,82,0.12)", border: "1.5px solid rgba(255,106,82,0.35)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 30, boxShadow: "0 0 32px rgba(255,106,82,0.2)",
            }}>🚀</div>

            {/* Heading */}
            <div style={{
              fontSize: 22, fontWeight: 800, letterSpacing: "-0.03em", marginBottom: 10,
              fontFamily: "var(--font-display)",
              background: "linear-gradient(135deg, #efe7df 0%, #ffb199 100%)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
            }}>You are in! Activate your free trial</div>

            {/* Body */}
            <div style={{ fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.75, marginBottom: 20 }}>
              You have full access to explore the dashboard. To keep using AI Workflow Coordinator after 7 days, add your card details below. <strong style={{ color: "#ffb199" }}>You will not be charged for 7 days.</strong>
            </div>

            {/* Feature bullets */}
            <div style={{
              background: "rgba(255,106,82,0.06)", border: "1px solid rgba(255,106,82,0.18)",
              borderRadius: 14, padding: "14px 18px", marginBottom: 24, textAlign: "left",
            }}>
              {[
                ["✅", "Full access for 7 days — completely free"],
                ["💳", "Card saved securely — cancel anytime"],
                ["💰", "Just $19.99/month after trial ends"],
              ].map(([icon, text]) => (
                <div key={text} style={{
                  display: "flex", alignItems: "center", gap: 10,
                  fontSize: 13, color: "var(--color-text-secondary)", padding: "5px 0",
                }}>
                  <span style={{ fontSize: 15, flexShrink: 0 }}>{icon}</span>
                  <span>{text}</span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <BillingWall
              user={user}
              token={token}
              status={billing?.status}
              isNewUser={true}
              daysLeft={billing?.days_left}
              trialEndsAt={billing?.trial_ends_at}
              onSuccess={() => {
                setBilling({ status: "active", show_wall: false });
                setShowBillingPopup(false);
              }}
              inlineMode={true}
            />

            {/* Use the app free for 7 days — no card needed */}
            <button
              onClick={() => setShowBillingPopup(false)}
              style={{
                marginTop: 12, width: "100%", height: 46, borderRadius: 14,
                background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.18)",
                color: "#efe7df", fontSize: 14, fontWeight: 600,
                cursor: "pointer", fontFamily: "var(--font-sans)",
              }}
            >
              Continue without a card — {billing?.days_left !== undefined ? `${billing.days_left + 1} day${billing.days_left + 1 === 1 ? "" : "s"}` : "7 days"} free
            </button>
            <div style={{ marginTop: 8, fontSize: 11, color: "var(--color-text-tertiary)" }}>
              No card needed. Access stops after your 7-day trial unless you subscribe.
            </div>
          </div>
        </div>
      )}

      {/* Trial reminder — small side card, once a day */}
      {showTrialNote && billing?.status === "trialing" && billing?.days_left !== undefined && (
        <div style={{
          position: "fixed", right: 20, bottom: 84, zIndex: 9000, width: 270,
          padding: "14px 16px", borderRadius: 14,
          background: "linear-gradient(160deg,#2a1a0c 0%,#1a1008 100%)",
          border: "1px solid rgba(217,154,63,0.5)",
          boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
          fontFamily: "var(--font-sans)", color: "#f5f0eb",
        }}>
          <button onClick={dismissTrialNote} aria-label="Dismiss" style={{
            position: "absolute", top: 6, right: 8, background: "none", border: "none",
            color: "#d9cfc6", fontSize: 16, cursor: "pointer", lineHeight: 1,
          }}>×</button>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, paddingRight: 14 }}>
            ⏰ {billing.days_left === 0 ? "Your free trial ends today" : `Free trial ends in ${billing.days_left} day${billing.days_left === 1 ? "" : "s"}`}
          </div>
          <button onClick={async () => {
            try {
              const res = await fetch(`${BASE_URL}/billing/checkout`, { method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } });
              const d = await res.json();
              if (d.checkout_url) window.location.href = d.checkout_url;
            } catch (_) {}
          }} style={{ width: "100%", padding: "8px 14px", background: "linear-gradient(135deg,#ff6a52,#c81f30)", border: "none", borderRadius: 9, color: "#f5f0eb", cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
            Pay Now →
          </button>
        </div>
      )}

      <FeedbackButton user={user} token={token} currentPage={NAV_ITEMS[activeNav]?.label} />
      {/* Subtle dot grid background */}
      <div style={{ position: "fixed", inset: 0, backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.025) 1px, transparent 1px)", backgroundSize: "32px 32px", pointerEvents: "none", zIndex: 0 }} />
      {/* Ambient top glow */}
      <div style={{ position: "fixed", top: -200, left: "30%", width: 600, height: 400, background: "radial-gradient(ellipse, rgba(255,106,82,0.06) 0%, transparent 70%)", pointerEvents: "none", zIndex: 0 }} />

      {/* Mobile nav backdrop — tapping it closes the drawer */}
      {isPhone && mobileNavOpen && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={() => setMobileNavOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 54, backdropFilter: "blur(2px)" }}
        />
      )}

      {/* Hamburger toggle — only rendered on phone widths; the sidebar
          itself becomes a full off-canvas drawer instead of a permanent
          icon rail there, so this is how it's reached. */}
      {isPhone && (
        <button
          onClick={() => setMobileNavOpen(o => !o)}
          aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
          style={{
            position: "fixed", top: 10, left: 10, zIndex: 60,
            width: 38, height: 38, borderRadius: 10,
            background: "rgba(20,15,14,0.85)", border: "1px solid rgba(255,255,255,0.12)",
            backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#f5f0eb", cursor: "pointer", fontSize: 16,
          }}
        >
          {mobileNavOpen ? "✕" : "☰"}
        </button>
      )}

      <Sidebar
        activeNav={activeNav}
        onNavChange={setActiveNav}
        navBadges={navBadges}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(c => !c)}
        mobileOpen={mobileNavOpen}
        onCloseMobile={() => setMobileNavOpen(false)}
      />
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
        style={{ paddingLeft: isPhone ? 0 : (winWidth < 900 ? 64 : (sidebarCollapsed ? 64 : 228)), display: "flex", flexDirection: "column", minHeight: "100vh", position: "relative", zIndex: 1, transition: "padding-left 0.25s cubic-bezier(0.4,0,0.2,1)", minWidth: 0, overflowX: "auto" }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={activeNav}
            initial={pageTransition.initial}
            animate={pageTransition.animate}
            exit={pageTransition.exit}
            style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}
          >
            {activeNav === 0 ? (
              <Dashboard
                tasks={tasks} total={total} loading={loading} error={error}
                submitting={submitting} moveTask={moveTask} removeTask={removeTask}
                addTask={addTask} reload={reload} clearError={clearError}
              />
            ) : activeNav === 1 ? (
              <TasksPage />
            ) : activeNav === 2 ? (
              <CompliancePage />
            ) : activeNav === 3 ? (
              <KnowledgePage />
            ) : activeNav === 4 ? (
              <ReportsPage />
            ) : activeNav === 5 ? (
              <OwnershipGraph />
            ) : activeNav === 6 ? (
              <IntegrationsPage />
            ) : activeNav === 7 ? (
              <LocalePage />
            ) : activeNav === 8 ? (
              <TeamsPage />
            ) : activeNav === 9 ? (
              <ApiPage />
            ) : activeNav === 10 ? (
              <SettingsPage />
            ) : (
              <PlaceholderPage label={currentNavLabel} />
            )}
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

// -- Router — decides which page to show --------------------------------------

// -- Freelancer Dashboard -------------------------------------------------------
function FreelancerDashboard() {
  const { user, token, logout } = useAuth();
  const API = BASE_URL;
  const hdrs = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [copied, setCopied]     = useState(false);

  useEffect(() => {
    fetch(`${API}/referral/my-stats`, { headers: hdrs })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(d => setStats(d))
      .catch(() => setError("Could not load your stats. Please refresh."))
      .finally(() => setLoading(false));
  }, []);  // eslint-disable-line

  const copyLink = () => {
    if (!stats?.referral_link) return;
    navigator.clipboard.writeText(stats.referral_link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const CARDS = stats ? [
    { label: "Total Referred",  value: stats.total,     color: "#ff6a52", icon: "👥", bg: "rgba(255,106,82,0.1)",  border: "rgba(255,106,82,0.25)" },
    { label: "On Trial",        value: stats.trialing,  color: "#d99a3f", icon: "⏱",  bg: "rgba(217,154,63,0.1)",  border: "rgba(217,154,63,0.25)" },
    { label: "Paid",            value: stats.paid,      color: "#3fae7d", icon: "💰", bg: "rgba(63,174,125,0.1)",  border: "rgba(63,174,125,0.25)" },
    { label: "Cancelled",       value: stats.cancelled, color: "#ff4d5e", icon: "✕",  bg: "rgba(255,77,94,0.1)", border: "rgba(255,77,94,0.25)" },
  ] : [];

  const convRate = stats && stats.total > 0
    ? Math.round((stats.paid / stats.total) * 100)
    : 0;

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-page)", fontFamily: "var(--font-sans)", position: "relative" }}>
      {/* Ambient background */}
      <div style={{ position: "fixed", inset: 0, backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.025) 1px, transparent 1px)", backgroundSize: "32px 32px", pointerEvents: "none", zIndex: 0 }} />
      <div style={{ position: "fixed", top: -200, left: "30%", width: 600, height: 400, background: "radial-gradient(ellipse, rgba(255,106,82,0.06) 0%, transparent 70%)", pointerEvents: "none", zIndex: 0 }} />

      <div style={{ position: "relative", zIndex: 1, maxWidth: 860, margin: "0 auto", padding: "clamp(20px,4vw,48px) clamp(16px,3vw,32px)" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 36 }}>
          <div>
            <div style={{ fontSize: 24, fontWeight: 800, color: "var(--color-text-primary)", fontFamily: "var(--font-display)", letterSpacing: "-0.03em" }}>
              👋 Hi, {user?.name?.split(" ")[0] || "there"}
            </div>
            <div style={{ fontSize: 13, color: "var(--color-text-tertiary)", marginTop: 4 }}>
              Your referral dashboard — share your link and track every signup
            </div>
          </div>
          <button onClick={logout} style={{ height: 36, padding: "0 16px", borderRadius: 8, border: "1px solid rgba(255,77,94,0.3)", background: "transparent", color: "#ff4d5e", fontFamily: "var(--font-sans)", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            Sign Out
          </button>
        </div>

        {/* Error */}
        {error && (
          <div style={{ padding: "12px 16px", borderRadius: 10, background: "rgba(255,77,94,0.1)", border: "1px solid rgba(255,77,94,0.25)", color: "#ff4d5e", fontSize: 13, marginBottom: 24 }}>
            ⚠ {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, color: "var(--color-text-tertiary)", fontSize: 13, padding: "40px 0" }}>
            <span style={{ width: 18, height: 18, border: "2px solid rgba(255,255,255,0.1)", borderTopColor: "#ff6a52", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
            Loading your stats…
          </div>
        )}

        {!loading && stats && (
          <>
            {/* Referral link card */}
            <div style={{ background: "linear-gradient(135deg, rgba(255,106,82,0.12), rgba(200,31,48,0.1))", border: "1px solid rgba(255,106,82,0.25)", borderRadius: 18, padding: "24px 28px", marginBottom: 28 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#ff6a52", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 10 }}>
                🔗 Your Unique Referral Link
              </div>
              <div style={{ fontSize: 13, color: "var(--color-text-tertiary)", marginBottom: 14, lineHeight: 1.6 }}>
                Share this link. Every user who signs up through it is automatically counted under your name.
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 200, padding: "10px 14px", background: "rgba(0,0,0,0.25)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, fontFamily: "monospace", fontSize: 13, color: "#ffb199", letterSpacing: "0.02em", wordBreak: "break-all" }}>
                  {stats.referral_link}
                </div>
                <button onClick={copyLink}
                  style={{ height: 42, padding: "0 20px", borderRadius: 10, border: "none", background: copied ? "rgba(63,174,125,0.2)" : "linear-gradient(135deg,#ff6a52,#c81f30)", color: copied ? "#3fae7d" : "#f5f0eb", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", transition: "all 0.2s", border: copied ? "1px solid rgba(63,174,125,0.4)" : "none" }}>
                  {copied ? "✓ Copied!" : "Copy Link"}
                </button>
              </div>
              <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>Your code:</span>
                <span style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 700, color: "#ffb199", background: "rgba(99,102,241,0.15)", padding: "2px 10px", borderRadius: 6 }}>
                  {stats.referral_code}
                </span>
              </div>
            </div>

            {/* Stats cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14, marginBottom: 28 }}>
              {CARDS.map(card => (
                <div key={card.label} style={{ background: card.bg, border: `1px solid ${card.border}`, borderRadius: 14, padding: "20px 20px 18px" }}>
                  <div style={{ fontSize: 22, marginBottom: 8 }}>{card.icon}</div>
                  <div style={{ fontSize: 30, fontWeight: 800, color: card.color, fontFamily: "var(--font-display)", letterSpacing: "-0.03em", lineHeight: 1 }}>
                    {card.value}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginTop: 6, fontWeight: 500 }}>
                    {card.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Conversion rate bar */}
            <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid var(--border-glass)", borderRadius: 14, padding: "20px 24px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--color-text-primary)", fontFamily: "var(--font-display)" }}>
                  Trial → Paid Conversion
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: convRate >= 50 ? "#3fae7d" : convRate >= 25 ? "#d99a3f" : "#ff4d5e", fontFamily: "var(--font-display)" }}>
                  {convRate}%
                </div>
              </div>
              <div style={{ height: 8, background: "rgba(255,255,255,0.06)", borderRadius: 999, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${convRate}%`, borderRadius: 999, background: convRate >= 50 ? "linear-gradient(90deg,#3fae7d,#3fae7d)" : convRate >= 25 ? "linear-gradient(90deg,#d99a3f,#e5283a)" : "#ff4d5e", transition: "width 0.8s ease" }} />
              </div>
              <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 10 }}>
                {stats.paid} paid out of {stats.total} total referrals
                {stats.total === 0 && " — share your link to get started!"}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// -- Invite landing page — /invite/:slug --------------------------------------
function InvitePage() {
  const slug = window.location.pathname.replace(/^\/invite\/?/, "").trim();
  const API  = BASE_URL;

  const [status, setStatus] = React.useState("loading"); // loading | found | notfound
  const [name,   setName]   = React.useState("");

  React.useEffect(() => {
    if (!slug) { setStatus("notfound"); return; }
    fetch(`${API}/referral/resolve-slug/${encodeURIComponent(slug)}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => {
        if (d.valid) {
          setName(d.name);
          setStatus("found");
          // Redirect to register with the referral code after a short delay
          setTimeout(() => {
            window.location.href = `/?ref=${encodeURIComponent(d.referral_code)}`;
          }, 1800);
        } else {
          setStatus("notfound");
        }
      })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  const containerStyle = {
    minHeight: "100vh", display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center",
    background: "linear-gradient(135deg, #0a0302 0%, #160705 100%)",
    fontFamily: "'Share Tech Mono', monospace",
  };

  if (status === "loading") return (
    <div style={containerStyle}>
      <div style={{ fontSize: 13, color: "rgba(255,138,76,0.5)", letterSpacing: "0.2em" }}>RESOLVING INVITE…</div>
    </div>
  );

  if (status === "notfound") return (
    <div style={containerStyle}>
      <div style={{ fontSize: 22, color: "#ff4d5e", marginBottom: 12 }}>✕</div>
      <div style={{ fontSize: 13, color: "rgba(255,45,85,0.8)", letterSpacing: "0.15em" }}>INVITE LINK NOT FOUND</div>
      <a href="/" style={{ marginTop: 20, fontSize: 11, color: "rgba(255,138,76,0.5)", textDecoration: "none", letterSpacing: "0.1em" }}>← GO HOME</a>
    </div>
  );

  return (
    <div style={containerStyle}>
      <div style={{ textAlign: "center", padding: 40, border: "1px solid rgba(255,138,76,0.2)", borderRadius: 8, background: "rgba(255,138,76,0.03)", maxWidth: 360 }}>
        <div style={{ fontSize: 28, marginBottom: 10 }}>◈</div>
        <div style={{ fontSize: 11, color: "rgba(255,138,76,0.4)", letterSpacing: "0.2em", marginBottom: 16 }}>YOU WERE INVITED BY</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: "#ff8a4c", letterSpacing: "0.05em", marginBottom: 20, fontFamily: "'Orbitron', monospace" }}>{name}</div>
        <div style={{ fontSize: 11, color: "rgba(255,138,76,0.5)", letterSpacing: "0.12em" }}>Redirecting to registration…</div>
        <div style={{ marginTop: 16, height: 2, background: "rgba(255,138,76,0.1)", borderRadius: 1, overflow: "hidden" }}>
          <div style={{ height: "100%", background: "#ff8a4c", animation: "invite-progress 1.8s linear forwards" }} />
        </div>
        <style>{`@keyframes invite-progress { from { width: 0% } to { width: 100% } }`}</style>
        <a href={`/?ref=${slug}`} style={{ display: "block", marginTop: 14, fontSize: 10, color: "rgba(255,138,76,0.3)", textDecoration: "none" }}>Click here if not redirected</a>
      </div>
    </div>
  );
}

function AppRouter() {
  const { isAuthenticated, loading, user } = useAuth();

  // Public legal routes — no auth required
  if (window.location.pathname === "/terms")   return <TermsPage />;
  if (window.location.pathname === "/privacy") return <PrivacyPage />;
  if (window.location.pathname === "/refund")  return <RefundPage />;

  // Password reset — public route, works whether or not the user is logged in
  if (window.location.pathname === "/reset-password") return <ResetPasswordPage />;

  // Handle /invite/:slug before auth check — public route
  if (window.location.pathname.startsWith("/invite/")) {
    return <InvitePage />;
  }

  if (loading) return <AppLoader />;
  if (!isAuthenticated) return <AuthPage />;

  // Super-admin → show admin dashboard, bypass all other pages
  if (user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase())) {
    return <AdminDashboard />;
  }

  // Freelancer → show their personal referral dashboard
  if (user?.subscription_status === "freelancer") {
    return <FreelancerDashboard />;
  }

  return <AuthenticatedApp />;
}

// -- Root App — wraps everything in AuthProvider -------------------------------
export default function App() {
  return (
    <>
      <style>{GLOBAL_STYLES}</style>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </>
  );
}
